import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, Navigate, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Gem, Home, Target, Gift, Dices, Users, UserCheck, Ticket, Banknote, FileText, Film, Shield } from 'lucide-react';

const addPulsateStyles = () => {
  if (typeof document === 'undefined') return;
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    body { font-family: 'Roboto', sans-serif; }
    h1, h2, h3 { font-family: 'Roboto Slab', serif !important; }
    @keyframes pulsate {
      0% { box-shadow: 0 0 30px rgba(0, 255, 65, 0.4); }
      50% { box-shadow: 0 0 60px rgba(0, 255, 65, 0.8); }
      100% { box-shadow: 0 0 30px rgba(0, 255, 65, 0.4); }
    }
    .pulsate-btn { animation: pulsate 2s ease-in-out infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    .fade-in { animation: fadeIn 0.2s ease forwards; }
    @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
    .hunt-progress-shimmer {
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
      animation: shimmer 1.8s ease-in-out infinite;
    }
    @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(var(--marquee-x, -50%)); } }
    .marquee-track { animation: marquee var(--marquee-duration, 25s) linear infinite; }
    .marquee-track:hover { animation-play-state: paused; }
    @keyframes billFly {
      0% { transform: translateY(0) translateX(0) rotate(0deg) scale(0.6); opacity: 0; }
      12% { opacity: 1; }
      100% { transform: translateY(-140px) translateX(var(--bill-drift, 0px)) rotate(var(--bill-spin, 360deg)) scale(1); opacity: 0; }
    }
    .money-bill { animation: billFly 1.6s ease-out forwards; }
    @keyframes printerWiggle {
      0%, 100% { transform: rotate(-4deg); }
      50% { transform: rotate(4deg); }
    }
    .printer-wiggle { animation: printerWiggle 0.5s ease-in-out infinite; }
  `;
  document.head.appendChild(styleSheet);
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">🦄</text></svg>';
  document.head.appendChild(link);
  document.title = 'Dankquan';
};

const bg = 'min-h-screen flex flex-col bg-zinc-950 text-white relative';

// Soft, layered radial blooms instead of one flat linear gradient wash — reads as moodier
// and more premium, closer to what the other sites in this space do with layered smoke/
// nebula backgrounds. Pure CSS, no image assets, fixed so it doesn't scroll/repaint per page.
function AtmosphereBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      <div className="absolute -top-1/4 -left-1/4 w-[70vw] h-[70vw] rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(97,95,255,0.35), transparent 70%)', filter: 'blur(90px)' }} />
      <div className="absolute -bottom-1/4 -right-1/4 w-[60vw] h-[60vw] rounded-full opacity-25" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.3), transparent 70%)', filter: 'blur(100px)' }} />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[50vw] h-[50vw] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, rgba(79,57,246,0.25), transparent 70%)', filter: 'blur(110px)' }} />
    </div>
  );
}

const STORAGE_KEY = 'dankquan_auth';

// Converts "Sweet Bonanza" -> tries multiple CDN filename patterns
function getCdnUrls(slotName) {
  const base = slotName.trim();
  const encoded = encodeURIComponent(base);
  return [
    `https://cdn.rainbet.com/slots/${encoded}.png`,
    `https://cdn.rainbet.com/slots/${encoded}.jpg`,
    `https://cdn.rainbet.com/slots/${encoded}.webp`,
  ];
}

function SlotImage({ name, size = 48 }) {
  const [urlIndex, setUrlIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const urls = getCdnUrls(name);

  const handleError = () => {
    if (urlIndex + 1 < urls.length) {
      setUrlIndex(i => i + 1);
    } else {
      setFailed(true);
    }
  };

  if (failed) {
    return (
      <div style={{ width: size, height: size, borderRadius: 6, background: 'rgba(79,57,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.45, flexShrink: 0 }}>
        🎰
      </div>
    );
  }

  return (
    <img
      src={urls[urlIndex]}
      alt={name}
      onError={handleError}
      style={{ width: size, height: size, borderRadius: 6, objectFit: 'cover', flexShrink: 0, background: 'rgba(79,57,246,0.1)' }}
    />
  );
}

// Simple avatar-cycler reveal for the giveaway winner. Cycles through the (now-public,
// post-roll) entrant pool at decreasing speed and lands on the actual winner — the animation
// itself doesn't decide anything, the winner was already picked server-side; this just reveals it.
// A real spinning wheel for Slot Picker — not a simplified stand-in. The winner is
// already decided server-side by the same provably-fair pick as everything else here;
// this only reveals it. Segment colors alternate for readability, the wheel spins several
// full rotations then decelerates to land the winner's segment exactly under the pointer.
function SlotWheel({ pool, winner, onDone }) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(true);
  const n = Math.max(pool?.length || 0, 1);
  const segAngle = 360 / n;
  const winnerIdx = Math.max(0, (pool || []).findIndex(p => p.username === winner.username && p.provider === winner.provider));
  const showLabels = n <= 24;

  useEffect(() => {
    const centerAngle = winnerIdx * segAngle + segAngle / 2;
    const spins = 6;
    const target = spins * 360 + (360 - centerAngle);
    const start = setTimeout(() => setRotation(target), 60);
    const finish = setTimeout(() => { setSpinning(false); onDone?.(); }, 60 + 4400);
    return () => { clearTimeout(start); clearTimeout(finish); };
  }, [winnerIdx, segAngle, onDone]);

  const colors = ['#4f39f6', '#615fff'];
  const gradient = (pool || []).map((_, i) => {
    const from = (i / n) * 100;
    const to = ((i + 1) / n) * 100;
    return `${colors[i % colors.length]} ${from}% ${to}%`;
  }).join(', ') || '#4f39f6';

  return (
    <div className="flex flex-col items-center py-6">
      <div className="relative" style={{ width: 220, height: 220 }}>
        <div
          className="absolute -top-1 left-1/2 -translate-x-1/2 z-10"
          style={{ width: 0, height: 0, borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderTop: '15px solid #facc15', filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.4))' }}
        />
        <div
          className="w-full h-full rounded-full border-[6px] border-white/10 shadow-2xl relative overflow-hidden"
          style={{
            background: `conic-gradient(${gradient})`,
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 4.4s cubic-bezier(0.11, 0.68, 0.14, 1)' : 'none',
          }}
        >
          {showLabels && (pool || []).map((p, i) => {
            const angle = i * segAngle + segAngle / 2;
            return (
              <div
                key={i}
                className="absolute top-1/2 left-1/2 text-[9px] font-bold text-white/90"
                style={{ transform: `rotate(${angle}deg) translate(0, -86px) rotate(${-angle}deg) translate(-50%, -50%)`, transformOrigin: '0 0', whiteSpace: 'nowrap' }}
              >
                {p.username.slice(0, 9)}
              </div>
            );
          })}
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-9 h-9 rounded-full bg-zinc-900 border-2 border-white/20 shadow-lg" />
        </div>
      </div>
      <p className={`mt-4 font-bold transition-all duration-200 ${!spinning ? 'text-yellow-400 text-2xl' : 'text-gray-400 text-sm'}`}>
        {spinning ? 'Spinning...' : winner.username}
      </p>
      {!spinning && <p className="text-sm text-gray-400 mt-0.5">🎉 Picked!</p>}
    </div>
  );
}

// A continuously scrolling strip of everyone entered so far — just identity (avatar,
// username, platform), never their Rainbet username / slot call / Discord ID. Duplicates
// the list once so the CSS marquee loops seamlessly; pauses on hover.
// Tile width must match the actual rendered size (w-16 = 64px) + the flex gap (gap-4 =
// 16px) so the scroll distance below is exact, not approximate.
const CAROUSEL_TILE_WIDTH = 80;
const CAROUSEL_SPEED_PX_PER_SEC = 45;

// Small icon row shown under each entrant tile / next to a winner: guild membership,
// VIP status, platform, whether their Twitch/Kick has ever been linked to a Discord
// account on this site, and whether their Rainbet account is on Dankquan's referral list.
function EntrantIcons({ inGuild, isVerified, isVIP, provider, linked, referralVerified, size = 'sm' }) {
  const px = size === 'sm' ? 11 : 13;
  return (
    <span className="inline-flex items-center gap-1" style={{ fontSize: px }}>
      {isVIP && <span title="VIP — $15,000+ wagered">👑</span>}
      {isVerified && <span title="Verified member — has the role in the Discord server">🛡️</span>}
      {inGuild && !isVerified && <span title="In the Discord server, but not verified">🦄</span>}
      <ProviderIcon provider={provider} size={px + 3} />
      {linked && <span title="Twitch/Kick linked to a Discord account on the site" style={{ color: '#4ade80' }}>✓</span>}
      {referralVerified && <span title="Rainbet account signed up under code DANKQUAN">🎟️</span>}
    </span>
  );
}

const ParticipantsCarousel = React.memo(function ParticipantsCarousel({ participants }) {
  if (!participants || participants.length === 0) return null;
  const loop = [...participants, ...participants];
  // A fixed PIXEL distance rather than a percentage. translateX(-50%) is relative to the
  // element's own current width — every time someone new enters mid-scroll, that width
  // changes and the percentage math snaps to a different offset instantly, which is what
  // caused the visible "jump" whenever the entry count changed. A pixel distance computed
  // from the participant count doesn't have that problem: it only changes when the loop
  // restarts (animation-iteration boundary), never mid-scroll.
  const distance = participants.length * CAROUSEL_TILE_WIDTH;
  const duration = Math.max(distance / CAROUSEL_SPEED_PX_PER_SEC, 8);

  return (
    <div className="overflow-hidden">
      <div
        className="flex gap-4 marquee-track"
        style={{ width: 'max-content', '--marquee-duration': `${duration}s`, '--marquee-x': `-${distance}px` }}
      >
        {loop.map((p, i) => (
          <div key={i} className="flex flex-col items-center gap-1 flex-shrink-0 w-16 group">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/10 bg-zinc-700 flex items-center justify-center transition-transform duration-200 group-hover:scale-110 group-hover:border-indigo-400/50">
              {p.avatar ? (
                <img src={p.avatar} alt={p.username} className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg">{p.provider === 'twitch' ? '🟣' : '🟢'}</span>
              )}
            </div>
            <span className="text-[10px] text-gray-400 truncate w-full text-center">{p.username}</span>
            <EntrantIcons inGuild={p.inGuild} isVerified={p.isVerified} isVIP={p.isVIP} provider={p.provider} linked={p.linked} referralVerified={p.referralVerified} />
          </div>
        ))}
      </div>
    </div>
  );
}, (prev, next) => {
  // The parent polls every 5s and always hands us a freshly-parsed array, even when the
  // roster hasn't actually changed. Comparing a cheap signature instead of reference
  // equality means a no-op poll never re-renders this at all — nothing to restart or snap.
  const sig = (list) => (list || []).map(p => `${p.provider}:${p.username}:${p.inGuild}:${p.isVIP}:${p.linked}`).join('|');
  return sig(prev.participants) === sig(next.participants);
});

// Admin-only "who is this person really" card — no proof/math, just the identity facts
// you actually need: Discord linked, guild membership, and which platform they're on.
function AdminIdentityCard({ winner, onClose, callAdmin }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const discordId = winner.resolvedDiscordId || winner.discordId || null;

  // Whatever was already loaded (from the winner card, the claims list, wherever) is shown
  // immediately so the modal isn't empty — but that data could be from minutes ago. A live
  // check kicks off the moment this opens and replaces it the second a real answer comes
  // back, so wherever admin actually stops to look, it's never showing stale information.
  const [live, setLive] = useState(null);
  const [checking, setChecking] = useState(!!discordId);
  useEffect(() => {
    if (!discordId || !callAdmin) { setChecking(false); return; }
    let cancelled = false;
    callAdmin('recheck-discord', { method: 'POST', body: { discordId } }).then((data) => {
      if (!cancelled && data?.success) setLive({ inGuild: data.inGuild, isVerified: data.isVerified });
    }).finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [discordId, callAdmin]);

  const inGuild = live ? live.inGuild : winner.inGuild;
  const isVerified = live ? live.isVerified : winner.isVerified;

  const rows = [
    { label: 'Platform', value: winner.provider === 'twitch' ? 'Twitch' : 'Kick', ok: true },
    { label: 'Discord linked', value: discordId ? `Yes (${discordId})` : 'No', ok: !!discordId },
    { label: 'In your Discord server', value: inGuild ? 'Yes' : 'No', ok: !!inGuild },
    { label: 'Verified role', value: isVerified ? 'Yes' : 'No', ok: !!isVerified },
    { label: 'Rainbet under your code', value: winner.referralVerified ? 'Yes' : 'No', ok: !!winner.referralVerified },
  ];

  const modal = (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="fade-in" style={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>{winner.username}</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#71717a' }}>{checking ? 'Checking live...' : 'Identity check'}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0, marginLeft: 12 }}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(r => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: 13, color: '#a1a1aa' }}>{r.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: r.ok ? '#4ade80' : '#71717a' }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(modal, document.body);
}

function isValidCombo(accounts) {
  const hasDiscord = !!accounts.discord;
  const streamerCount = (accounts.twitch ? 1 : 0) + (accounts.kick ? 1 : 0);
  return hasDiscord && streamerCount === 1;
}

function getComboStatus(accounts) {
  const hasDiscord = !!accounts.discord;
  const hasTwitch = !!accounts.twitch;
  const hasKick = !!accounts.kick;
  const streamerCount = (hasTwitch ? 1 : 0) + (hasKick ? 1 : 0);
  if (!hasDiscord && streamerCount === 0) return { valid: false, message: 'Connect Discord + Twitch or Discord + Kick to access Guess the Balance' };
  if (!hasDiscord) return { valid: false, message: 'Connect Discord to complete your sign-in' };
  if (streamerCount === 0) return { valid: false, message: 'Connect Twitch or Kick to complete your sign-in' };
  if (streamerCount > 1) return { valid: false, message: 'Only connect one streaming platform (Twitch OR Kick, not both)' };
  return { valid: true, message: "You're all set!" };
}

function loadSession() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}
function saveSession(a) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(a)); } catch {}
}

const SITE_URL = window.location.origin;

function getTwitchAuthUrl() {
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_TWITCH_CLIENT_ID || '',
    redirect_uri: `${SITE_URL}/callback/twitch`,
    response_type: 'code',
    scope: 'user:read:email',
    state: 'twitch',
  });
  return `https://id.twitch.tv/oauth2/authorize?${params}`;
}

// Admin-only, one-time: separate from the public "Sign in with Twitch" flow above. This
// requests the "user:read:chat" scope so Twitch will let this app subscribe to Dankquan's
// own chat via EventSub. Uses a different redirect_uri (/callback/twitch-chat) so it never
// gets confused with a regular visitor logging in.
function getTwitchChatAuthUrl() {
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_TWITCH_CLIENT_ID || '',
    redirect_uri: `${SITE_URL}/callback/twitch-chat`,
    response_type: 'code',
    scope: 'user:read:chat user:write:chat user:bot channel:bot moderator:read:followers',
    state: 'twitch-chat',
    // Always show Twitch's approval screen — without this, Twitch silently bounces straight
    // back when the app was approved before, which just looks like the page refreshed.
    force_verify: 'true',
  });
  return `https://id.twitch.tv/oauth2/authorize?${params}`;
}

function getDiscordAuthUrl() {
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_DISCORD_CLIENT_ID || '',
    redirect_uri: `${SITE_URL}/callback/discord`,
    response_type: 'code',
    scope: 'identify',
    state: 'discord',
  });
  return `https://discord.com/api/oauth2/authorize?${params}`;
}

async function getKickAuthUrl() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = btoa(String.fromCharCode(...array)).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
  sessionStorage.setItem('kick_code_verifier', verifier);
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_KICK_CLIENT_ID || '',
    redirect_uri: `${SITE_URL}/callback/kick`,
    response_type: 'code',
    scope: 'user:read',
    state: 'kick',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  return `https://id.kick.com/oauth/authorize?${params}`;
}

const PROVIDERS = {
  twitch:  { name: 'Twitch',  color: '#9146FF', bg: 'rgba(145,70,255,0.12)', border: 'rgba(145,70,255,0.3)' },
  kick:    { name: 'Kick',    color: '#53FC18', bg: 'rgba(83,252,24,0.10)',  border: 'rgba(83,252,24,0.3)'  },
  discord: { name: 'Discord', color: '#5865F2', bg: 'rgba(88,101,242,0.12)', border: 'rgba(88,101,242,0.3)' },
};

function ProviderIcon({ provider, size = 18 }) {
  if (provider === 'twitch') return <svg width={size} height={size} viewBox="0 0 512 512" fill="#9146FF"><path d="M391.17 103.47H352.54v109.7h38.63zm-104.32 0h-38.63v109.7h38.63zM120.83 0L24.31 100.05v311.9h115.19V512l96.53-100.05h77.25L487.69 256V0zm328.24 237.75l-77.22 80.05h-77.24l-67.6 70.05v-70.05h-96.53V37.75h318.61z"/></svg>;
  if (provider === 'kick') return <svg width={size} height={size} viewBox="0 0 24 24" fill="#53FC18"><path d="M2 2h4v8l6-8h5L10 12l7 10H12l-6-8v8H2V2z"/></svg>;
  if (provider === 'discord') return <svg width={size * 1.15} height={size * 1.15} viewBox="0 0 127.14 96.36" fill="#5865F2"><path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15zM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69z"/></svg>;
  return null;
}

function AuthModal({ accounts, error, setError, disconnect, onClose }) {
  const status = getComboStatus(accounts);
  const hasTwitch = !!accounts.twitch;
  const hasKick = !!accounts.kick;
  const hasDiscord = !!accounts.discord;
  const connected = Object.values(accounts);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const modal = (
    <div onClick={e => e.target === e.currentTarget && onClose?.()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="fade-in" style={{ background: '#18181b', border: '1px solid rgba(79,57,246,0.25)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>Sign In</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#71717a' }}>Guess the Balance needs Discord + Twitch <em>or</em> Discord + Kick</p>
          </div>
          {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0, marginLeft: 12 }}>×</button>}
        </div>

        {connected.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {connected.map(acc => {
              const p = PROVIDERS[acc.provider];
              return (
                <div key={acc.provider} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: p.bg, border: `1px solid ${p.border}` }}>
                  {acc.avatar && <img src={acc.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e7' }}>{acc.username}</div>
                    <div style={{ fontSize: 11, color: p.color }}>{p.name} connected</div>
                  </div>
                  <button onClick={() => disconnect(acc.provider)} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 18 }}>×</button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!hasDiscord && (
            <button onClick={() => { window.location.href = getDiscordAuthUrl(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 16px', borderRadius: 10, background: PROVIDERS.discord.bg, border: `1px solid ${PROVIDERS.discord.border}`, color: '#e4e4e7', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              <ProviderIcon provider="discord" /> Sign in with Discord
            </button>
          )}
          {!hasTwitch && (
            <button onClick={() => { window.location.href = getTwitchAuthUrl(); }} disabled={hasKick} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 16px', borderRadius: 10, background: hasKick ? 'rgba(255,255,255,0.03)' : PROVIDERS.twitch.bg, border: `1px solid ${hasKick ? 'rgba(255,255,255,0.06)' : PROVIDERS.twitch.border}`, color: hasKick ? '#52525b' : '#e4e4e7', cursor: hasKick ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}>
              <ProviderIcon provider="twitch" /> Sign in with Twitch
            </button>
          )}
          {!hasKick && (
            <button onClick={async () => { window.location.href = await getKickAuthUrl(); }} disabled={hasTwitch} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 16px', borderRadius: 10, background: hasTwitch ? 'rgba(255,255,255,0.03)' : PROVIDERS.kick.bg, border: `1px solid ${hasTwitch ? 'rgba(255,255,255,0.06)' : PROVIDERS.kick.border}`, color: hasTwitch ? '#52525b' : '#e4e4e7', cursor: hasTwitch ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}>
              <ProviderIcon provider="kick" /> Sign in with Kick
            </button>
          )}
        </div>

        <div style={{ marginTop: 20, padding: '10px 14px', borderRadius: 8, background: status.valid ? 'rgba(34,197,94,0.08)' : 'rgba(79,57,246,0.08)', border: `1px solid ${status.valid ? 'rgba(34,197,94,0.2)' : 'rgba(79,57,246,0.15)'}`, fontSize: 13, color: status.valid ? '#4ade80' : '#a3b3ff', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{status.valid ? '✓' : '○'}</span> {status.message}
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 13, color: '#f87171', display: 'flex', justifyContent: 'space-between' }}>
            {error} <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {status.valid && (
          <button onClick={onClose} style={{ marginTop: 16, width: '100%', padding: 12, borderRadius: 10, background: 'linear-gradient(135deg, #4f39f6, #615fff)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Enter Guess the Balance →
          </button>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

// Everything the signed-in person has already claimed — opened from the profile menu so the
// Giveaway page itself only has to show prizes that are still waiting to be claimed.
function WinHistoryModal({ accounts, onClose }) {
  const [wins, setWins] = useState(null);
  const provider = accounts.twitch ? 'twitch' : accounts.kick ? 'kick' : null;
  const username = accounts.twitch?.username || accounts.kick?.username || '';
  const sessionToken = accounts.discord?.sessionToken;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!provider || !username) { setWins([]); return; }
      try {
        const res = await fetch(`/.netlify/functions/claim-giveaway?provider=${provider}&username=${encodeURIComponent(username)}`, {
          headers: { 'X-Session-Token': sessionToken || '' },
        });
        const data = await res.json();
        if (!cancelled) setWins((data.claims || []).filter(c => c.claimed));
      } catch { if (!cancelled) setWins([]); }
    })();
    return () => { cancelled = true; };
  }, [provider, username, sessionToken]);

  const total = (wins || []).reduce((sum, w) => sum + (w.amount || 0), 0);

  const modal = (
    <div onClick={e => e.target === e.currentTarget && onClose()} className="fixed inset-0 z-[99999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 md:p-6">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col fade-in shadow-2xl shadow-black/60">
        <div className="px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold">🏆 Giveaway win history</h3>
            {wins && wins.length > 0 && <p className="text-xs text-gray-400 mt-0.5">{wins.length} win{wins.length === 1 ? '' : 's'} · <span className="text-green-400 font-semibold">{money(total)}</span> total</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {wins === null ? (
            <p className="text-center text-gray-500 text-sm py-10">Loading…</p>
          ) : wins.length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-10">No claimed wins yet — prizes you claim on the Giveaway page will show up here.</p>
          ) : wins.map(w => (
            <div key={w.id} className="flex items-center justify-between gap-3 bg-zinc-800/50 rounded-xl px-4 py-3 border border-white/[0.06]">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{w.eventType === 'slotpicker' ? '🎰' : '🎁'} {w.eventTitle || (w.eventType === 'slotpicker' ? 'Slot Picker' : 'Giveaway')}</p>
                <p className="text-xs text-gray-400">Won {new Date(w.wonAt).toLocaleDateString()} · <span className="text-green-400 font-bold">{money(w.amount || 0)}</span></p>
              </div>
              <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold shrink-0 ${w.status === 'paid' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-white/5 text-gray-400 border border-white/10'}`}>
                {w.status === 'paid' ? '✓ Paid' : '⏳ Awaiting payment'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(modal, document.body);
}

function UserMenu({ accounts, isValid, disconnect, disconnectAll, rainbetUsername, onSaveRainbet }) {
  const [showModal, setShowModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [open, setOpen] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [rainbetInput, setRainbetInput] = useState('');
  const [savingRainbet, setSavingRainbet] = useState(false);
  const [rainbetError, setRainbetError] = useState(null);
  const connected = Object.keys(accounts);
  const primary = accounts.discord || accounts.twitch || accounts.kick;

  const handleSaveRainbet = async () => {
    if (!rainbetInput.trim()) return;
    setSavingRainbet(true);
    setRainbetError(null);
    const data = await onSaveRainbet(rainbetInput.trim());
    if (!data.success) setRainbetError(data.message || 'Could not save right now.');
    setSavingRainbet(false);
  };

  if (connected.length === 0) return (
    <>
      <button onClick={() => setShowModal(true)} className="px-4 py-2 md:px-6 md:py-3 text-base md:text-xl font-semibold" style={{ borderRadius: 12, background: 'rgba(79,57,246,0.15)', border: '1px solid rgba(79,57,246,0.3)', color: '#a3b3ff', cursor: 'pointer', whiteSpace: 'nowrap' }}>Sign In</button>
      {showModal && <AuthModal accounts={accounts} error={authError} setError={setAuthError} disconnect={disconnect} onClose={() => setShowModal(false)} />}
    </>
  );

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)} className="flex items-center gap-2 md:gap-3 pl-1.5 pr-2.5 py-1.5 md:pl-[9px] md:pr-[15px] md:py-[9px] rounded-xl bg-white/[0.04] border border-white/[0.08] cursor-pointer text-white">
        {primary?.avatar && <img src={primary.avatar} alt="" className="w-[26px] h-[26px] md:w-[39px] md:h-[39px] rounded-full object-cover flex-shrink-0" onError={e => e.target.style.display='none'} />}
        <div className="flex gap-[3px] md:gap-[4.5px]">{connected.map(p => <div key={p} className="w-[6px] h-[6px] md:w-[9px] md:h-[9px] rounded-full" style={{ background: PROVIDERS[p].color }} />)}</div>
        <span className="text-[11px] md:text-[17px]" style={{ color: '#71717a' }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
          <div className="fade-in" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#18181b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 18, minWidth: 360, zIndex: 200, boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
            {connected.map(p => (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 6px', fontSize: 20 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: PROVIDERS[p].color }} />
                <span style={{ color: '#a1a1aa', textTransform: 'capitalize', minWidth: 75 }}>{p}</span>
                <span style={{ color: '#e4e4e7', fontWeight: 500 }}>{accounts[p].username}</span>
              </div>
            ))}
            <div style={{ fontSize: 17, padding: '9px 12px', borderRadius: 9, margin: '12px 0', background: isValid ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', color: isValid ? '#4ade80' : '#f87171' }}>
              {isValid ? '✓ Guess the Balance access' : '⚠ Incomplete sign-in'}
            </div>

            {accounts.discord && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12, marginTop: 6 }}>
                <p style={{ fontSize: 17, color: '#71717a', marginBottom: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Rainbet Username</p>
                {rainbetUsername === null ? (
                  <p style={{ fontSize: 18, color: '#71717a' }}>Loading...</p>
                ) : rainbetUsername ? (
                  <div>
                    <p style={{ fontSize: 20, color: '#e4e4e7', fontWeight: 500 }}>🔒 {rainbetUsername}</p>
                    <p style={{ fontSize: 16, color: '#71717a', marginTop: 3 }}>Linked — only Dankquan can change this.</p>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', gap: 9 }}>
                      <input
                        value={rainbetInput}
                        onChange={e => setRainbetInput(e.target.value)}
                        placeholder="Your Rainbet username"
                        style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9, padding: '9px 12px', color: '#fff', fontSize: 18 }}
                      />
                      <button onClick={handleSaveRainbet} disabled={savingRainbet || !rainbetInput.trim()} style={{ padding: '9px 15px', borderRadius: 9, background: 'rgba(79,57,246,0.2)', border: '1px solid rgba(79,57,246,0.35)', color: '#a3b3ff', fontSize: 16.5, fontWeight: 700, cursor: 'pointer', opacity: savingRainbet ? 0.5 : 1, flexShrink: 0 }}>
                        {savingRainbet ? '...' : 'Link'}
                      </button>
                    </div>
                    <p style={{ fontSize: 16, color: '#71717a', marginTop: 6 }}>One-time — can't be changed once set (only Dankquan can update it after).</p>
                    {rainbetError && <p style={{ fontSize: 16.5, color: '#f87171', marginTop: 6 }}>{rainbetError}</p>}
                  </div>
                )}
              </div>
            )}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12, marginTop: 12, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {accounts.discord && (accounts.twitch || accounts.kick) && (
                <button onClick={() => { setOpen(false); setShowHistory(true); }} style={{ width: '100%', padding: 12, borderRadius: 9, background: 'rgba(234,179,8,0.10)', border: '1px solid rgba(234,179,8,0.28)', color: '#fde047', fontSize: 18, cursor: 'pointer', fontWeight: 600 }}>
                  🏆 Giveaway win history
                </button>
              )}
              {!isValid && (
                <button onClick={() => { setOpen(false); setShowModal(true); }} style={{ width: '100%', padding: 12, borderRadius: 9, background: 'rgba(79,57,246,0.12)', border: '1px solid rgba(79,57,246,0.25)', color: '#a3b3ff', fontSize: 18, cursor: 'pointer', fontWeight: 600 }}>
                  Connect more accounts
                </button>
              )}
              <button onClick={() => { disconnectAll(); setOpen(false); }} style={{ width: '100%', padding: 12, borderRadius: 9, background: 'transparent', border: '1px solid rgba(255,255,255,0.06)', color: '#52525b', fontSize: 18, cursor: 'pointer' }}>Sign out</button>
            </div>
          </div>
        </>
      )}
      {showModal && <AuthModal accounts={accounts} error={authError} setError={setAuthError} disconnect={disconnect} onClose={() => setShowModal(false)} />}
      {showHistory && <WinHistoryModal accounts={accounts} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function OAuthCallback({ onAccountAdded }) {
  const { provider } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Completing sign-in...');
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const safeTimeout = (fn, ms) => { const t = setTimeout(() => { if (!cancelled) fn(); }, ms); timers.push(t); return t; };

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    if (error || !code || !['twitch','discord','kick','twitch-chat'].includes(provider)) {
      setStatus(params.get('error_description') || 'Authorization failed.');
      setErrored(true);
      safeTimeout(() => navigate('/'), 3000);
      return () => { cancelled = true; timers.forEach(clearTimeout); };
    }
    const verifier = sessionStorage.getItem('kick_code_verifier');
    const exchange = async () => {
      try {
        setStatus(provider === 'twitch-chat' ? 'Connecting chat reading...' : `Connecting your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account...`);
        const body = { code };
        if (provider === 'kick' && verifier) body.codeVerifier = verifier;
        const res = await fetch(`/.netlify/functions/auth-${provider}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed');
        if (cancelled) return;

        // This one isn't a visitor login — it's Dankquan granting the site permission to
        // read his own Twitch chat, so it doesn't touch localStorage or the account menu.
        if (provider === 'twitch-chat') {
          const missing = data.missingExtras || [];
          setStatus(missing.length
            ? `Connected as ${data.username}, but Twitch didn't grant: ${missing.join(', ')}. Try again and approve everything. Redirecting to admin...`
            : `Twitch connected as ${data.username} — chat posting and follow time are on! Redirecting to admin...`);
          safeTimeout(() => navigate('/admin'), missing.length ? 5000 : 2000);
          return;
        }

        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        existing[provider] = data;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
        if (onAccountAdded) onAccountAdded(existing);
        if (provider === 'kick') sessionStorage.removeItem('kick_code_verifier');
        setStatus(`${data.username} connected! Redirecting...`);
        safeTimeout(() => navigate('/'), 1200);
      } catch (err) {
        if (cancelled) return;
        setStatus(err.message || 'Something went wrong.');
        setErrored(true);
        safeTimeout(() => navigate('/'), 3000);
      }
    };
    exchange();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [provider, navigate, onAccountAdded]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0f1e', color: '#e4e4e7', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ textAlign: 'center', maxWidth: 320 }}>
        {!errored && <div style={{ width: 40, height: 40, border: '3px solid rgba(79,57,246,0.3)', borderTopColor: '#615fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />}
        {errored && <div style={{ fontSize: 32, marginBottom: 16 }}>⚠</div>}
        <p style={{ color: errored ? '#f87171' : '#a1a1aa', fontSize: 15 }}>{status}</p>
      </div>
    </div>
  );
}

function Header({ accounts, isValid, disconnect, disconnectAll, rainbetUsername, onSaveRainbet }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nav = [
    { to: '/', label: 'Home', Icon: Home },
    { to: '/bonus-hunts', label: 'Bonus Hunts', Icon: Target },
    { to: '/giveaway', label: 'Giveaway', Icon: Gift },
    { to: '/rewards', label: 'Rewards', Icon: Gem },
    ...(SHOW_CLIPS_PAGE ? [{ to: '/clips', label: 'Clips', Icon: Film }] : []),
  ];

  return (
    <header className="sticky top-0 z-40 bg-zinc-950/80 backdrop-blur border-b border-white/[0.06]">
      <div className="relative flex items-center justify-between px-4 py-3 w-full">
        <Link to="/" className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition min-w-0">
          <span className="w-8 h-8 md:w-11 md:h-11 rounded-full flex items-center justify-center flex-shrink-0 text-lg md:text-2xl bg-gradient-to-br from-indigo-500/25 to-purple-500/10 border border-white/10">🦄</span>
          <span className="text-xl md:text-4xl font-black bg-gradient-to-r from-[#a3b3ff] via-[#615fff] to-[#372aac] bg-clip-text text-transparent truncate">DANKQUAN</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {nav.map(item => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2.5 rounded-xl text-sm xl:text-base font-semibold transition whitespace-nowrap ${active ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-indigo-500/10 hover:text-white'}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-2">
          <UserMenu accounts={accounts} isValid={isValid} disconnect={disconnect} disconnectAll={disconnectAll} rainbetUsername={rainbetUsername} onSaveRainbet={onSaveRainbet} />
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-zinc-950/95 border-t border-white/[0.06] px-4 py-4 flex flex-col gap-2">
          {nav.map(item => <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="px-4 py-3 rounded-xl hover:bg-indigo-500/20 transition font-medium">{item.label}</Link>)}
        </div>
      )}
    </header>
  );
}

function ResponsibleGamblingFooter() {
  return (
    <div className="mt-10">
      <div className="h-[3px] bg-gradient-to-r from-[#a3b3ff] via-[#615fff] to-[#372aac]" />
      <div className="bg-black/60">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center text-xs text-gray-400 leading-relaxed">
          <span className="font-bold text-gray-300">Gamble Aware.</span> Gambling should be fun. If you or someone you know has a gambling problem, help is available. Visit{' '}
          <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition">BeGambleAware.org</a>{' '}
          or call <span className="font-bold text-gray-300">1-800-522-4700</span> (US National Problem Gambling Helpline). Must be 18+ (or legal age in your jurisdiction) to participate. Please gamble responsibly.
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  const [payouts, setPayouts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/.netlify/functions/giveaway?path=payouts')
      .then(res => res.json())
      .then(data => { if (!cancelled && typeof data?.totalPaid === 'number') setPayouts(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const shownTotal = useCountUp(payouts?.totalPaid || 0);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 md:py-20 min-h-[80vh]">
      <div className="relative max-w-3xl mx-auto text-center">
        <div
          className="absolute inset-0 -z-10 blur-3xl opacity-40"
          style={{ background: 'radial-gradient(circle at center, rgba(97,95,255,0.5), transparent 70%)' }}
        />

        <div className="text-7xl md:text-8xl mb-6 animate-bounce">🦄</div>

        <h1 className="text-6xl md:text-8xl font-black bg-gradient-to-r from-[#a3b3ff] via-[#615fff] to-[#372aac] bg-clip-text text-transparent leading-tight">
          DANKQUAN
        </h1>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href="https://rainbet.com/?r=dankquan" target="_blank" rel="noopener noreferrer" className="px-7 py-3.5 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-300 hover:to-green-500 text-black font-bold rounded-xl transition active:scale-[0.98] pulsate-btn">
            Join Rainbet — Code DANKQUAN
          </a>
        </div>
        <p className="text-[11px] text-gray-600 mt-2">Ad · Dankquan earns commission on play under this code · 18+ · Play responsibly</p>

        {payouts?.payoutCount > 0 && (
          <div className="mt-10 bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-green-500/25 px-6 py-5">
            <p className="text-xs uppercase tracking-[0.2em] text-green-400/80 mb-1">Given away to the community</p>
            <p className="text-4xl md:text-5xl font-black tabular-nums text-green-400 leading-none">{money(shownTotal)}</p>
            <Link to="/giveaway" className="inline-block mt-2 text-xs text-indigo-400 hover:text-indigo-300 underline">See the giveaway portal →</Link>
          </div>
        )}

        <div className="mt-10 flex items-center justify-center gap-6">
          <a href="https://twitch.tv/Dankquan" target="_blank" rel="noopener noreferrer" aria-label="Twitch" className="w-16 h-16 rounded-full flex items-center justify-center bg-black border border-white/10 hover:border-indigo-400/40 transition active:scale-[0.98]">
            <ProviderIcon provider="twitch" size={30} />
          </a>
          <a href="https://kick.com/Dankquan" target="_blank" rel="noopener noreferrer" aria-label="Kick" className="w-16 h-16 rounded-full flex items-center justify-center bg-black border border-white/10 hover:border-green-400/40 transition active:scale-[0.98]">
            <ProviderIcon provider="kick" size={30} />
          </a>
          <a href="https://discord.gg/Dankquan" target="_blank" rel="noopener noreferrer" aria-label="Discord" className="w-16 h-16 rounded-full flex items-center justify-center bg-black border border-white/10 hover:border-blue-400/40 transition active:scale-[0.98]">
            <ProviderIcon provider="discord" size={30} />
          </a>
        </div>
      </div>
    </div>
  );
}

// Compact card for the horizontal past-hunts strip — click to load full detail below.
function PastHuntStripCard({ hunt, selected, onClick, fxRates }) {
  const isNonUSD = hunt.currency && hunt.currency !== 'USD';
  const rate = isNonUSD ? fxRates?.[hunt.currency] : null;
  const pl = isNonUSD && rate ? (hunt.stats?.profitLoss || 0) / rate : hunt.stats?.profitLoss;
  const startCost = isNonUSD && rate ? (hunt.startCost || 0) / rate : hunt.startCost;
  const isProfit = pl >= 0;
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 w-36 md:w-60 text-left rounded-xl md:rounded-2xl p-3 md:p-6 border transition-all duration-200 hover:-translate-y-1 active:translate-y-0 active:scale-[0.98] ${
        selected ? 'bg-indigo-600/20 border-indigo-400/60' : 'bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 border-white/[0.06] hover:border-indigo-500/40'
      }`}
    >
      <p className="text-sm md:text-[21px] font-bold truncate mb-0.5 md:mb-1">{hunt.title}</p>
      <p className={`text-sm md:text-[21px] font-black mb-1 md:mb-2 ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
        {isProfit ? '+' : ''}${pl?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </p>
      <div className="text-[11px] md:text-lg text-gray-400 space-y-0 md:space-y-0.5">
        <div className="flex justify-between"><span>Start</span><span className="text-gray-300">${startCost?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
        <div className="flex justify-between"><span>Avg X</span><span className="text-gray-300">{hunt.stats?.currentAverageMultiplier?.toFixed(1)}x</span></div>
      </div>
      {isNonUSD && <p className="text-[9px] md:text-xs text-gray-500 mt-1 md:mt-2">USD · tap for {hunt.currency}</p>}
    </button>
  );
}

// Full breakdown for whichever past hunt is selected in the strip above.
function PastHuntDetail({ hunt }) {
  const [guessEntries, setGuessEntries] = useState(null);
  const [bonusSort, setBonusSort] = useState('payout');
  const pl = hunt.stats?.profitLoss;
  const isProfit = pl >= 0;
  const finalBalance = hunt.stats?.totalWinnings;

  useEffect(() => {
    let cancelled = false;
    setGuessEntries(null);
    (async () => {
      try {
        const res = await fetch(`/.netlify/functions/hunts?path=hunts/${hunt.id}/guess-the-balance`);
        const data = await res.json();
        if (!cancelled) setGuessEntries(data.entries || []);
      } catch {
        if (!cancelled) setGuessEntries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [hunt.id]);

  const closestGuess = finalBalance > 0 && guessEntries?.length > 0
    ? (() => {
        const winner = guessEntries[0];
        if (!winner) return null;
        return { ...winner, diff: Math.abs(winner.guessAmount - finalBalance) };
      })()
    : null;

  const bonuses = hunt.bonuses || [];
  const sortedBonuses = bonusSort === 'payout'
    ? [...bonuses].sort((a, b) => (b.payout || 0) - (a.payout || 0))
    : bonuses;

  return (
    <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-xl md:rounded-2xl border border-white/10 p-3 md:p-6 space-y-2 md:space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg md:text-3xl font-bold">{hunt.title}</h3>
          <p className="text-xs md:text-[21px] text-gray-400 mt-0.5 md:mt-1">{hunt.casino} · {hunt.currency} · {hunt.stats?.bonusCount} bonuses</p>
        </div>
        <span className={`px-2.5 py-1 md:px-4 md:py-1.5 rounded-full text-xs md:text-[21px] font-bold ${isProfit ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          {isProfit ? '+' : ''}${pl?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 text-center">
        <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3"><p className="text-gray-400 text-[10px] md:text-lg">Start Cost</p><p className="font-bold text-sm md:text-2xl text-green-400">${hunt.startCost?.toLocaleString()}</p></div>
        <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3"><p className="text-gray-400 text-[10px] md:text-lg">Winnings</p><p className="font-bold text-sm md:text-2xl">${hunt.stats?.totalWinnings?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
        <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3"><p className="text-gray-400 text-[10px] md:text-lg">Avg Multiplier</p><p className="font-bold text-sm md:text-2xl">{hunt.stats?.currentAverageMultiplier?.toFixed(1)}x</p></div>
        <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3"><p className="text-gray-400 text-[10px] md:text-lg">P/L %</p><p className={`font-bold text-sm md:text-2xl ${isProfit ? 'text-green-400' : 'text-red-400'}`}>{isProfit ? '+' : ''}{hunt.stats?.profitLossPercentage?.toFixed(1)}%</p></div>
      </div>

      {closestGuess && (
        <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-[21px] px-1 flex-wrap">
          <span className="text-yellow-400">🎯</span>
          <span className="text-gray-400">Closest guess:</span>
          <span className="font-bold text-white">{closestGuess.username?.replace(/\s*\[\d+\]$/, '')}</span>
          <span className="text-gray-500">—</span>
          <span className="text-green-400 font-semibold">${closestGuess.guessAmount?.toLocaleString()}</span>
          <span className="text-gray-600 text-[10px] md:text-lg">(off by ${Math.abs(closestGuess.guessAmount - finalBalance).toLocaleString(undefined, { maximumFractionDigits: 0 })})</span>
        </div>
      )}

      {sortedBonuses.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5 md:mb-2">
            <p className="text-xs md:text-[21px] text-gray-400 font-semibold">Bonus Results</p>
            <div className="flex gap-1 text-[10px] md:text-lg">
              <button onClick={() => setBonusSort('order')} className={`px-2.5 py-1 md:px-4 md:py-1.5 rounded-lg font-semibold transition ${bonusSort === 'order' ? 'bg-indigo-600 text-white' : 'bg-zinc-700 text-gray-400 hover:bg-zinc-600'}`}>Order</button>
              <button onClick={() => setBonusSort('payout')} className={`px-2.5 py-1 md:px-4 md:py-1.5 rounded-lg font-semibold transition ${bonusSort === 'payout' ? 'bg-indigo-600 text-white' : 'bg-zinc-700 text-gray-400 hover:bg-zinc-600'}`}>Highest Payout</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 md:gap-2 max-h-96 overflow-y-auto">
            {sortedBonuses.map((b, i) => {
              const multiplier = b.betSize > 0 ? (b.payout / b.betSize).toFixed(1) : null;
              const isBig = b.payout > b.betSize * 100;
              return (
                <div key={i} className={`flex justify-between items-center p-2 md:p-3 rounded-lg md:rounded-xl ${isBig ? 'bg-green-900/30 border border-green-500/30' : 'bg-white/5'}`}>
                  <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                    <SlotImage name={b.slotName} size={40} />
                    <div className="min-w-0">
                      <p className="font-semibold text-xs md:text-[21px] truncate">{b.slotName}</p>
                      <p className="text-[10px] md:text-lg text-gray-400">{b.provider} · ${b.betSize} bet</p>
                    </div>
                  </div>
                  <div className="text-right ml-2 md:ml-3 flex-shrink-0">
                    <p className={`font-bold text-xs md:text-[21px] ${isBig ? 'text-green-400' : 'text-gray-300'}`}>${b.payout?.toLocaleString()}</p>
                    {multiplier && <p className="text-[10px] md:text-lg text-gray-500">{multiplier}x</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Everyone's current guess, scrolling — same marquee pattern as the participants
// carousel and the payout ticker.
const GuessTicker = React.memo(function GuessTicker({ entries }) {
  if (!entries || entries.length === 0) return null;
  const loop = [...entries, ...entries];
  const duration = Math.max(entries.length * 3, 12);

  return (
    <div className="overflow-hidden mb-4">
      <div className="flex gap-2.5 marquee-track" style={{ width: 'max-content', '--marquee-duration': `${duration}s` }}>
        {loop.map((g, i) => (
          <span key={i} className="flex items-center gap-1.5 text-xs whitespace-nowrap px-3 py-1.5 rounded-full bg-white/5 border border-zinc-600/60 shrink-0">
            <span className="text-gray-300">{g.username?.replace(/\s*\[\d+\]$/, '')}</span>
            <span className="text-green-400 font-bold tabular-nums">${g.guessAmount?.toLocaleString()}</span>
          </span>
        ))}
      </div>
    </div>
  );
}, (prev, next) => {
  const sig = (list) => (list || []).map(g => `${g.username}:${g.guessAmount}`).join('|');
  return sig(prev.entries) === sig(next.entries);
});

function BonusHuntsPage({ accounts, disconnect }) {
  const [hunts, setHunts] = useState([]);
  const [currentHunt, setCurrentHunt] = useState(null);
  const [guessStatus, setGuessStatus] = useState(null);
  const [guessAmount, setGuessAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [userGuess, setUserGuess] = useState(null);
  const [guessHistory, setGuessHistory] = useState([]);
  const [bonusSort, setBonusSort] = useState('order');
  const [selectedPastHuntId, setSelectedPastHuntId] = useState(null);
  const [showPastHunts, setShowPastHunts] = useState(false);
  const [fxRates, setFxRates] = useState(null);
  const unmountedRef = useRef(false);
  useEffect(() => () => { unmountedRef.current = true; }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/.netlify/functions/fx')
      .then(res => res.json())
      .then(data => { if (!cancelled && data.rates) setFxRates(data.rates); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isValid = isValidCombo(accounts);
  const streamerName = accounts.twitch?.username || accounts.kick?.username || '';

  const loadHunts = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/hunts?path=hunts&limit=1000');
      const data = await res.json();
      const all = data.hunts || [];
      setCurrentHunt(all[0] || null);
      setHunts(all.slice(1));
      if (all[0]) {
        try {
          const gRes = await fetch(`/.netlify/functions/hunts?path=hunts/${all[0].id}/guess-the-balance`);
          const gData = await gRes.json();
          setGuessStatus(gData);
          const myGuess = gData.entries?.find(g => g.username?.toLowerCase() === streamerName?.toLowerCase());
          if (myGuess) {
            setUserGuess(myGuess);
            setGuessAmount(myGuess.guessAmount.toString());
          }
        } catch(e) {}
      }
    } catch(e) { console.error(e); }
    if (!silent) setLoading(false);
  }, [streamerName]);

  useEffect(() => {
    loadHunts();
    let interval = null;
    const startInterval = () => {
      if (interval) return;
      interval = setInterval(() => loadHunts({ silent: true }), 60000);
    };
    const stopInterval = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        loadHunts({ silent: true });
        startInterval();
      }
    };
    if (!document.hidden) startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadHunts]);

  const [submitSuccess, setSubmitSuccess] = useState(false);

  const submitGuess = async () => {
    if (!guessAmount || !currentHunt) return;
    setSubmitting(true);
    setSubmitSuccess(false);
    try {
      const res = await fetch('/.netlify/functions/guess', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': accounts.discord?.sessionToken || '' },
        body: JSON.stringify({
          huntId: currentHunt.id,
          username: streamerName,
          guessAmount: parseFloat(guessAmount),
        }),
      });
      const data = await res.json();
      if (data.success) {
        const newGuess = { username: streamerName, guessAmount: parseFloat(guessAmount), timestamp: new Date().toLocaleTimeString() };
        setUserGuess(newGuess);
        setGuessHistory(prev => [...prev, newGuess].slice(-20));
        setSubmitSuccess(true);
        setTimeout(() => { if (!unmountedRef.current) setSubmitSuccess(false); }, 4000);
        const expectedAmount = parseFloat(guessAmount);
        let attempts = 0;
        const poll = async () => {
          if (unmountedRef.current) return;
          attempts++;
          try {
            const gRes = await fetch(`/.netlify/functions/hunts?path=hunts/${currentHunt.id}/guess-the-balance`);
            const gData = await gRes.json();
            if (unmountedRef.current) return;
            const entries = gData.entries || [];
            const myEntry = entries.find(e => e.username?.toLowerCase().includes(streamerName.toLowerCase()));
            if (myEntry && myEntry.guessAmount === expectedAmount) return;
            if (attempts < 6) setTimeout(poll, 2000);
          } catch (e) {
            if (attempts < 6 && !unmountedRef.current) setTimeout(poll, 2000);
          }
        };
        setTimeout(poll, 1500);
      } else {
        alert(`❌ ${data.message || 'Error submitting guess'}`);
      }
    } catch(e) { alert('❌ Failed to submit guess'); }
    setSubmitting(false);
  };

  return (
    <div className="flex-1 flex flex-col">
    <div className="max-w-6xl mx-auto p-6 md:p-10 flex-1 w-full">
      <h1 className="text-4xl font-black mb-4 text-center">🎯 Bonus Hunts</h1>
      <div className="flex justify-center mb-8">
        <a href="https://rainbet.com/?r=dankquan" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gradient-to-r from-green-400 to-green-600 text-black font-bold rounded-xl hover:from-green-300 hover:to-green-500 transition pulsate-btn text-center">🟢 Join Rainbet →</a>
      </div>

      {loading && <div className="text-center text-xl py-20">Loading hunts...</div>}

      {!loading && (() => {
        const selectedHunt = hunts.find(h => h.id === selectedPastHuntId) || null;

        return (
          <div className="space-y-8">
            {hunts.length > 0 && (() => {
              const wins = hunts.filter(h => (h.stats?.profitLoss || 0) >= 0).length;
              const losses = hunts.length - wins;
              const winRate = Math.round((wins / hunts.length) * 100);
              return (
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-1 md:gap-2">
                  <button onClick={() => setShowPastHunts(v => !v)} className="flex items-center gap-2 text-left group" aria-expanded={showPastHunts}>
                    <h2 className="text-sm md:text-[21px] font-bold text-gray-400 group-hover:text-gray-200 transition uppercase tracking-wide">📜 Past Hunts</h2>
                    <span className="text-gray-500 text-xs md:text-base">{showPastHunts ? '▾' : '▸'}</span>
                  </button>
                  <div className="flex items-center gap-1 md:gap-2">
                    <span className="text-xs md:text-lg text-gray-500">W/L ({hunts.length} hunts):</span>
                    <span className="text-sm md:text-2xl font-black text-green-400">{wins}W</span>
                    <span className="text-sm md:text-2xl font-black text-gray-600">-</span>
                    <span className="text-sm md:text-2xl font-black text-red-400">{losses}L</span>
                    <span className="text-xs md:text-lg text-gray-400">({winRate}%)</span>
                  </div>
                </div>
                {showPastHunts && (<>
                <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                  {hunts.map(hunt => (
                    <PastHuntStripCard
                      key={hunt.id}
                      hunt={hunt}
                      selected={selectedPastHuntId === hunt.id}
                      onClick={() => setSelectedPastHuntId(id => id === hunt.id ? null : hunt.id)}
                      fxRates={fxRates}
                    />
                  ))}
                </div>
                {selectedHunt && (
                  <div className="mt-3 fade-in">
                    <div className="flex justify-end mb-2">
                      <button onClick={() => setSelectedPastHuntId(null)} className="text-xs text-gray-400 hover:text-white transition">Close ✕</button>
                    </div>
                    <PastHuntDetail hunt={selectedHunt} />
                  </div>
                )}
                </>)}
              </div>
              );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {!currentHunt ? (
                  <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] text-center text-gray-400 py-16">
                    <p className="text-xl mb-1">No active hunt right now</p><p className="text-sm">Check back when a hunt is live!</p>
                  </div>
                ) : (() => {
                  const opened = currentHunt.stats?.openedBonuses || 0;
                  const total = currentHunt.stats?.bonusCount || 0;
                  const remaining = currentHunt.stats?.unopenedBonuses || 0;
                  const progressPct = total > 0 ? Math.round((opened / total) * 100) : 0;
                  const totalWinnings = currentHunt.stats?.totalWinnings || 0;
                  const avgMultiplier = currentHunt.stats?.currentAverageMultiplier || 0;
                  const startCost = currentHunt.startCost || 0;
                  const avgBetSize = opened > 0 && currentHunt.bonuses
                    ? currentHunt.bonuses.filter(b => b.payout > 0).reduce((sum, b) => sum + (b.betSize || 0), 0) / Math.max(opened, 1)
                    : 0;
                  const projected = totalWinnings + (avgMultiplier * avgBetSize * remaining);
                  const projectedPL = projected - startCost;
                  const isAhead = totalWinnings >= (startCost * (opened / Math.max(total, 1)));

                  const hitBonuses = (currentHunt.bonuses || []).filter(b => b.payout > 0 && b.betSize > 0);
                  const biggestHit = hitBonuses.reduce((best, b) => {
                    const mult = b.payout / b.betSize;
                    return mult > (best?.mult || 0) ? { ...b, mult } : best;
                  }, null);
                  // Distinct from "biggest hit" above (which is the highest MULTIPLIER) —
                  // this is the single largest dollar payout, which isn't always the same bonus.
                  const topWin = hitBonuses.reduce((best, b) => (b.payout > (best?.payout || 0) ? b : best), null);
                  const runningAvgPerBonus = opened > 0 ? totalWinnings / opened : 0;
                  const breakEvenNeeded = remaining > 0 ? Math.max(0, startCost - totalWinnings) : 0;
                  const breakEvenMultNeeded = remaining > 0 && avgBetSize > 0
                    ? breakEvenNeeded / (avgBetSize * remaining)
                    : null;

                  return (
                    <>
                      <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/10 space-y-5">
                        <div className="flex items-center justify-between">
                          <h2 className="text-2xl font-bold">{currentHunt.title}</h2>
                          <span className="px-3 py-1 bg-red-500 rounded-full text-sm font-bold animate-pulse">🔴 LIVE</span>
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-400">{opened} of {total} opened</span>
                            <span className="font-bold text-indigo-400">{progressPct}%</span>
                          </div>
                          <div className="w-full bg-zinc-700 rounded-full h-3 overflow-hidden relative">
                            <div
                              className="h-3 rounded-full transition-all duration-500 relative overflow-hidden"
                              style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #4f39f6, #615fff)' }}
                            >
                              <div className="absolute inset-0 hunt-progress-shimmer" />
                            </div>
                          </div>
                          <div className="flex justify-between text-xs mt-1 text-gray-500">
                            <span>Start</span>
                            <span>{remaining} remaining</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-white/5 rounded-xl p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Start Cost</p>
                            <p className="text-lg font-bold text-green-400">${startCost.toLocaleString()}</p>
                          </div>
                          <div className="bg-white/5 rounded-xl p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Winnings So Far</p>
                            <p className={`text-lg font-bold ${isAhead ? 'text-green-400' : 'text-red-400'}`}>${totalWinnings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                          </div>
                          <div className="bg-white/5 rounded-xl p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Avg Multiplier</p>
                            <p className="text-lg font-bold text-yellow-400">{avgMultiplier.toFixed(1)}x</p>
                          </div>
                          {opened > 0 && remaining > 0 ? (
                            <div className="bg-white/5 rounded-xl p-3 text-center">
                              <p className="text-gray-400 text-xs mb-1">Projected Final</p>
                              <p className={`text-lg font-bold ${projectedPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                ${projected.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </p>
                              <p className={`text-xs ${projectedPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {projectedPL >= 0 ? '+' : ''}${projectedPL.toLocaleString(undefined, { maximumFractionDigits: 0 })} P/L
                              </p>
                            </div>
                          ) : (
                            <div className="bg-white/5 rounded-xl p-3 text-center">
                              <p className="text-gray-400 text-xs mb-1">Remaining</p>
                              <p className="text-lg font-bold text-blue-400">{remaining}</p>
                            </div>
                          )}
                        </div>

                        {(biggestHit || topWin || opened > 0 || breakEvenMultNeeded !== null) && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {topWin && (
                              <div className="flex items-center gap-3 bg-gradient-to-r from-yellow-500/10 to-transparent rounded-xl p-3 border border-yellow-500/20">
                                <span className="text-2xl">💰</span>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-400">Top win</p>
                                  <p className="text-sm font-bold text-yellow-400 truncate">${topWin.payout.toLocaleString()} — {topWin.slotName} ({(topWin.payout / topWin.betSize).toFixed(1)}x)</p>
                                </div>
                              </div>
                            )}
                            {biggestHit && (
                              <div className="flex items-center gap-3 bg-gradient-to-r from-purple-500/10 to-transparent rounded-xl p-3 border border-purple-500/20">
                                <span className="text-2xl">🏆</span>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-400">Highest multiplier</p>
                                  <p className="text-sm font-bold text-purple-300 truncate">{biggestHit.slotName} — {biggestHit.mult.toFixed(1)}x (${biggestHit.payout.toLocaleString()})</p>
                                </div>
                              </div>
                            )}
                            {opened > 0 && (
                              <div className="flex items-center gap-3 bg-white/[0.03] rounded-xl p-3 border border-zinc-600/40">
                                <span className="text-2xl">📊</span>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-400">Running average</p>
                                  <p className="text-sm font-bold text-gray-200">${runningAvgPerBonus.toLocaleString(undefined, { maximumFractionDigits: 0 })} per bonus opened</p>
                                </div>
                              </div>
                            )}
                            {breakEvenMultNeeded !== null && remaining > 0 && (
                              <div className="flex items-center gap-3 bg-white/[0.03] rounded-xl p-3 border border-zinc-600/40">
                                <span className="text-2xl">⚖️</span>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-400">Needed to break even</p>
                                  <p className="text-sm font-bold text-gray-200">
                                    {breakEvenMultNeeded <= 0 ? 'Already ahead 🎉' : `~${breakEvenMultNeeded.toFixed(1)}x avg on remaining ${remaining}`}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {currentHunt.bonuses?.length > 0 && (
                        <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/[0.06]">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold">Bonuses</h3>
                            <div className="flex gap-1 text-xs">
                              <button onClick={() => setBonusSort('order')} className={`px-3 py-1 rounded-lg font-semibold transition ${bonusSort === 'order' ? 'bg-indigo-600 text-white' : 'bg-zinc-700 text-gray-400 hover:bg-zinc-600'}`}>Order</button>
                              <button onClick={() => setBonusSort('payout')} className={`px-3 py-1 rounded-lg font-semibold transition ${bonusSort === 'payout' ? 'bg-indigo-600 text-white' : 'bg-zinc-700 text-gray-400 hover:bg-zinc-600'}`}>Highest Payout</button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                            {(bonusSort === 'payout'
                              ? [...currentHunt.bonuses].sort((a, b) => (b.payout || 0) - (a.payout || 0))
                              : currentHunt.bonuses
                            ).map((b, i) => {
                              const isBiggest = biggestHit && b.slotName === biggestHit.slotName && b.payout === biggestHit.payout;
                              return (
                                <div key={i} className={`flex justify-between items-center p-3 rounded-xl ${b.payout > 0 ? 'bg-green-900/30 border border-green-500/30' : 'bg-white/5'} ${isBiggest ? 'ring-1 ring-yellow-400/50' : ''}`}>
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <SlotImage name={b.slotName} size={44} />
                                    <div className="min-w-0">
                                      <p className="font-semibold text-sm truncate flex items-center gap-1">{isBiggest && <span title="Biggest hit">🏆</span>} {b.slotName}</p>
                                      {b.betSize && <p className="text-xs text-gray-400">{b.provider} · ${b.betSize} bet</p>}
                                    </div>
                                  </div>
                                  <div className="text-right ml-3 flex-shrink-0">
                                    {b.payout > 0 ? (
                                      <>
                                        <p className={`font-bold text-sm ${b.payout > b.betSize * 100 ? 'text-green-400' : 'text-gray-300'}`}>${b.payout?.toLocaleString()}</p>
                                        <p className="text-xs text-gray-500">{(b.payout / b.betSize).toFixed(1)}x</p>
                                      </>
                                    ) : (
                                      <p className="text-gray-500 text-sm">Pending</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="lg:col-span-1">
                <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/10 lg:sticky lg:top-24">
                  <h2 className="text-xl font-bold mb-2">🎯 Guess the Balance</h2>
                  {currentHunt && guessStatus?.entries?.length > 0 && (
                    <GuessTicker entries={guessStatus.entries} />
                  )}
                  {!currentHunt ? (
                    <p className="text-gray-400 text-sm py-6 text-center">Guessing opens when a hunt goes live!</p>
                  ) : !isValid ? (
                    <div className="text-center py-4">
                      <p className="text-gray-400 text-sm mb-4">Sign in with Discord + Twitch or Kick to guess the final balance.</p>
                      <button onClick={() => setShowAuthModal(true)} className="w-full py-3 bg-gradient-to-r from-[#4f39f6] to-[#372aac] hover:from-[#615fff] hover:to-[#432dd7] active:scale-[0.98] text-white font-bold rounded-xl transition">
                        Sign In to Guess
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-400 text-sm mb-4">Hunt: <span className="text-white font-semibold">{currentHunt.title}</span><br />Start: <span className="text-green-400 font-bold">${currentHunt.startCost?.toLocaleString()}</span></p>
                      <div className="mb-4 px-4 py-3 rounded-xl bg-white/5 border border-zinc-600 flex items-center gap-3">
                        <span className="text-gray-400 text-sm">Guessing as:</span>
                        <span className="font-bold text-white">{streamerName}</span>
                        <span className="text-xs text-gray-500 ml-auto">{accounts.twitch ? '🟣 Twitch' : '🟢 Kick'}</span>
                      </div>
                      {guessStatus?.isOpen === false ? (
                        <div className="bg-red-500/20 border border-red-500/40 rounded-xl p-4 text-center"><p className="text-red-400 font-bold text-lg">🔒 Guessing is closed</p></div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">
                              Your Guess ($) {userGuess && <span className="text-yellow-400">- Edit to update</span>}
                            </label>
                            <input type="number" value={guessAmount} onChange={e => setGuessAmount(e.target.value)} placeholder="e.g. 2500" className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
                          </div>
                          <button onClick={submitGuess} disabled={submitting || !guessAmount} className="w-full py-3 bg-gradient-to-r from-[#4f39f6] to-[#372aac] hover:from-[#615fff] hover:to-[#432dd7] active:scale-[0.98] text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed">
                            {submitting ? '⏳ Updating...' : userGuess ? '✏️ Update Guess' : '🎯 Submit Guess'}
                          </button>
                          {submitSuccess && (
                            <div className="fade-in flex items-center gap-2 px-4 py-3 rounded-xl bg-green-500/15 border border-green-500/30 text-green-400 font-semibold text-sm">
                              ✅ Guess updated!
                            </div>
                          )}
                          {guessHistory.length > 0 && (
                            <div className="bg-white/[0.03] rounded-xl p-4 border border-zinc-600">
                              <p className="text-xs text-gray-400 font-semibold mb-2">📝 Your Submission History</p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {guessHistory.map((g, i) => (
                                  <div key={i} className="text-sm flex justify-between items-center bg-zinc-800/50 rounded px-2 py-1">
                                    <span className="text-gray-300">${g.guessAmount?.toLocaleString()}</span>
                                    <span className="text-xs text-gray-500">{g.timestamp}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showAuthModal && (
        <AuthModal accounts={accounts} error={authError} setError={setAuthError} disconnect={disconnect}
          onClose={() => setShowAuthModal(false)} />
      )}
    </div>
    <ResponsibleGamblingFooter />
    </div>
  );
}

const money = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

// Counts up to the real figure on first paint. Skipped entirely for anyone who's asked
// their OS to reduce motion — they just get the final number immediately.
function useCountUp(target, duration = 1100) {
  const [value, setValue] = useState(target);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!target) { setValue(0); return; }
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || startedRef.current) { setValue(target); return; }
    startedRef.current = true;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// The headline trust signal: real money, already paid, to named people. Sits above the
// entry form so it's the first thing someone sees before deciding to enter. The total and
// the subtext both come straight from giveaway.js's "payouts" path, which already excludes
// Wager Rewards — this is specifically giveaway/portal money sent directly to people.
function TotalGivenAway({ payouts, onOpenList }) {
  const shown = useCountUp(payouts?.totalPaid || 0);

  // The scrolling strip shows each person's TOTAL received (not individual payouts), in a
  // random order picked once per visit. Held in a ref so the 30s refresh doesn't reshuffle
  // and restart the animation mid-scroll.
  const stripRef = useRef(null);
  if (!stripRef.current && payouts?.leaderboard?.length) {
    const pool = payouts.leaderboard.map(u => ({ username: u.username, total: u.total }));
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    stripRef.current = pool.slice(0, 12);
  }
  const strip = stripRef.current || [];

  if (!payouts || !payouts.payoutCount) return null;

  return (
    <div className="rounded-2xl border border-green-500/30 bg-gradient-to-br from-green-500/10 via-zinc-800/60 to-zinc-800/60 overflow-hidden">
      <button
        type="button"
        onClick={onOpenList}
        className="w-full p-6 text-center block cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 rounded-2xl"
        aria-label="Tipped to the chat — see the full list"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-green-400/80 mb-1">Tipped to the chat</p>
        <p className="text-5xl md:text-6xl font-black tabular-nums text-green-400 leading-none">
          {money(shown)}
        </p>
        <p className="text-xs text-gray-500 mt-2">
          This doesn't include flopped bonus buys or wager rewards — just the money sent directly to people.
        </p>
      </button>

      {strip.length > 1 && (
        <div className="border-t border-green-500/20 bg-zinc-900/40 py-2 overflow-hidden relative">
          <div
            className="marquee-track flex gap-3 w-max"
            style={{ '--marquee-duration': `${Math.max(20, strip.length * 3)}s` }}
          >
            {[...strip, ...strip].map((r, i) => (
              <span key={i} className="flex items-center gap-2 text-sm whitespace-nowrap px-3 py-1 rounded-full bg-zinc-800/70 border border-zinc-700/60">
                <span className="text-gray-300">{r.username}</span>
                <span className="text-green-400 font-bold tabular-nums">{money(r.total)}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PayoutLeaderboardModal({ payouts, onClose }) {
  const [query, setQuery] = useState('');
  const rows = (payouts?.leaderboard || []).filter(r => r.username.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-white/10 rounded-b-2xl md:rounded-2xl w-full max-w-lg h-[52vh] md:h-auto md:max-h-[85vh] flex flex-col fade-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 border-b border-zinc-800 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold">Everyone who's been paid</h3>
            <p className="text-sm text-gray-400">{money(payouts?.totalPaid || 0)} total · {payouts?.winnerCount || 0} people</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="p-4 border-b border-zinc-800">
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Find a name"
            className="w-full bg-zinc-800 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="overflow-y-auto flex-1">
          {rows.length === 0 ? (
            <p className="text-center text-gray-500 py-10 text-sm">No one by that name yet.</p>
          ) : rows.map((r, i) => (
            <div key={r.username} className="flex items-center gap-3 px-5 py-2.5 border-b border-zinc-800/60">
              <span className="text-xs text-gray-500 tabular-nums w-7 shrink-0">{query ? '' : `#${i + 1}`}</span>
              <span className="flex-1 truncate">{r.username}</span>
              {r.count > 1 && <span className="text-xs text-gray-500 shrink-0">×{r.count}</span>}
              <span className="text-green-400 font-bold tabular-nums shrink-0">{money(r.total)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const RAINBET_PROMPT_DISMISSED_KEY = 'dankquan_dismissed_rainbet_prompt';

// A nudge to link Rainbet, shown on both the Rewards and Giveaway pages for anyone
// signed in with Discord who hasn't added one yet. "Never shows again" once dismissed —
// tracked in localStorage, same pattern as the rest of the site's local preferences.
function RainbetPrompt({ accounts, rainbetUsername, onSaveRainbet }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(RAINBET_PROMPT_DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dismiss = () => {
    try { localStorage.setItem(RAINBET_PROMPT_DISMISSED_KEY, '1'); } catch {}
    setDismissed(true);
  };

  const save = async () => {
    if (!input.trim()) return;
    setSaving(true);
    setError(null);
    const data = await onSaveRainbet(input.trim());
    if (!data.success) setError(data.message || 'Could not save right now.');
    setSaving(false);
  };

  if (dismissed || !accounts.discord || rainbetUsername === null || rainbetUsername) return null;

  return (
    <div className="fade-in flex items-start gap-3 bg-indigo-500/10 border border-white/[0.07] rounded-xl px-4 py-3 mb-4">
      <span className="text-xl shrink-0">🔗</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-indigo-200">Add your Rainbet username</p>
        <p className="text-xs text-gray-400 mt-0.5 mb-2">Needed to track your wager and claim rewards or giveaway wins. One-time — only Dankquan can change it after.</p>
        <div className="flex flex-wrap gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && save()}
            placeholder="Your Rainbet username"
            className="flex-1 min-w-[160px] bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button onClick={save} disabled={saving || !input.trim()} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] rounded-lg text-sm font-bold transition disabled:opacity-50">
            {saving ? '...' : 'Link'}
          </button>
        </div>
        {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
      </div>
      <button onClick={dismiss} className="text-gray-500 hover:text-gray-300 shrink-0 p-1" aria-label="Dismiss — don't show this again">
        <X size={16} />
      </button>
    </div>
  );
}

function RewardsPage({ accounts, rainbetUsername, onSaveRainbet }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [message, setMessage] = useState(null);

  const sessionToken = accounts.discord?.sessionToken;

  const load = useCallback(async (opts = {}) => {
    try {
      const meRes = await fetch('/.netlify/functions/wager?path=me', { headers: { 'X-Session-Token': sessionToken || '' } });
      setMe(await meRes.json());
    } catch (e) { console.error(e); }
    if (!opts.silent) setLoading(false);
  }, [sessionToken]);

  useEffect(() => { load(); }, [load]);

  const claim = async (amount) => {
    setClaiming(true);
    setMessage(null);
    try {
      const res = await fetch('/.netlify/functions/wager', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({ amount }),
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'success' : 'error', text: data.message || 'Something went wrong.' });
      if (data.success) load({ silent: true });
    } catch {
      setMessage({ type: 'error', text: 'Something went wrong. Try again.' });
    }
    setClaiming(false);
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="max-w-3xl mx-auto p-6 md:p-10 flex-1 w-full">
        <h1 className="text-4xl font-black mb-2 text-center">💎 Wager Rewards</h1>
        <p className="text-center text-sm text-gray-400 mb-6">100% of commission, given back — tracked continuously, never resets.</p>

        <RainbetPrompt accounts={accounts} rainbetUsername={rainbetUsername} onSaveRainbet={onSaveRainbet} />

        {message && (
          <div className={`text-sm px-4 py-3 rounded-xl mb-4 ${message.type === 'success' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="text-center text-xl py-20">Loading...</div>
        ) : (
          <WagerRewardsTab me={me} onClaim={claim} claiming={claiming} />
        )}
      </div>
    </div>
  );
}

function WagerRewardsTab({ me, onClaim, claiming }) {
  const linked = !!me?.linked;
  const tracked = !!me?.tracked;
  const canClaim = linked && tracked;

  const wager = canClaim ? (me.wager || 0) : 0;
  const owed = canClaim ? (me.owed || 0) : 0;
  const paid = canClaim ? (me.paid || 0) : 0;
  const pending = canClaim ? (me.pending || 0) : 0;
  const available = canClaim ? (me.available || 0) : 0;

  return (
    <div className="space-y-4">
      <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 text-center">
        <p className="text-3xl mb-1">💯</p>
        <p className="text-lg font-bold">Currently giving back 100% of your commission</p>
      </div>

      {!linked && (
        <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 text-center space-y-1.5">
          <p className="text-3xl">🔗</p>
          <p className="text-lg font-bold">
            {me?.reason === 'signin' ? 'Sign in with Discord to track your own progress' : 'Link your Rainbet username to start earning'}
          </p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            {me?.reason === 'signin'
              ? "Sign in to see your real wager and claim what you've earned."
              : 'Open the account menu at the top right and add the Rainbet username you play on. It can only be linked once, and only to one Discord account.'}
          </p>
        </div>
      )}

      {linked && !tracked && (
        <p className="text-sm text-gray-400 bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-xl px-4 py-3 border border-zinc-700/60 text-center">
          No wager on file yet for <span className="text-gray-200">{me.rainbetUsername}</span>. Totals get updated manually — check back after the next update.
        </p>
      )}

      <div className="bg-gradient-to-br from-indigo-500/15 to-zinc-800/60 rounded-2xl border border-white/10 p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-300/80 mb-1">Total wagered</p>
        <p className="text-4xl md:text-5xl font-black tabular-nums">${wager.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        {canClaim && (
          <p className="text-sm text-gray-400 mt-1">as <span className="text-gray-200">{me.rainbetUsername}</span> · lifetime, never resets</p>
        )}

        {canClaim && (
          <div className="grid grid-cols-3 gap-4 mt-5 pt-4 border-t border-zinc-700/60 text-sm">
            <div>
              <p className="text-gray-500 text-xs">Total earned</p>
              <p className="font-bold text-cyan-400 tabular-nums">${owed.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Paid{pending > 0 ? ` (+ $${pending.toFixed(2)} pending)` : ''}</p>
              <p className="font-bold tabular-nums">${paid.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Available now</p>
              <p className="font-bold text-green-400 tabular-nums">${available.toFixed(2)}</p>
            </div>
          </div>
        )}
      </div>

      {canClaim && (
        <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 text-center">
          {available > 0 ? (
            <>
              <p className="text-sm text-gray-400 mb-3">You've got <span className="text-green-400 font-bold">${available.toFixed(2)}</span> ready to claim.</p>
              <button
                onClick={() => onClaim(available)}
                disabled={!!claiming}
                className="px-6 py-3 bg-green-600 hover:bg-green-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50"
              >
                {claiming ? 'Sending claim...' : `Claim $${available.toFixed(2)}`}
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500">Nothing available to claim right now — keep wagering and it'll build up here.</p>
          )}
        </div>
      )}
    </div>
  );
}

// Shows what the winner's actually said in chat since winning. Twitch only.
// ---------------------------------------------------------------------------------------
// Live giveaway (Nightbot-style). One set of components drives both the public Giveaway
// page (viewers watch entrants + chat + the winner pop up) and the admin page (same view
// plus the controls), so what Dankquan shares on screen is exactly what viewers see.
// ---------------------------------------------------------------------------------------

// The winner card's clock counts up from 0:00 and turns red once the winner has been quiet this long.
const WINNER_TIMER_SECONDS = 60;

const clockText = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// "12s", "3m 20s", "1h 5m"
const durText = (secs) => {
  const t = Math.floor(secs);
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${t % 60}s`;
  return `${Math.floor(t / 3600)}h ${Math.floor((t % 3600) / 60)}m`;
};

const ageText = (days) => {
  if (days == null) return null;
  if (days < 1) return 'under a day';
  if (days < 60) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 730) return `${Math.floor(days / 30)} months`;
  return `${Math.floor(days / 365)} years`;
};

// Calls fn every `ms` while the tab is visible, and once more the moment it becomes visible
// again. Doesn't call on mount — callers do their own first load.
function usePolling(fn, ms) {
  const ref = useRef(fn);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => {
    let timer = null;
    const tick = () => ref.current();
    const start = () => { if (!timer) timer = setInterval(tick, ms); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVisibility = () => { if (document.hidden) stop(); else { tick(); start(); } };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [ms]);
}

// What the public page polls: the running giveaway, whether entries are open, how people
// enter, and the current winner (with the messages they've sent since being picked).
function useGiveawayMeta(enabled) {
  const [meta, setMeta] = useState(undefined); // undefined = still loading, null = nothing running
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await fetch('/.netlify/functions/giveaway?path=current');
      const d = await res.json();
      if (d && 'giveaway' in d) {
        const g = d.giveaway;
        setMeta(g ? { giveaway: g, isOpen: !!g.isOpen, entryMode: g.entryMode, keyword: g.keyword, activeWindow: g.activeWindow || 0, entryCount: g.entryCount || 0 } : null);
      }
    } catch (e) { console.error(e); }
  }, [enabled]);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 4000);
  return meta;
}

function EntrantAvatar({ entry, size = 36 }) {
  const [failed, setFailed] = useState(false);
  const initial = (entry.username || '?').charAt(0).toUpperCase();
  if (entry.avatar && !failed) {
    return <img src={entry.avatar} alt="" onError={() => setFailed(true)} className="rounded-full object-cover shrink-0 bg-zinc-700" style={{ width: size, height: size }} />;
  }
  return (
    <span className="rounded-full shrink-0 bg-zinc-700 text-gray-300 font-bold flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {initial}
    </span>
  );
}

// ---------------------------------------------------------------------------------------
// "Fast chat" — a read-only chat that connects straight from the viewer's browser to Twitch's
// chat servers (anonymous, read-only), so there's no round trip through our own site: delay is
// about what Twitch itself has, and a spam flood can't overload anything of ours. Twitch emotes
// come with each message; 7TV emotes (global + the channel's own set) are loaded in the browser.
// ---------------------------------------------------------------------------------------

const FAST_CHAT_MAX_MESSAGES = 150;

function parseIrcLine(line) {
  let rest = line;
  const tags = {};
  if (rest.startsWith('@')) {
    const sp = rest.indexOf(' ');
    rest.slice(1, sp).split(';').forEach(kv => {
      const i = kv.indexOf('=');
      tags[i < 0 ? kv : kv.slice(0, i)] = i < 0 ? '' : kv.slice(i + 1);
    });
    rest = rest.slice(sp + 1);
  }
  let prefix = '';
  if (rest.startsWith(':')) {
    const sp = rest.indexOf(' ');
    prefix = rest.slice(1, sp);
    rest = rest.slice(sp + 1);
  }
  let head = rest;
  let trailing = '';
  const t = rest.indexOf(' :');
  if (t !== -1) { head = rest.slice(0, t); trailing = rest.slice(t + 2); }
  const [command, ...params] = head.split(' ');
  return { tags, prefix, command, params, trailing };
}

const unescapeIrcTag = (v) => (v || '').replace(/\\s/g, ' ').replace(/\\:/g, ';').replace(/\\\\/g, '\\');

// Twitch's default name colors can be nearly invisible on a dark background — lift dark ones.
function readableColor(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#a3b3ff';
  const n = parseInt(m[1], 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (lum < 0.4) { const k = 0.45; r = Math.round(r + (255 - r) * k); g = Math.round(g + (255 - g) * k); b = Math.round(b + (255 - b) * k); }
  return `rgb(${r},${g},${b})`;
}

// Splits a message into text and emote pieces: Twitch's own emotes by the position ranges
// Twitch sends, then any word that matches a 7TV emote name.
function buildChatParts(text, emoteTag, seventv) {
  const chars = Array.from(text);
  const ranges = [];
  if (emoteTag) {
    emoteTag.split('/').forEach(e => {
      const [id, pos] = e.split(':');
      (pos || '').split(',').forEach(r => {
        const [a, b] = r.split('-').map(Number);
        if (id && Number.isFinite(a) && Number.isFinite(b)) ranges.push({ id, a, b });
      });
    });
    ranges.sort((x, y) => x.a - y.a);
  }
  const parts = [];
  const pushText = (str) => {
    str.split(/(\s+)/).forEach(tok => {
      if (!tok) return;
      const src = seventv[tok];
      if (src) parts.push({ type: 'emote', src, alt: tok });
      else parts.push({ type: 'text', value: tok });
    });
  };
  let cursor = 0;
  for (const r of ranges) {
    if (r.a < cursor) continue;
    if (r.a > cursor) pushText(chars.slice(cursor, r.a).join(''));
    parts.push({ type: 'emote', src: `https://static-cdn.jtvnw.net/emoticons/v2/${r.id}/default/dark/2.0`, alt: chars.slice(r.a, r.b + 1).join('') });
    cursor = r.b + 1;
  }
  if (cursor < chars.length) pushText(chars.slice(cursor).join(''));
  return parts;
}

// 7TV "paints" (the gradient / animated name styles). Looked up lazily, once per chatter, a few
// at a time, and cached — so a flood of new chatters can't hammer 7TV. Anyone without a paint
// (or if 7TV is unreachable) just keeps their normal Twitch name colour.
function sevenTvColor(n) {
  const c = (n >>> 0);
  return `rgba(${(c >>> 24) & 255},${(c >>> 16) & 255},${(c >>> 8) & 255},${((c & 255) / 255).toFixed(3)})`;
}

function paintStyle(p) {
  if (!p) return null;
  const stops = (p.stops || []).map(st => `${sevenTvColor(st.color)} ${(st.at * 100).toFixed(1)}%`).join(', ');
  let image = null;
  if (p.function === 'LINEAR_GRADIENT' && stops) image = `${p.repeat ? 'repeating-' : ''}linear-gradient(${p.angle || 0}deg, ${stops})`;
  else if (p.function === 'RADIAL_GRADIENT' && stops) image = `${p.repeat ? 'repeating-' : ''}radial-gradient(circle, ${stops})`;
  else if (p.function === 'URL' && p.image_url) image = `url(${p.image_url})`;
  const shadow = (p.shadows || []).map(sh => `drop-shadow(${sh.x_offset}px ${sh.y_offset}px ${sh.radius}px ${sevenTvColor(sh.color)})`).join(' ');
  if (!image) {
    if (!p.color) return null;
    return { color: sevenTvColor(p.color), filter: shadow || undefined };
  }
  return {
    backgroundImage: image,
    backgroundSize: p.function === 'URL' ? 'cover' : undefined,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    filter: shadow || undefined,
  };
}

function createPaintLoader(onChange) {
  const userPaint = new Map(); // twitch user id -> paint id or null
  const seen = new Set();
  const queue = [];
  let paints = {};
  let active = 0;
  let paintsPromise = null;

  const loadPaints = () => {
    if (!paintsPromise) {
      paintsPromise = fetch('https://7tv.io/v3/gql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: '{ cosmetics(list: []) { paints { id function color angle repeat image_url stops { at color } shadows { x_offset y_offset radius color } } } }' }),
      }).then(r => r.json()).then(d => {
        (d?.data?.cosmetics?.paints || []).forEach(p => { paints[p.id] = p; });
      }).catch(() => { /* no paints — plain colours */ });
    }
    return paintsPromise;
  };

  // One lookup at a time per worker; each finished lookup starts the next queued one.
  const lookup = (uid) => loadPaints()
    .then(() => fetch(`https://7tv.io/v3/users/twitch/${uid}`))
    .then(r => (r.ok ? r.json() : null))
    .then(d => { userPaint.set(uid, d?.user?.style?.paint_id || null); })
    .catch(() => { userPaint.set(uid, null); });

  function pump() {
    if (active >= 4 || queue.length === 0) return;
    const uid = queue.shift();
    active += 1;
    lookup(uid).finally(() => {
      active -= 1;
      onChange();
      pump();
    });
    pump();
  }

  return {
    request(uid) {
      if (!uid || seen.has(uid) || queue.length > 150) return;
      seen.add(uid);
      queue.push(uid);
      pump();
    },
    paintFor(uid) {
      const id = userPaint.get(uid);
      return id ? paints[id] || null : null;
    },
  };
}

// Small badge icons: broadcaster, moderator and subscriber only.
function ChatBadge({ type }) {
  const common = { width: 18, height: 18, viewBox: '0 0 18 18', className: 'inline-block align-middle mr-1', style: { marginTop: -2 } };
  if (type === 'broadcaster') {
    return (
      <svg {...common} aria-label="Broadcaster"><title>Broadcaster</title>
        <rect width="18" height="18" rx="3" fill="#e91916" />
        <circle cx="9" cy="9" r="2" fill="#fff" />
        <path d="M5.2 5.2a5.4 5.4 0 0 0 0 7.6M12.8 5.2a5.4 5.4 0 0 1 0 7.6" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'moderator') {
    return (
      <svg {...common} aria-label="Moderator"><title>Moderator</title>
        <rect width="18" height="18" rx="3" fill="#00ad03" />
        <path d="M5 13 L12.5 5.5 M10 5 h3 v3 M5 13 l1.5-0.5 M4.5 11.5 l2 2" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg {...common} aria-label="Subscriber"><title>Subscriber</title>
      <rect width="18" height="18" rx="3" fill="#8205b4" />
      <path d="M9 3.6l1.6 3.4 3.7.5-2.7 2.6.7 3.7L9 12l-3.3 1.8.7-3.7L3.7 7.5l3.7-.5z" fill="#fff" />
    </svg>
  );
}

const FastChatRow = React.memo(function FastChatRow({ m, seventv, paint }) {
  const parts = React.useMemo(() => buildChatParts(m.text, m.emotes, seventv), [m.text, m.emotes, seventv]);
  const pStyle = React.useMemo(() => paintStyle(paint), [paint]);
  const nameStyle = pStyle || { color: readableColor(m.color) };
  return (
    <div className="px-3 py-1 text-[14px] leading-6 break-words">
      {m.badges.map(b => <ChatBadge key={b} type={b} />)}
      <span className="font-bold" style={nameStyle}>{m.user}</span>
      <span className="text-gray-400">: </span>
      <span className={m.action ? 'italic' : ''} style={m.action ? { color: readableColor(m.color) } : undefined}>
        {parts.map((p, i) => p.type === 'emote'
          ? <img key={i} src={p.src} alt={p.alt} title={p.alt} loading="lazy" className="inline-block align-middle mx-0.5" style={{ height: 28 }} />
          : <span key={i}>{p.value}</span>)}
      </span>
    </div>
  );
});

function FastChat({ channel }) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('connecting'); // connecting | live | reconnecting
  const [roomId, setRoomId] = useState(null);
  const [seventv, setSeventv] = useState({});
  const [atBottom, setAtBottom] = useState(true);
  const bufferRef = useRef([]);
  const dirtyRef = useRef(false);
  const listRef = useRef(null);
  const stickRef = useRef(true);
  const idRef = useRef(0);
  const [, setPaintTick] = useState(0);
  const loaderRef = useRef(null);
  if (!loaderRef.current) loaderRef.current = createPaintLoader(() => setPaintTick(v => v + 1));

  // Connect straight to Twitch chat (anonymous "justinfan" login — read-only) and reconnect if dropped.
  useEffect(() => {
    const room = (channel || 'dankquan').toLowerCase();
    let ws = null;
    let stopped = false;
    let retry = 0;
    let timer = null;
    bufferRef.current = [];
    dirtyRef.current = true;

    const connect = () => {
      setStatus(retry === 0 ? 'connecting' : 'reconnecting');
      ws = new WebSocket('wss://irc-ws.chat.twitch.tv:443');
      ws.onopen = () => {
        ws.send('CAP REQ :twitch.tv/tags twitch.tv/commands');
        ws.send('PASS SCHMOOPIIE');
        ws.send(`NICK justinfan${10000 + Math.floor(Math.random() * 80000)}`);
        ws.send(`JOIN #${room}`);
      };
      ws.onmessage = (ev) => {
        String(ev.data).split('\r\n').forEach(line => {
          if (!line) return;
          if (line.startsWith('PING')) { ws.send('PONG :tmi.twitch.tv'); return; }
          const msg = parseIrcLine(line);
          if (msg.command === 'ROOMSTATE') {
            if (msg.tags['room-id']) setRoomId(msg.tags['room-id']);
            retry = 0;
            setStatus('live');
          } else if (msg.command === 'PRIVMSG') {
            let text = msg.trailing;
            let action = false;
            const SOH = String.fromCharCode(1);
            if (text.startsWith(`${SOH}ACTION `)) {
              action = true;
              text = text.slice(8);
              if (text.endsWith(SOH)) text = text.slice(0, -1);
            }
            const nick = (msg.prefix.split('!')[0] || '').trim();
            idRef.current += 1;
            bufferRef.current.push({
              id: idRef.current,
              user: unescapeIrcTag(msg.tags['display-name']) || nick,
              color: msg.tags.color || '',
              userId: msg.tags['user-id'] || '',
              badges: ['broadcaster', 'moderator', 'subscriber'].filter(b => (`,${msg.tags.badges || ''}`).includes(`,${b}/`) || (b === 'subscriber' && (`,${msg.tags.badges || ''}`).includes(',founder/'))),
              emotes: msg.tags.emotes || '',
              text,
              action,
            });
            if (bufferRef.current.length > FAST_CHAT_MAX_MESSAGES) bufferRef.current = bufferRef.current.slice(-FAST_CHAT_MAX_MESSAGES);
            dirtyRef.current = true;
          }
        });
      };
      ws.onclose = () => {
        if (stopped) return;
        setStatus('reconnecting');
        retry += 1;
        timer = setTimeout(connect, Math.min(1000 * 2 ** retry, 15000));
      };
      ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      try { if (ws) ws.close(); } catch { /* already closed */ }
    };
  }, [channel]);

  // Messages land in a buffer as fast as Twitch sends them; the screen only updates a few times
  // a second, so even a flood of messages is cheap to draw.
  useEffect(() => {
    const t = setInterval(() => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      const snap = bufferRef.current.slice();
      snap.forEach(m => loaderRef.current.request(m.userId));
      setMessages(snap);
    }, 150);
    return () => clearInterval(t);
  }, []);

  // 7TV emotes: the global set plus this channel's own set (looked up by its Twitch ID).
  useEffect(() => {
    let cancelled = false;
    const add = (emotes, into) => (emotes || []).forEach(e => {
      if (e && e.id && e.name) into[e.name] = `https://cdn.7tv.app/emote/${e.id}/2x.webp`;
    });
    (async () => {
      const map = {};
      try { const g = await (await fetch('https://7tv.io/v3/emote-sets/global')).json(); add(g.emotes, map); } catch { /* 7TV unreachable — plain text */ }
      if (roomId) {
        try { const c = await (await fetch(`https://7tv.io/v3/users/twitch/${roomId}`)).json(); add(c && c.emote_set && c.emote_set.emotes, map); } catch { /* no channel set */ }
      }
      if (!cancelled) setSeventv(map);
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  useEffect(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    stickRef.current = near;
    setAtBottom(near);
  };
  const jumpToLatest = () => {
    const el = listRef.current;
    if (!el) return;
    stickRef.current = true;
    setAtBottom(true);
    el.scrollTop = el.scrollHeight;
  };

  return (
    <div className="relative flex flex-col flex-1 min-h-0 h-full bg-zinc-950/60">
      <div className="px-3 py-1.5 text-[11px] flex items-center justify-between border-b border-white/5">
        <span className={status === 'live' ? 'text-green-400' : 'text-yellow-400'}>
          ● {status === 'live' ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
        </span>
        <span className="text-gray-500">Read-only</span>
      </div>
      <div ref={listRef} onScroll={onScroll} className="flex-1 min-h-0 overflow-y-auto py-2">
        {messages.length === 0
          ? <p className="text-center text-gray-500 text-sm py-12">Waiting for chat…</p>
          : messages.map(m => <FastChatRow key={m.id} m={m} seventv={seventv} paint={loaderRef.current.paintFor(m.userId)} />)}
      </div>
      {!atBottom && (
        <button onClick={jumpToLatest} className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-500 text-xs font-bold shadow-lg transition">↓ Jump to latest</button>
      )}
    </div>
  );
}

// The searchable, paged entrants list (100 per page). Search runs on the server across
// everyone, so a viewer can jump straight to their own name without paging through.
function LiveEntrantsPanel({ refreshKey, onChannel, onRemove, modeText, footerNote, tall }) {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(query.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/.netlify/functions/giveaway?path=entrants&page=${page}&q=${encodeURIComponent(search)}`);
      const d = await res.json();
      if (Array.isArray(d?.entries)) {
        setData(d);
        if (d.channel && onChannel) onChannel(d.channel);
      }
    } catch (e) { console.error(e); }
  }, [page, search, onChannel]);

  useEffect(() => { load(); }, [load, refreshKey]);
  usePolling(load, 10000);

  const entries = data?.entries || [];
  const shownPage = data?.page || 1;
  const pages = data?.pages || 1;
  const pageSize = data?.pageSize || 100;
  const filteredTotal = data?.filteredTotal || 0;
  const from = filteredTotal === 0 ? 0 : (shownPage - 1) * pageSize + 1;
  const to = Math.min(shownPage * pageSize, filteredTotal);

  return (
    <div className="bg-zinc-900/70 border border-white/10 rounded-2xl flex flex-col overflow-hidden min-w-0">
      <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap border-b border-white/10">
        <h3 className="font-bold text-lg">Entrants</h3>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/10 sm:order-3">{(data?.total || 0).toLocaleString()} entered{data?.activeCount != null ? ` · ${data.activeCount.toLocaleString()} active` : ''}</span>
        <p className="text-xs text-gray-400 w-full sm:w-auto sm:flex-1 sm:text-center sm:order-2">{modeText}</p>
      </div>

      <div className="px-4 pt-3 pb-3">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search participants…"
          className="w-full bg-zinc-800 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className={`${tall ? 'h-[520px] md:h-[640px]' : 'h-[420px] md:h-[480px]'} overflow-y-auto border-t border-white/5`}>
        {!data ? (
          <p className="text-center text-gray-500 py-12 text-sm">Loading entrants…</p>
        ) : entries.length === 0 ? (
          <p className="text-center text-gray-500 py-12 text-sm">{search ? 'No one by that name.' : 'No one has entered yet.'}</p>
        ) : entries.map(e => {
          const st = e.inGuild && e.isVerified ? 'ok' : (e.inGuild || e.isVerified) ? 'partial' : 'none';
          return (
            <div key={`${e.provider}:${e.username}`} className={`group flex items-center gap-3 px-4 py-2.5 border-b border-white/5 hover:bg-white/[0.03] ${e.active === false ? 'opacity-40' : ''}`} title={e.active === false ? "Hasn't chatted recently — can't be drawn until they type again" : undefined}>
              <EntrantAvatar entry={e} />
              <span className="flex-1 min-w-0 truncate text-[15px] font-medium">
                {e.username}
                {(e.inGuild || e.isVerified) && <span className="ml-1.5" title="In the Dankquan Discord">🦄</span>}
                {e.active === false && <span className="ml-1.5" title="Hasn't chatted recently">💤</span>}
              </span>
              {e.provider === 'kick' && <ProviderIcon provider="kick" size={14} />}
              {onRemove && (
                <button onClick={() => onRemove(e)} className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-500 hover:text-red-400 p-1 transition" aria-label={`Remove ${e.username}`} title="Remove from the pool">
                  <X size={14} />
                </button>
              )}
              <span
                title={st === 'ok' ? 'In the Discord and verified' : st === 'partial' ? (e.inGuild ? 'In the Discord, but not verified yet' : 'Verified, but not showing as in the Discord') : (e.linked ? 'Not in the Discord yet' : 'Not linked to Discord on the site yet')}
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-black shrink-0 ${st === 'ok' ? 'bg-green-500 text-black' : st === 'partial' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50' : 'bg-zinc-700 text-zinc-400'}`}
              >{st === 'ok' ? '✓' : st === 'partial' ? '!' : '✕'}</span>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap text-xs">
        <span className="text-gray-400">{filteredTotal === 0 ? '0' : `${from}-${to}`} of {filteredTotal.toLocaleString()}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={shownPage <= 1} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 font-semibold transition disabled:opacity-40">Previous</button>
          <span className="text-gray-400 tabular-nums">{shownPage} / {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={shownPage >= pages} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 font-semibold transition disabled:opacity-40">Next</button>
        </div>
        {footerNote && <span className="text-gray-300 w-full sm:w-auto text-right">{footerNote}</span>}
      </div>
    </div>
  );
}

// The winner pop-up: who they are, whether they're in the Discord / verified, how old their
// accounts are, a countdown for them to say something, and everything they've typed in chat
// since being picked. Viewers get the same card; the admin gets the action buttons too.
function WinnerModal({ winner, title, messages, onClose, admin }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const info = winner.accountInfo || {};
  const elapsed = winner.wonAt ? (now - new Date(winner.wonAt).getTime()) / 1000 : 0;
  const overTime = elapsed >= WINNER_TIMER_SECONDS;
  const replied = messages.length > 0;
  const chronological = [...messages].reverse();
  const wonMs = winner.wonAt ? new Date(winner.wonAt).getTime() : null;
  const lastBeforeSec = wonMs && winner.lastChatAt ? Math.max(0, (wonMs - new Date(winner.lastChatAt).getTime()) / 1000) : null;
  const inDiscord = winner.inGuild || winner.isVerified;
  const ages = [
    info.twitchDays != null ? `Twitch account ${ageText(info.twitchDays)} old` : null,
    info.discordDays != null ? `Discord account ${ageText(info.discordDays)} old` : null,
  ].filter(Boolean).join(' · ');

  // Follow time is looked up live for the admin (so it also works for winners picked before the
  // follower permission was approved); viewers never see the winner card any more.
  const fetchFollow = admin?.fetchFollow;
  const followLogin = winner.provider === 'twitch' ? (winner.login || winner.username || '').toLowerCase() : '';
  const [follow, setFollow] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setFollow(null);
    if (!fetchFollow || !followLogin) return undefined;
    fetchFollow(followLogin).then(d => { if (!cancelled) setFollow(d || { status: 'error' }); }).catch(() => { if (!cancelled) setFollow({ status: 'error' }); });
    return () => { cancelled = true; };
  }, [fetchFollow, followLogin]);
  const followFallback = info.followingDays != null ? { status: 'following', days: info.followingDays } : info.notFollowing ? { status: 'not-following' } : null;
  const followShown = follow || followFallback;
  const stats = winner.winStats || null;
  const avatar = winner.avatar || info.avatarUrl || null;

  const modal = (
    <div onClick={e => e.target === e.currentTarget && onClose()} className="fixed inset-0 z-[99999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 md:p-6">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col fade-in shadow-2xl shadow-black/60">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
          <h3 className="text-lg font-bold">Winner{title ? <span className="text-gray-500 font-normal text-sm"> · {title}</span> : null}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" aria-label="Close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-center gap-4 bg-zinc-800/60 border border-white/10 rounded-2xl p-4">
            <EntrantAvatar entry={{ username: winner.username, avatar }} size={64} />
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-black truncate">
                {winner.username}
                {inDiscord && <span className="ml-2" title="In the Dankquan Discord">🦄</span>}
                {winner.isVIP && <span className="ml-1" title="VIP">👑</span>}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${winner.linked ? (winner.inGuild ? 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30' : 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30') : 'bg-red-500/10 text-red-300 border-red-500/30'}`}>
                  {winner.linked ? (winner.inGuild ? '🦄 In the Discord' : 'Not in the Discord') : 'No Discord linked on the site'}
                </span>
                {winner.isVerified && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-green-500/10 text-green-300 border-green-500/30">✅ Verified</span>}
                {winner.linked && winner.inGuild && !winner.isVerified && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-yellow-500/10 text-yellow-300 border-yellow-500/30">Not verified</span>}
                {winner.referralVerified && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white/5 text-gray-300 border-white/10">🎟️ Under code DANKQUAN</span>}
              </div>
              {ages && <p className="text-xs text-gray-400 mt-1.5">{ages}</p>}
              {followLogin && (
                <p className="text-xs mt-1">
                  {!followShown ? <span className="text-gray-500">Checking follow…</span>
                    : followShown.status === 'following' ? <span className="text-pink-300 font-semibold">💜 Following for {ageText(followShown.days)}</span>
                    : followShown.status === 'not-following' ? <span className="text-red-300 font-semibold">💔 Not following</span>
                    : followShown.status === 'no-access' ? <span className="text-yellow-300">Follow time needs one more Twitch permission — <a href={getTwitchChatAuthUrl()} className="underline hover:text-yellow-200">reconnect Twitch</a></span>
                    : followShown.status === 'broadcaster' ? null
                    : <span className="text-gray-500">Couldn't check follow time right now</span>}
                </p>
              )}
              {admin && winner.announced === false && (
                <div className="text-xs mt-1 text-yellow-300">
                  <p>⚠️ Couldn't post the winner in Twitch chat{winner.announceError ? <span className="text-yellow-200/80"> ({winner.announceError})</span> : null}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {admin.onAnnounce && <button onClick={admin.onAnnounce} disabled={admin.busy} className="px-2.5 py-1 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 font-semibold transition disabled:opacity-50">📣 Post again</button>}
                    <a href={getTwitchChatAuthUrl()} className="underline hover:text-yellow-200">Reconnect Twitch</a>
                  </div>
                </div>
              )}
              {admin && winner.announced === true && <p className="text-xs mt-1 text-green-400">✓ Announced in Twitch chat</p>}
              {admin && stats && (
                <p className="text-xs mt-1 text-gray-300">
                  {stats.count > 0
                    ? <>🏆 Paid <span className="font-bold">{stats.count}×</span> before · <span className="text-green-400 font-bold">{money(stats.total)}</span> total</>
                    : <span className="text-gray-400">🆕 Never been paid before</span>}
                </p>
              )}
              <p className="text-xs text-gray-500 truncate">@{(winner.login || winner.username || '').toLowerCase()}</p>
            </div>
            <div
              className={`px-3 py-2 rounded-xl border font-mono text-lg font-bold tabular-nums shrink-0 ${replied ? 'bg-green-500/15 border-green-500/40 text-green-300' : overTime ? 'bg-red-500/15 border-red-500/40 text-red-300' : 'bg-zinc-800 border-white/15 text-white'}`}
              title={replied ? 'Winner has replied' : 'Time since the winner was picked — turns red after a minute'}
            >
              {replied ? '✓ here' : clockText(elapsed)}
            </div>
          </div>

          {winner.slotCall && <p className="text-sm text-yellow-200">🎰 Requested slot: <span className="font-semibold">{winner.slotCall}</span></p>}

          {admin && !admin.stream && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              {winner.rainbetUsername
                ? <span className="text-gray-400">Rainbet: <span className="text-gray-200">{winner.rainbetUsername}</span></span>
                : <span className="text-gray-500 italic">No Rainbet username linked</span>}
              <button onClick={() => admin.editRainbet(winner)} className="text-indigo-400 hover:text-indigo-300 underline">✏️ Edit</button>
              <button onClick={() => admin.verify(winner)} className="text-indigo-400 hover:text-indigo-300 underline">🔍 Verify</button>
              <button onClick={() => admin.checkChat(winner.username)} disabled={admin.checkingChat} className="text-indigo-400 hover:text-indigo-300 underline disabled:opacity-50">{admin.checkingChat ? '⏳ Checking…' : '📊 Chat activity'}</button>
              {winner.rainbetUsername && (
                <button onClick={() => admin.checkRainbet(winner.rainbetUsername)} disabled={admin.checkingRainbet} className="text-indigo-400 hover:text-indigo-300 underline disabled:opacity-50">{admin.checkingRainbet ? '⏳ Checking…' : '✅ Rainbet signup'}</button>
              )}
            </div>
          )}
          {admin && !admin.stream && admin.chatStats && (
            <div className="text-xs bg-zinc-800/60 rounded-xl p-3">
              {admin.chatStats.found ? <p>Messages in top-100 chatstats: <span className="font-bold text-white">{admin.chatStats.messageCount ?? 'unknown'}</span></p>
                : admin.chatStats.error ? <p className="text-red-400">{admin.chatStats.error}</p>
                : <p className="text-gray-400">Not in the top 100 chatters — check StreamElements directly.</p>}
            </div>
          )}
          {admin && !admin.stream && admin.rainbetCheck && (
            <div className="text-xs bg-zinc-800/60 rounded-xl p-3">
              {admin.rainbetCheck.verified
                ? <p className="text-green-400 font-semibold">✅ Found in your referral list ({admin.rainbetCheck.listSize} names on file)</p>
                : <p className="text-yellow-400">⚠️ Not found in your referral list ({admin.rainbetCheck.listSize} names on file)</p>}
            </div>
          )}

          <div>
            {winner.provider === 'twitch' && (
              <div className={`mb-3 rounded-xl border px-3 py-2 text-sm ${lastBeforeSec == null ? 'bg-white/5 border-white/10 text-gray-400' : lastBeforeSec <= 300 ? 'bg-green-500/10 border-green-500/30 text-green-200' : lastBeforeSec <= 1800 ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200' : 'bg-red-500/10 border-red-500/30 text-red-200'}`}>
                {lastBeforeSec == null
                  ? 'No chat message on record before being picked'
                  : <>Last chatted <span className="font-bold">{durText(lastBeforeSec)}</span> before being picked{winner.lastChatAt ? <span className="text-gray-400"> ({new Date(winner.lastChatAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })})</span> : null}{winner.lastChatText ? <span className="block text-gray-300 truncate">“{winner.lastChatText}”</span> : null}</>}
              </div>
            )}
            <p className="text-sm font-semibold text-gray-300 mb-2">Winner Messages{wonMs ? <span className="text-gray-500 font-normal"> · picked at {new Date(wonMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}</span> : null}</p>
            <div className="min-h-[190px] max-h-[260px] overflow-y-auto bg-zinc-950/60 border border-white/10 rounded-2xl p-3 space-y-2">
              {chronological.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-16">Waiting for winner to send a message</p>
              ) : chronological.map((m, i) => (
                <div key={`${m.at}-${i}`} className="text-sm">
                  <span className="text-[11px] text-gray-500 mr-2 tabular-nums">{new Date(m.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}</span>
                  {wonMs && (() => {
                    const after = Math.max(0, (new Date(m.at).getTime() - wonMs) / 1000);
                    return <span className={`text-[11px] font-bold mr-2 tabular-nums ${after < WINNER_TIMER_SECONDS ? 'text-green-400' : 'text-red-400'}`}>+{clockText(after)}</span>;
                  })()}
                  <span className="text-gray-100 break-words">{m.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-2 flex-wrap">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 font-semibold text-sm transition">Close</button>
          {admin && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => admin.addToPortal(winner)} className="px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 font-bold text-sm transition">➕ Add to Giveaway Portal</button>
              <button onClick={admin.onClearWinner} disabled={admin.busy} className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 font-semibold text-sm transition disabled:opacity-50">🧹 Clear</button>
              <button onClick={admin.onReroll} disabled={admin.busy} className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-600 hover:opacity-90 text-black font-bold text-sm transition disabled:opacity-50">🔁 Reroll Winner</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(modal, document.body);
}

// Everyone already picked in this giveaway (skipped, rerolled past or cleared), each with a
// one-click "Reopen" that brings them back as the current winner with their chat intact —
// for when a winner gets skipped and Dankquan wants to go back to them. Earlier giveaways'
// winners are listed underneath for reference.
function PastWinnersPanel({ winners, history, onReopen, busy }) {
  const [open, setOpen] = useState(true);
  const [showEarlier, setShowEarlier] = useState(false);
  const earlier = (history || []).filter(h => h.winner).slice(0, 12);
  if (winners.length === 0 && earlier.length === 0) return null;
  return (
    <div className="bg-zinc-800/40 border border-white/10 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-5 py-3 text-left">
        <span className="font-bold text-sm uppercase tracking-wide text-gray-300">
          Past winners <span className="text-gray-500 normal-case font-normal tracking-normal">· {winners.length} this giveaway</span>
        </span>
        <span className="text-gray-500 text-xs">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {winners.map(w => {
            const msgs = w.messages || [];
            return (
              <div key={`${w.provider}:${w.username}:${w.wonAt}`} className="flex items-center gap-3 bg-zinc-900/60 border border-white/[0.06] rounded-xl px-3 py-2.5">
                <EntrantAvatar entry={{ username: w.username, avatar: w.avatar || w.accountInfo?.avatarUrl || null }} size={36} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">
                    {w.username}{(w.inGuild || w.isVerified) && <span className="ml-1.5">🦄</span>}
                    {w.winStats?.count > 0 && <span className="ml-2 text-[11px] font-normal text-gray-400">{w.winStats.count}× · <span className="text-green-400">{money(w.winStats.total)}</span></span>}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {msgs.length === 0 ? 'No messages yet' : `${msgs.length} message${msgs.length === 1 ? '' : 's'} · “${msgs[0].text}”`}
                  </p>
                </div>
                <button onClick={() => onReopen(w)} disabled={busy} className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-xs font-bold transition disabled:opacity-50 shrink-0">↩ Reopen</button>
              </div>
            );
          })}
          {earlier.length > 0 && (
            <div className="pt-1">
              <button onClick={() => setShowEarlier(v => !v)} className="text-xs text-gray-500 hover:text-gray-300 px-1">{showEarlier ? '▾' : '▸'} Earlier giveaways ({earlier.length})</button>
              {showEarlier && (
                <div className="mt-2 space-y-1">
                  {earlier.map((h, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 text-xs bg-zinc-900/40 rounded-lg px-3 py-2">
                      <span className="text-gray-400 truncate">{h.title}{h.concludedAt ? ` · ${new Date(h.concludedAt).toLocaleDateString()}` : ''}</span>
                      <span className="text-yellow-400 font-semibold shrink-0">🏆 {h.winner.username}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The whole live section. `admin` is undefined for the public page; for the admin page it
// carries the current state plus every control handler (see AdminPage).
function LiveGiveaway({ admin }) {
  const publicMeta = useGiveawayMeta(!admin);
  const meta = admin ? admin.meta : publicMeta;
  const [channel, setChannel] = useState('dankquan');
  const [modalOpen, setModalOpen] = useState(false);
  const seenWonAt = useRef(null);
  const [formTitle, setFormTitle] = useState('');
  const [formMode, setFormMode] = useState('any');
  const [formKeyword, setFormKeyword] = useState('');
  const [editKeyword, setEditKeyword] = useState('');

  const g = meta?.giveaway || null;
  const winner = g?.winner || null;
  const wonAt = winner?.wonAt || null;
  const entryMode = meta?.entryMode === 'any' ? 'any' : 'keyword';
  const keyword = meta?.keyword || admin?.defaultKeyword || '';
  const activeWindow = entryMode === 'any' ? Number(meta?.activeWindow) || 0 : 0;

  // A new winner pops the card open for everyone watching. On first load it only pops if the
  // pick just happened — someone opening the page an hour later shouldn't get a stale modal.
  useEffect(() => {
    if (!wonAt) { setModalOpen(false); return; }
    if (seenWonAt.current === wonAt) return;
    const first = seenWonAt.current === null;
    seenWonAt.current = wonAt;
    if (!first || Date.now() - new Date(wonAt).getTime() < 3 * 60 * 1000) setModalOpen(true);
  }, [wonAt]);

  useEffect(() => { setEditKeyword(meta?.keyword || ''); }, [meta?.keyword]);

  if (meta === undefined) {
    return <div className="text-center text-gray-500 text-sm py-10">Loading giveaway…</div>;
  }

  if (!g) {
    if (!admin) {
      return (
        <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] text-center px-6 py-10">
          <p className="text-3xl mb-2">🎁</p>
          <p className="text-lg font-bold">No giveaway running right now</p>
          <p className="text-sm text-gray-400 mt-1">When Dankquan starts one on stream, you'll watch it here live — entrants, chat, and the winner.</p>
        </div>
      );
    }
    return (
      <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl p-6 border border-white/10 space-y-4">
        <h2 className="text-lg font-bold">Start a new giveaway</h2>
        <input value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Title (e.g. Giveaway 7)" className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
        <div>
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">How do people enter?</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFormMode('any')} className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${formMode === 'any' ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>Any chat message</button>
            <button onClick={() => setFormMode('keyword')} className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${formMode === 'keyword' ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>Keyword</button>
          </div>
          {formMode === 'keyword' && (
            <input value={formKeyword} onChange={e => setFormKeyword(e.target.value)} placeholder={admin.defaultKeyword ? `Keyword (default: ${admin.defaultKeyword})` : 'Keyword, e.g. danky'} maxLength={32} className="mt-3 w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
          )}
        </div>
        <button
          onClick={() => admin.onCreate({ title: formTitle.trim(), entryMode: formMode, keyword: formMode === 'keyword' ? (formKeyword.trim() || admin.defaultKeyword || '') : '' })}
          disabled={admin.busy || !formTitle.trim() || (formMode === 'keyword' && !(formKeyword.trim() || admin.defaultKeyword))}
          className="px-6 py-3 bg-gradient-to-r from-[#4f39f6] to-[#372aac] hover:from-[#615fff] hover:to-[#432dd7] active:scale-[0.98] text-white font-bold rounded-xl transition disabled:opacity-50"
        >Start giveaway &amp; open entries</button>
      </div>
    );
  }

  const modeText = !meta.isOpen
    ? 'Entries are closed.'
    : entryMode === 'any'
      ? (activeWindow ? `Any chat message enters. Only people who chatted in the last ${activeWindow === 60 ? 'hour' : `${activeWindow} min`} can win.` : 'Any chat message enters this giveaway.')
      : keyword ? `Type "${keyword}" in chat to enter this giveaway.` : 'Type the keyword in chat to enter this giveaway.';
  const messages = g.winnerMessages || [];

  return (
    <div className="space-y-4">
      <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/10 px-5 py-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-2xl font-black truncate">{g.title}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{modeText}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shrink-0 ${meta.isOpen ? 'bg-green-500/15 text-green-300 border-green-500/30' : 'bg-white/5 text-gray-400 border-white/10'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.isOpen ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
            {meta.isOpen ? 'ENTRIES OPEN' : 'ENTRIES CLOSED'}
          </span>
        </div>

        {admin && (
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-white/10">
            <button onClick={admin.onRoll} disabled={admin.busy || (meta.entryCount || 0) === 0} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-600 hover:opacity-90 text-black font-bold text-sm transition disabled:opacity-50">🎲 Choose a Winner</button>
            {meta.isOpen
              ? <button onClick={admin.onClose} disabled={admin.busy} className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 font-semibold text-sm transition disabled:opacity-50">Close entries</button>
              : <button onClick={admin.onOpen} disabled={admin.busy} className="px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 font-bold text-sm transition disabled:opacity-50">Open entries</button>}
            <span className="w-px h-6 bg-white/10 mx-1 hidden sm:block" />
            <div className="inline-flex rounded-xl overflow-hidden border border-white/10">
              <button onClick={() => admin.onSetEntry({ entryMode: 'any' })} className={`px-3 py-2 text-xs font-semibold transition ${entryMode === 'any' ? 'bg-indigo-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}>Any message</button>
              <button onClick={() => admin.onSetEntry({ entryMode: 'keyword', keyword: editKeyword || keyword })} className={`px-3 py-2 text-xs font-semibold transition ${entryMode === 'keyword' ? 'bg-indigo-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}>Keyword</button>
            </div>
            {entryMode === 'any' && (
              <div className="inline-flex items-center rounded-xl overflow-hidden border border-white/10" title="Everyone stays entered — only people who chatted within this window can be drawn">
                <span className="px-2.5 py-2 text-[11px] text-gray-400 bg-white/5">Chatted in</span>
                {[[0, 'Any time'], [15, '15m'], [30, '30m'], [60, '1h']].map(([w, label]) => (
                  <button key={w} onClick={() => admin.onSetEntry({ activeWindow: w })} className={`px-3 py-2 text-xs font-semibold transition ${activeWindow === w ? 'bg-indigo-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}>{label}</button>
                ))}
              </div>
            )}
            {entryMode === 'keyword' && (
              <>
                <input value={editKeyword} onChange={e => setEditKeyword(e.target.value)} placeholder="keyword" maxLength={32} className="w-28 bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500" />
                <button onClick={() => admin.onSetEntry({ entryMode: 'keyword', keyword: editKeyword })} disabled={!editKeyword.trim() || editKeyword.trim().toLowerCase() === (meta.keyword || '')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-semibold transition disabled:opacity-40">Save</button>
              </>
            )}
            <span className="flex-1" />
            <button onClick={admin.onReset} disabled={admin.busy} className="px-4 py-2.5 rounded-xl bg-white text-zinc-900 hover:bg-gray-200 font-bold text-sm transition disabled:opacity-50">Reset Giveaway</button>
            <button onClick={admin.onEnd} disabled={admin.busy} className="px-4 py-2.5 rounded-xl bg-white text-zinc-900 hover:bg-gray-200 font-bold text-sm transition disabled:opacity-50">End Giveaway</button>
          </div>
        )}
      </div>

      {winner && !modalOpen && (
        <button onClick={() => setModalOpen(true)} className="w-full flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-gradient-to-r from-yellow-500/15 to-transparent border border-yellow-500/30 hover:border-yellow-400/50 transition text-left">
          <span className="font-bold text-yellow-300 truncate">🏆 Winner: {winner.username}{(winner.inGuild || winner.isVerified) ? ' 🦄' : ''}</span>
          <span className="text-xs text-gray-400 shrink-0">View winner →</span>
        </button>
      )}

      <div className={`grid grid-cols-1 gap-4 items-stretch ${admin?.stream ? 'lg:grid-cols-[minmax(0,1fr)_560px]' : 'lg:grid-cols-[minmax(0,1fr)_380px]'}`}>
        <LiveEntrantsPanel
          refreshKey={admin?.refreshKey}
          onChannel={setChannel}
          onRemove={admin && !admin.stream ? admin.onRemove : undefined}
          modeText={modeText}
          tall={!!admin?.stream}
          footerNote={admin && !admin.stream && admin.eligible ? `${admin.eligible.total.toLocaleString()} users, ${admin.eligible.eligible.toLocaleString()} verified` : null}
        />
        <div className={`relative bg-zinc-900/70 border border-white/10 rounded-2xl overflow-hidden h-[520px] lg:h-auto ${admin?.stream ? 'lg:min-h-[760px]' : 'lg:min-h-[560px]'}`}>
          {/* Absolutely positioned so chat never makes the box taller: it fills whatever height the entrants list sets and scrolls inside. */}
          <div className="absolute inset-0 flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2 text-sm">
              <span className="font-semibold">Chat <span className="text-gray-500 font-normal">#{channel}</span></span>
            </div>
            <div className="flex-1 min-h-0 flex flex-col"><FastChat channel={channel} /></div>
          </div>
        </div>
      </div>

      {winner && modalOpen && (
        <WinnerModal
          winner={winner}
          title={g.title}
          messages={messages}
          onClose={() => setModalOpen(false)}
          admin={admin ? { ...admin.winnerTools, stream: admin.stream, busy: admin.busy, onReroll: admin.onReroll, onClearWinner: admin.onClearWinner } : null}
        />
      )}
    </div>
  );
}

function GiveawayPage({ accounts, rainbetUsername, onSaveRainbet }) {
  const [payouts, setPayouts] = useState(null);
  const [showPayoutList, setShowPayoutList] = useState(false);
  const [showClaim, setShowClaim] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [claims, setClaims] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [claimMsg, setClaimMsg] = useState(null);
  const [myStatus, setMyStatus] = useState(null);

  const isValid = isValidCombo(accounts);
  const streamerProvider = accounts.twitch ? 'twitch' : accounts.kick ? 'kick' : null;
  const streamerName = accounts.twitch?.username || accounts.kick?.username || '';
  const sessionToken = accounts.discord?.sessionToken;

  // The public Giveaway and Slot Picker sections are gone — this page is just the portal
  // now: the payout total, claiming prizes, and the viewer's own verification status.
  const load = useCallback(async () => {
    try {
      const [pRes, myRes] = await Promise.all([
        fetch('/.netlify/functions/giveaway?path=payouts'),
        sessionToken ? fetch('/.netlify/functions/giveaway?path=my-status', { headers: { 'X-Session-Token': sessionToken } }) : Promise.resolve(null),
      ]);
      const pData = await pRes.json();
      if (myRes) setMyStatus(await myRes.json());
      if (pData && typeof pData.totalPaid === 'number') setPayouts(pData);
    } catch (e) { console.error(e); }
  }, [sessionToken]);

  useEffect(() => {
    load();
    let interval = null;
    const startInterval = () => { if (!interval) interval = setInterval(() => load(), 30000); };
    const stopInterval = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVisibilityChange = () => {
      if (document.hidden) stopInterval();
      else { load(); startInterval(); }
    };
    if (!document.hidden) startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { stopInterval(); document.removeEventListener('visibilitychange', onVisibilityChange); };
  }, [load]);

  const loadClaims = useCallback(async () => {
    if (!isValid || !streamerProvider || !streamerName) { setClaims(null); return; }
    try {
      const res = await fetch(`/.netlify/functions/claim-giveaway?provider=${streamerProvider}&username=${encodeURIComponent(streamerName)}`, {
        headers: { 'X-Session-Token': sessionToken || '' },
      });
      const data = await res.json();
      setClaims(data.claims || []);
    } catch (e) { console.error(e); }
  }, [isValid, streamerProvider, streamerName, sessionToken]);

  useEffect(() => { loadClaims(); }, [loadClaims]);

  const claimPrize = async (id) => {
    setClaiming(id);
    setClaimMsg(null);
    try {
      const res = await fetch('/.netlify/functions/claim-giveaway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({ id, provider: streamerProvider, username: streamerName }),
      });
      const data = await res.json();
      setClaimMsg({ type: data.success ? 'success' : 'error', text: data.success ? "Claimed — Dankquan's been flagged to pay you." : (data.message || 'Could not claim.') });
      if (data.success) loadClaims();
    } catch {
      setClaimMsg({ type: 'error', text: 'Something went wrong. Try again.' });
    }
    setClaiming(null);
  };

  // Only prizes still waiting to be claimed show up on this page — everything already
  // claimed lives in the "Giveaway win history" button in the profile menu (top right).
  const unclaimed = (claims || []).filter(c => !c.claimed);

  return (
    <div className="flex-1 flex flex-col">
    <div className="max-w-6xl mx-auto p-6 md:p-10 flex-1 w-full">
      <h1 className="text-4xl font-black mb-6 text-center flex items-center justify-center gap-3"><Gift size={34} strokeWidth={2.25} /> Giveaway Portal</h1>

      <div className="max-w-3xl mx-auto">
      {/* Won something? One fun button — opens pending prizes, or a friendly "nothing yet". */}
      {isValid && (
        <div className="mb-6">
          <button
            onClick={() => { setShowClaim(v => !v); setClaimMsg(null); }}
            className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-black text-lg text-black bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 hover:opacity-95 active:scale-[0.99] transition shadow-lg shadow-orange-900/30 ${unclaimed.length > 0 && !showClaim ? 'pulsate-btn' : ''}`}
          >
            <span className="text-2xl">🎁</span>
            Claim Giveaway
            {unclaimed.length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-black text-yellow-300 font-bold">{unclaimed.length}</span>}
          </button>

          {showClaim && (
            <div className="mt-3 space-y-3 fade-in">
              {claimMsg && (
                <div className={`text-sm px-4 py-3 rounded-xl ${claimMsg.type === 'success' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
                  {claimMsg.text}
                </div>
              )}
              {unclaimed.length === 0 ? (
                <div className="px-4 py-3 rounded-xl bg-zinc-800/40 border border-white/[0.06] text-sm text-gray-500">
                  Nothing to claim right now. Past wins are in your profile menu (top right).
                </div>
              ) : unclaimed.map(c => (
                <div key={c.id} className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-yellow-500/30 p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-bold">{c.eventType === 'slotpicker' ? '🎰' : '🎁'} {c.eventTitle || (c.eventType === 'slotpicker' ? 'Slot Picker' : 'Giveaway')}</p>
                    <p className="text-sm text-gray-400">Won {new Date(c.wonAt).toLocaleDateString()} · <span className="text-green-400 font-bold">{money(c.amount)}</span></p>
                  </div>
                  <button onClick={() => claimPrize(c.id)} disabled={claiming === c.id} className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 hover:opacity-90 text-black font-bold rounded-xl transition disabled:opacity-50 text-sm">
                    {claiming === c.id ? 'Claiming...' : '🎉 Claim'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-6 flex flex-col items-center">
        <button
          onClick={() => setShowRules(v => !v)}
          className="px-4 py-1.5 rounded-full text-xs font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 transition"
        >
          📜 Rules {showRules ? '▾' : '▸'}
        </button>
        {showRules && (
          <div className="mt-3 w-full max-w-md bg-zinc-800/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-gray-300 text-center fade-in">
            We only allow one entry per household. If you don't follow this rule, you'll be banned and disqualified.
          </div>
        )}
      </div>

      <RainbetPrompt accounts={accounts} rainbetUsername={rainbetUsername} onSaveRainbet={onSaveRainbet} />

      {myStatus?.signedIn && !myStatus.isVerified && (
        <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl px-5 py-4 text-sm">
          <p className="font-bold text-yellow-300">
            {myStatus.inGuild ? "⚠️ You're not verified in the Discord yet" : "⚠️ You haven't joined the Discord server yet"}
          </p>
          <p className="text-yellow-200/80 mt-1">
            {myStatus.inGuild
              ? "Anyone can enter, but you won't be able to claim a prize until you're verified in the Discord — head over and get verified."
              : "Anyone can enter, but you won't be able to claim a prize until you've joined the Discord and gotten verified there."}
          </p>
          <a href="https://discord.gg/Dankquan" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm font-bold text-yellow-300 hover:text-yellow-200 underline">
            Open the Discord →
          </a>
        </div>
      )}

      <div className="mb-6">
        <TotalGivenAway payouts={payouts} onOpenList={() => setShowPayoutList(true)} />
      </div>

      {showPayoutList && <PayoutLeaderboardModal payouts={payouts} onClose={() => setShowPayoutList(false)} />}
      </div>

      {/* Live giveaway — entrants, Twitch chat and the winner card, same view Dankquan shares on stream. */}
      <div className="mb-6">
        <LiveGiveaway />
      </div>

    </div>
    <ResponsibleGamblingFooter />
    </div>
  );
}

// Renders each saved tweet via X's own official embed widget — no API keys, no rate
// limits, just linking to a post the same way any embed-a-tweet button on the web does.
// The widget script only needs to load once; every time the clip list changes we just
// ask it to re-scan for new <blockquote class="twitter-tweet"> elements.
// Flip to true to bring the public Clips page back (nav link + /clips route). While false,
// the link is hidden and /clips just sends people home. The admin Clips tab is unaffected,
// so clips can still be added in the meantime.
const SHOW_CLIPS_PAGE = false;

function ClipsPage() {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/.netlify/functions/clips')
      .then(res => res.json())
      .then(data => { if (!cancelled) setClips(data.clips || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (clips.length === 0) return;
    const loadWidgets = () => { window.twttr?.widgets?.load(containerRef.current); };
    if (window.twttr?.widgets) {
      loadWidgets();
      return;
    }
    const existing = document.getElementById('twitter-wjs');
    if (existing) {
      existing.addEventListener('load', loadWidgets, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = 'twitter-wjs';
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.onload = loadWidgets;
    document.body.appendChild(script);
  }, [clips]);

  return (
    <div className="flex-1 flex flex-col">
      <div className="max-w-5xl mx-auto p-6 md:p-10 flex-1 w-full">
        <h1 className="text-4xl font-black mb-2 text-center flex items-center justify-center gap-3"><Film size={34} strokeWidth={2.25} /> Clips</h1>
        <p className="text-center text-gray-400 text-sm mb-8">Big wins and funny moments, clipped from the stream.</p>

        {loading ? (
          <div className="text-center text-xl py-20">Loading...</div>
        ) : clips.length === 0 ? (
          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] text-center text-gray-400 py-16">
            <p className="text-xl mb-1">No clips yet</p>
            <p className="text-sm">Check back soon!</p>
          </div>
        ) : (
          <div ref={containerRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
            {clips.map(c => (
              <div key={c.id} className="w-full max-w-md">
                {c.label && <p className="text-sm text-gray-300 font-semibold mb-2 text-center">{c.label}</p>}
                <blockquote className="twitter-tweet" data-theme="dark">
                  <a href={c.url}>Loading clip…</a>
                </blockquote>
              </div>
            ))}
          </div>
        )}
      </div>
      <ResponsibleGamblingFooter />
    </div>
  );
}

// The giveaway/slot pools can hold well over a thousand entrants, so the admin page's frequent
// refreshes ask for the light version (no per-entrant lookups). Only the tabs that actually
// list entrants (Entrant Pool, Slot Picker) get the full one.
const adminStateQuery = (view) => (['entrants', 'slotpicker'].includes(view) ? {} : { query: { light: '1' } });

function AdminPage({ accounts }) {
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState('giveaway'); // 'giveaway' | 'slotpicker' | 'entrants' | 'portal' | 'payouts' | 'wagers' | 'referral'
  // Stream mode strips the page down to just the live giveaway (no sidebar, no other tabs, no
  // private details), so it's safe to share the screen. Remembered across reloads on purpose —
  // an accidental refresh mid-stream shouldn't suddenly show everything.
  const [streamMode, setStreamMode] = useState(() => { try { return localStorage.getItem('dq_admin_stream') === '1'; } catch { return false; } });
  const toggleStream = (on) => { setStreamMode(on); try { localStorage.setItem('dq_admin_stream', on ? '1' : '0'); } catch { /* storage blocked */ } };
  const [collapsed, setCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem('dq_admin_collapsed') || '{}'); } catch { return {}; } });
  const toggleGroup = (label) => setCollapsed(c => {
    const next = { ...c, [label]: !c[label] };
    try { localStorage.setItem('dq_admin_collapsed', JSON.stringify(next)); } catch { /* storage blocked */ }
    return next;
  });
  const viewRef = useRef('giveaway');
  useEffect(() => { viewRef.current = view; }, [view]);

  const [giveaway, setGiveaway] = useState(null);
  const [gPoolOpen, setGPoolOpen] = useState(false);
  const [slotpicker, setSlotpicker] = useState(null);
  const [spPoolOpen, setSpPoolOpen] = useState(false); // same pool as giveaway, kept per-load for clarity
  const [gHistory, setGHistory] = useState([]);
  const [spHistory, setSpHistory] = useState([]);

  // Pool settings + counts for the live giveaway view (the full entrants list is paged
  // separately — the admin page never pulls everyone in on every refresh).
  const [gMeta, setGMeta] = useState({ entryCount: 0, entryMode: 'keyword', keyword: '', activeWindow: 0 });
  const [gEligible, setGEligible] = useState(null); // { total, eligible }
  const [entriesRefresh, setEntriesRefresh] = useState(0);
  const [spTitle, setSpTitle] = useState('');
  const [spDescription, setSpDescription] = useState('');

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const [chatStats, setChatStats] = useState(null);
  const [checkingChatStats, setCheckingChatStats] = useState(false);
  const [rainbetCheck, setRainbetCheck] = useState(null);
  const [checkingRainbet, setCheckingRainbet] = useState(false);

  // Generic reveal-animation state shared by both features — `feature` tags which one
  // the current reveal belongs to so the two winner cards don't fight over it.
  const [reveal, setReveal] = useState({ feature: null, pool: [], winner: null, show: false, done: false });
  const [identityCard, setIdentityCard] = useState(null); // winner object | null

  const [referralList, setReferralList] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [savingReferralList, setSavingReferralList] = useState(false);

  const [payouts, setPayouts] = useState(null);
  const [payoutName, setPayoutName] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [savingPayout, setSavingPayout] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [fixingPayoutNames, setFixingPayoutNames] = useState(false);
  const [mergeFromName, setMergeFromName] = useState('');
  const [mergeToName, setMergeToName] = useState('');
  const [mergingNames, setMergingNames] = useState(false);

  const [wagerData, setWagerData] = useState(null);
  const [wagerImportText, setWagerImportText] = useState('');
  const [importingWagers, setImportingWagers] = useState(false);

  const [chatEntryStatus, setChatEntryStatus] = useState(null);
  const [chatSettingsOpen, setChatSettingsOpen] = useState(false);
  const [chatKeywordInput, setChatKeywordInput] = useState('');
  const [savingKeyword, setSavingKeyword] = useState(false);
  const [subscribingChat, setSubscribingChat] = useState(false);

  const [people, setPeople] = useState([]);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [clips, setClips] = useState([]);
  const [clipUrl, setClipUrl] = useState('');
  const [clipLabel, setClipLabel] = useState('');
  const [clipOrientation, setClipOrientation] = useState('landscape');
  const [savingClip, setSavingClip] = useState(false);
  const [peopleMultiCount, setPeopleMultiCount] = useState(0);
  const [peopleSharedIpCount, setPeopleSharedIpCount] = useState(0);
  const [securityData, setSecurityData] = useState(null);
  const [securityFlaggedCount, setSecurityFlaggedCount] = useState(0);
  const [blacklisting, setBlacklisting] = useState(null);
  const [refreshingName, setRefreshingName] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [lastEligibilityRefresh, setLastEligibilityRefresh] = useState(null);
  const [refreshingAllPeople, setRefreshingAllPeople] = useState(false);
  const [lastPeopleRefresh, setLastPeopleRefresh] = useState(null);
  const [peopleFlaggedOnly, setPeopleFlaggedOnly] = useState(false);

  const [giveawayClaims, setGiveawayClaims] = useState([]);
  const [manualClaimName, setManualClaimName] = useState('');
  const [manualClaimProvider, setManualClaimProvider] = useState('twitch');
  const [manualClaimAmount, setManualClaimAmount] = useState('');
  const [manualClaimTitle, setManualClaimTitle] = useState('');
  const [savingManualClaim, setSavingManualClaim] = useState(false);
  const [manualCheck, setManualCheck] = useState(null); // winner-check result for the name/provider typed above
  const [checkingWinner, setCheckingWinner] = useState(false);
  const [claimsTotalUnpaid, setClaimsTotalUnpaid] = useState(0);
  const [claimsTotalPaid, setClaimsTotalPaid] = useState(0);
  const [claimsNeedAttention, setClaimsNeedAttention] = useState(0);

  const sessionToken = accounts.discord?.sessionToken;

  const callAdmin = useCallback(async (action, opts = {}) => {
    const extra = opts.query ? '&' + new URLSearchParams(opts.query).toString() : '';
    const res = await fetch(`/.netlify/functions/admin?action=${action}${extra}`, {
      method: opts.method || 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
    return res.json();
  }, [sessionToken]);

  const loadAll = useCallback(async () => {
    const [gState, spState, gHist, spHist, referralData, payoutData, wagerRes, chatEntryData, peopleData, claimData, clipsData, securityRes] = await Promise.all([
      callAdmin('giveaway-state', adminStateQuery(viewRef.current)),
      callAdmin('slotpicker-state', adminStateQuery(viewRef.current)),
      callAdmin('giveaway-history'),
      callAdmin('slotpicker-history').catch(() => ({ history: [] })),
      callAdmin('get-referral-list'),
      callAdmin('payout-list'),
      callAdmin('wager-list'),
      callAdmin('chat-entry-status'),
      callAdmin('people-list'),
      callAdmin('giveaway-claim-list'),
      callAdmin('clips-list'),
      callAdmin('security-list'),
    ]);
    setGiveaway(gState.giveaway || null);
    setGPoolOpen(!!gState.poolOpen);
    setGMeta({ entryCount: gState.entryCount || 0, entryMode: gState.entryMode || 'keyword', keyword: gState.keyword || '', activeWindow: gState.activeWindow || 0 });
    setSlotpicker(spState.slotpicker || null);
    setSpPoolOpen(!!spState.poolOpen);
    setGHistory(gHist.history || []);
    setSpHistory(spHist.history || []);
    setReferralCount(referralData.count || 0);
    setReferralList((referralData.list || []).join('\n'));
    if (typeof payoutData?.totalPaid === 'number') setPayouts(payoutData);
    if (wagerRes?.config) setWagerData(wagerRes);
    if (chatEntryData && !chatEntryData.error) {
      setChatEntryStatus(chatEntryData);
      setChatKeywordInput(chatEntryData.keyword || '');
    }
    if (peopleData?.people) { setPeople(peopleData.people); setPeopleMultiCount(peopleData.multiCount || 0); setPeopleSharedIpCount(peopleData.sharedIpCount || 0); }
    else if (peopleData?.error) console.error('People list error:', peopleData.error);
    if (clipsData?.clips) setClips(clipsData.clips);
    if (securityRes && !securityRes.error) { setSecurityData(securityRes); setSecurityFlaggedCount(securityRes.flaggedCount || 0); }
    if (claimData?.claims) {
      setGiveawayClaims(claimData.claims);
      setClaimsTotalUnpaid(claimData.totalUnpaid || 0);
      setClaimsTotalPaid(claimData.totalPaid || 0);
      setClaimsNeedAttention(claimData.needsAttention || 0);
    }
  }, [callAdmin]);

  useEffect(() => {
    (async () => {
      if (!sessionToken) { setChecking(false); setIsAdmin(false); return; }
      const data = await callAdmin('whoami');
      setIsAdmin(!!data.isAdmin);
      setChecking(false);
      if (data.isAdmin) loadAll();
    })();
  }, [sessionToken, callAdmin, loadAll]);

  const pollState = useCallback(async () => {
    const [gState, spState] = await Promise.all([callAdmin('giveaway-state', adminStateQuery(viewRef.current)), callAdmin('slotpicker-state', adminStateQuery(viewRef.current))]);
    setGiveaway(gState.giveaway || null);
    setGPoolOpen(!!gState.poolOpen);
    setGMeta({ entryCount: gState.entryCount || 0, entryMode: gState.entryMode || 'keyword', keyword: gState.keyword || '', activeWindow: gState.activeWindow || 0 });
    setSlotpicker(spState.slotpicker || null);
    setSpPoolOpen(!!spState.poolOpen);
  }, [callAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let interval = null;
    const startInterval = () => { if (!interval) interval = setInterval(pollState, 5000); };
    const stopInterval = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVisibilityChange = () => {
      if (document.hidden) stopInterval();
      else { pollState(); startInterval(); }
    };
    if (!document.hidden) startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { stopInterval(); document.removeEventListener('visibilitychange', onVisibilityChange); };
  }, [isAdmin, pollState]);

  useEffect(() => {
    if (isAdmin && (view === 'entrants' || view === 'slotpicker')) pollState();
  }, [isAdmin, view, pollState]);

  const refreshEligible = useCallback(async () => {
    try {
      const d = await callAdmin('giveaway-eligible-count');
      if (typeof d?.eligible === 'number') setGEligible(d);
    } catch (e) { console.error(e); }
  }, [callAdmin]);

  const hasGiveaway = !!giveaway;
  useEffect(() => {
    if (!isAdmin || view !== 'giveaway' || !hasGiveaway) return;
    refreshEligible();
    const t = setInterval(refreshEligible, 60000);
    return () => clearInterval(t);
  }, [isAdmin, view, hasGiveaway, refreshEligible]);

  // --- Shared entrant pool ---
  const openPool = async () => { setBusy(true); await callAdmin('pool-open', { method: 'POST' }); await loadAll(); setBusy(false); };
  const closePool = async () => { setBusy(true); await callAdmin('pool-close', { method: 'POST' }); await loadAll(); setBusy(false); };
  const clearPool = async () => {
    if (!window.confirm('Clear the ENTIRE shared entrant pool? This removes everyone from both the Giveaway and Slot Picker.')) return;
    setBusy(true);
    await callAdmin('pool-clear', { method: 'POST' });
    await loadAll();
    setBusy(false);
  };
  const removeEntrant = async (entry) => {
    if (!window.confirm(`Remove ${entry.username}? This removes them from both the Giveaway and Slot Picker.`)) return;
    const data = await callAdmin('pool-entry-remove', { method: 'POST', body: { username: entry.username, provider: entry.provider, enteredAt: entry.enteredAt } });
    if (data.success) { setMessage({ type: 'success', text: `Removed ${entry.username}.` }); loadAll(); setEntriesRefresh(n => n + 1); }
    else setMessage({ type: 'error', text: data.error || 'Could not remove that entry.' });
  };

  // --- Giveaway ---
  // New giveaway: starts from an empty entrants list (anything left over from the last one
  // is cleared), then opens entries with the chosen entry mode.
  const createGiveaway = async ({ title, entryMode, keyword }) => {
    if (!title || !title.trim()) return;
    setBusy(true); setMessage(null);
    setReveal({ feature: null, pool: [], winner: null, show: false, done: false });
    await callAdmin('pool-clear', { method: 'POST' });
    const data = await callAdmin('giveaway-create', { method: 'POST', body: { title, description: '', entryMode, keyword } });
    if (data.success) { setMessage({ type: 'success', text: 'Giveaway started and open for entries.' }); setGEligible(null); await loadAll(); setEntriesRefresh(n => n + 1); }
    else setMessage({ type: 'error', text: data.error || 'Could not create giveaway.' });
    setBusy(false);
  };
  // "Choose a Winner" / "Reroll Winner" go straight to the winner card — no confirm prompt
  // and no spinner, since this is run live on stream.
  const rollGiveaway = async () => {
    setBusy(true); setMessage(null); setChatStats(null); setRainbetCheck(null);
    const data = await callAdmin('giveaway-roll', { method: 'POST' });
    if (data.success) await pollState();
    else setMessage({ type: 'error', text: data.error || 'Could not roll a winner.' });
    setBusy(false);
  };
  const rerollGiveaway = async () => {
    setBusy(true); setMessage(null); setChatStats(null); setRainbetCheck(null);
    const data = await callAdmin('giveaway-reroll', { method: 'POST' });
    if (data.success) await pollState();
    else setMessage({ type: 'error', text: data.error || 'Could not reroll.' });
    setBusy(false);
  };
  const clearGiveawayWinner = async () => {
    setBusy(true); setMessage(null); setChatStats(null); setRainbetCheck(null);
    const data = await callAdmin('giveaway-clear-winner', { method: 'POST' });
    if (data.success) await pollState();
    else setMessage({ type: 'error', text: data.error || 'Could not clear winner.' });
    setBusy(false);
  };
  // Reset: wipes the entrants and the current winner but leaves the giveaway running.
  const resetGiveaway = async () => {
    if (!window.confirm('Reset this giveaway? This clears every entrant and the current winner. The giveaway stays open.')) return;
    setBusy(true); setMessage(null); setChatStats(null); setRainbetCheck(null);
    await callAdmin('pool-clear', { method: 'POST' });
    await callAdmin('giveaway-clear-winner', { method: 'POST' });
    await pollState();
    setEntriesRefresh(n => n + 1);
    setGEligible(null);
    setBusy(false);
  };
  // End: moves it to Past Giveaways, closes entries and empties the entrants list.
  const archiveGiveaway = async () => {
    if (!window.confirm('End this giveaway? It moves to Past Giveaways, entries close, and the entrants list is cleared.')) return;
    setBusy(true);
    setReveal({ feature: null, pool: [], winner: null, show: false, done: false });
    const data = await callAdmin('giveaway-archive', { method: 'POST' });
    if (data.success) {
      await callAdmin('pool-close', { method: 'POST' });
      await callAdmin('pool-clear', { method: 'POST' });
      setGEligible(null);
      await loadAll();
      setEntriesRefresh(n => n + 1);
    }
    setBusy(false);
  };
  const reopenWinner = async (w) => {
    setBusy(true); setMessage(null); setChatStats(null); setRainbetCheck(null);
    const data = await callAdmin('giveaway-restore-winner', { method: 'POST', body: { username: w.username, provider: w.provider } });
    if (data.success) await pollState();
    else setMessage({ type: 'error', text: data.error || 'Could not reopen that winner.' });
    setBusy(false);
  };
  const setEntrySettings = async (settings) => {
    await callAdmin('pool-set-entry', { method: 'POST', body: settings });
    await pollState();
  };

  // --- Slot Picker ---
  const createSlotpicker = async () => {
    if (!spTitle.trim()) return;
    setBusy(true); setMessage(null);
    setReveal({ feature: null, pool: [], winner: null, show: false, done: false });
    const data = await callAdmin('slotpicker-create', { method: 'POST', body: { title: spTitle, description: spDescription } });
    if (data.success) { setSpTitle(''); setSpDescription(''); setMessage({ type: 'success', text: 'Slot Picker created and open for entries.' }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not create slot picker.' });
    setBusy(false);
  };
  const rollSlotpicker = async () => {
    if (!window.confirm('Pick now? Entries stay open — this just picks randomly from everyone currently in the pool.')) return;
    setBusy(true); setMessage(null);
    const data = await callAdmin('slotpicker-roll', { method: 'POST' });
    if (data.success) {
      setMessage({ type: 'success', text: `Picked: ${data.winner.username} (${data.winner.provider})` });
      setReveal({ feature: 'slotpicker', pool: (data.slotpicker.entries || []).map(e => ({ username: e.username, provider: e.provider, avatar: e.avatar || null })), winner: data.winner, show: true, done: false });
      loadAll();
    } else setMessage({ type: 'error', text: data.error || 'Could not pick.' });
    setBusy(false);
  };
  const rerollSlotpicker = async () => {
    if (!window.confirm('Pick again from the same pool?')) return;
    setBusy(true); setMessage(null);
    const data = await callAdmin('slotpicker-reroll', { method: 'POST' });
    if (data.success) {
      setMessage({ type: 'success', text: `New pick: ${data.winner.username} (${data.winner.provider})` });
      setReveal({ feature: 'slotpicker', pool: (data.slotpicker.entries || []).map(e => ({ username: e.username, provider: e.provider, avatar: e.avatar || null })), winner: data.winner, show: true, done: false });
      loadAll();
    } else setMessage({ type: 'error', text: data.error || 'Could not repick.' });
    setBusy(false);
  };
  const clearSlotpickerWinner = async () => {
    if (!window.confirm('Clear the current pick? Entries in the pool are untouched.')) return;
    setBusy(true);
    setReveal({ feature: null, pool: [], winner: null, show: false, done: false });
    const data = await callAdmin('slotpicker-clear-winner', { method: 'POST' });
    if (data.success) { setMessage({ type: 'success', text: 'Pick cleared.' }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not clear.' });
    setBusy(false);
  };
  const archiveSlotpicker = async () => {
    if (!window.confirm('Archive this Slot Picker event? The entrant pool is untouched.')) return;
    setBusy(true);
    setReveal({ feature: null, pool: [], winner: null, show: false, done: false });
    const data = await callAdmin('slotpicker-archive', { method: 'POST' });
    if (data.success) loadAll();
    setBusy(false);
  };

  const announceWinner = async () => {
    setBusy(true);
    const data = await callAdmin('giveaway-announce', { method: 'POST' });
    setMessage(data.success ? { type: 'success', text: 'Winner posted in Twitch chat.' } : { type: 'error', text: `Couldn't post in Twitch chat: ${data.error || 'unknown error'}` });
    await pollState();
    setBusy(false);
  };

  const fetchWinnerFollow = useCallback((login) => callAdmin('winner-follow', { query: { login } }), [callAdmin]);

  const checkChatStats = async (username) => {
    setCheckingChatStats(true); setChatStats(null); setRainbetCheck(null);
    const data = await callAdmin('chatstats', { query: { username } });
    setChatStats(data);
    setCheckingChatStats(false);
  };
  const checkRainbet = async (rainbetName) => {
    setCheckingRainbet(true); setRainbetCheck(null);
    const data = await callAdmin('verify-rainbet', { query: { username: rainbetName } });
    setRainbetCheck(data);
    setCheckingRainbet(false);
  };

  // Works for whichever feature's winner is being edited — resolvedDiscordId comes from
  // the icon-enrichment pass (chat entrants resolved via identity-links), discordId from
  // a site-form entry directly.
  const editWinnerRainbet = async (winner) => {
    const current = winner?.rainbetUsername || '';
    const next = window.prompt("Set/override this person's linked Rainbet username (only you can do this):", current);
    if (next === null) return;
    setBusy(true);
    const targetDiscordId = winner.resolvedDiscordId || winner.discordId;
    const data = targetDiscordId
      ? await callAdmin('set-profile-rainbet', { method: 'POST', body: { discordId: targetDiscordId, rainbetUsername: next } })
      : await callAdmin('entry-set-rainbet', { method: 'POST', body: { username: winner.username, provider: winner.provider, enteredAt: winner.enteredAt, rainbetUsername: next } });
    if (data.success) { setMessage({ type: 'success', text: `Rainbet username set to "${data.rainbetUsername}".` }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not update.' });
    setBusy(false);
  };
  const editPersonRainbet = async (person) => {
    const next = window.prompt(`Set/override the Rainbet username linked to ${person.username} (${person.platform}):`, person.rainbetUsername || '');
    if (next === null) return;
    setBusy(true);
    const data = await callAdmin('set-profile-rainbet', { method: 'POST', body: { discordId: person.discordId, rainbetUsername: next } });
    if (data.success) { setMessage({ type: 'success', text: `Rainbet username set to "${data.rainbetUsername}".` }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not update.' });
    setBusy(false);
  };
  const recheckDiscordRole = async (person) => {
    if (!person.discordId) { setMessage({ type: 'error', text: 'No Discord ID on file for this entry.' }); return; }
    setMessage({ type: 'success', text: `Rechecking ${person.username}'s Discord status live...` });
    const data = await callAdmin('recheck-discord', { method: 'POST', body: { discordId: person.discordId } });
    if (data.success) {
      const d = data.debugInfo;
      const detail = d ? ` (role "${d.lookingForRoleName}" ${d.roleIdFound ? 'found in your server' : 'NOT found in your server — check the name'}, they have ${d.memberRoleIds?.length ?? 0} role${d.memberRoleIds?.length === 1 ? '' : 's'} total, member fetch status ${d.memberFetchStatus})` : '';
      setMessage({
        type: 'success',
        text: `${person.username} — ${data.inGuild ? 'in the server' : 'NOT in the server'}, ${data.isVerified ? 'has the verified role ✅' : 'does NOT have the verified role ❌'}.${detail}`,
      });
      loadAll();
    } else {
      setMessage({ type: 'error', text: data.error || 'Could not recheck right now.' });
    }
  };
  const refreshDiscordName = async (person) => {
    if (!person.discordId) return;
    setRefreshingName(person.discordId);
    const data = await callAdmin('refresh-discord-info', { method: 'POST', body: { discordId: person.discordId } });
    if (data.success) { setMessage({ type: 'success', text: `Got it — ${data.discordUsername}.` }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not fetch their Discord info.' });
    setRefreshingName(null);
  };
  const refreshAllEligibility = async () => {
    setRefreshingAll(true);
    setMessage({ type: 'success', text: 'Checking everyone live against Discord — this can take a bit for a lot of people...' });
    const data = await callAdmin('refresh-all-eligibility', { method: 'POST' });
    if (data.success) {
      setLastEligibilityRefresh(new Date().toLocaleTimeString());
      setMessage({ type: 'success', text: `Checked ${data.checked} accounts live — ${data.verifiedCount} currently verified. Every view is showing this exact snapshot now.` });
      await loadAll();
    } else {
      setMessage({ type: 'error', text: data.error || 'Could not refresh everyone right now.' });
    }
    setRefreshingAll(false);
  };
  const refreshAllPeople = async () => {
    setRefreshingAllPeople(true);
    setMessage({ type: 'success', text: 'Checking everyone live and fetching any missing Discord names — this can take a bit...' });
    const data = await callAdmin('refresh-all-people', { method: 'POST' });
    if (data.success) {
      setLastPeopleRefresh(`Last: ${new Date().toLocaleTimeString()} — ${data.checked} checked, ${data.verifiedCount} verified, ${data.namesFixed} name${data.namesFixed === 1 ? '' : 's'} fixed`);
      setMessage({ type: 'success', text: `Checked ${data.checked} accounts live, ${data.verifiedCount} currently verified, filled in ${data.namesFixed} missing Discord name${data.namesFixed === 1 ? '' : 's'}.` });
      await loadAll();
    } else {
      setMessage({ type: 'error', text: data.error || 'Could not refresh everyone right now.' });
    }
    setRefreshingAllPeople(false);
  };
  const blacklistAccount = async (discordId, label, reason) => {
    if (!discordId) { setMessage({ type: 'error', text: 'No Discord ID to blacklist.' }); return; }
    if (!window.confirm(`Blacklist ${label}? They'll never be eligible to win a giveaway or slot pick again, even if they link a fresh Twitch/Kick account to the same Discord.`)) return;
    setBlacklisting(discordId);
    const data = await callAdmin('blacklist-add', { method: 'POST', body: { discordId, label, reason: reason || 'Manually blacklisted' } });
    if (data.success) { setMessage({ type: 'success', text: `${label} blacklisted.` }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not blacklist that account.' });
    setBlacklisting(null);
  };
  const removeFromBlacklist = async (discordId, label) => {
    if (!window.confirm(`Remove ${label} from the blacklist? They'll be eligible to win again.`)) return;
    setBlacklisting(discordId);
    const data = await callAdmin('blacklist-remove', { method: 'POST', body: { discordId } });
    if (data.success) { setMessage({ type: 'success', text: `${label} removed from the blacklist.` }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not update that.' });
    setBlacklisting(null);
  };

  // Adds this winner to the separate Giveaway Portal ledger, so they can come claim it
  // themselves (proving identity) and Dankquan can track paid/unpaid independently of the
  // wager-rewards claims system.
  const addToGiveawayPortal = async (winner, eventType, eventTitle) => {
    const amountStr = window.prompt(`Prize amount for ${winner.username} ($):`, '');
    if (amountStr === null) return;
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) { setMessage({ type: 'error', text: 'Enter a valid amount above 0.' }); return; }
    setBusy(true);
    const data = await callAdmin('giveaway-claim-add', {
      method: 'POST',
      body: { username: winner.username, provider: winner.provider, amount, eventType, eventTitle, wonAt: winner.wonAt },
    });
    if (data.success) { setMessage({ type: 'success', text: `Added ${money(amount)} to the Giveaway Portal for ${winner.username}.` }); loadAll(); }
    else setMessage({ type: 'error', text: data.error || 'Could not add to the portal.' });
    setBusy(false);
  };

  const manualKey = () => `${manualClaimProvider}:${manualClaimName.trim().toLowerCase()}`;

  // Looks the typed winner up against everything the site knows (Discord link, server role,
  // Rainbet, blacklist, past claims, shared connection, account age) before they're added.
  const runWinnerCheck = async () => {
    if (!manualClaimName.trim()) { setMessage({ type: 'error', text: 'Enter their username.' }); return null; }
    setCheckingWinner(true);
    setManualCheck(null);
    const data = await callAdmin('winner-check', { method: 'POST', body: { username: manualClaimName.trim(), provider: manualClaimProvider } });
    setCheckingWinner(false);
    if (!data.success) { setMessage({ type: 'error', text: data.error || 'Could not check that person.' }); return null; }
    const result = { ...data, for: manualKey() };
    setManualCheck(result);
    return result;
  };

  const addManualGiveawayClaim = async () => {
    const amount = Number(manualClaimAmount);
    if (!manualClaimName.trim()) { setMessage({ type: 'error', text: 'Enter their username.' }); return; }
    if (!Number.isFinite(amount) || amount <= 0) { setMessage({ type: 'error', text: 'Enter a valid amount above 0.' }); return; }

    // Always checked first. A clean (or can't-verify-yet) result goes straight through; a
    // problem stops here so the checklist gets read, and a second click means "add anyway".
    let check = manualCheck && manualCheck.for === manualKey() ? manualCheck : null;
    const alreadyReviewed = !!check;
    if (!check) {
      check = await runWinnerCheck();
      if (!check) return;
    }
    const problem = check.verdict === 'bad' || check.verdict === 'flag';
    if (problem && !alreadyReviewed) return;
    if (problem && !window.confirm(`${check.summary}\n\nAdd ${manualClaimName.trim()} to the portal anyway?`)) return;

    setSavingManualClaim(true);
    const data = await callAdmin('giveaway-claim-add', {
      method: 'POST',
      body: { username: manualClaimName.trim(), provider: manualClaimProvider, amount, eventType: 'giveaway', eventTitle: manualClaimTitle.trim() || 'Giveaway', adminOverride: check.verdict === 'bad' },
    });
    if (data.success) {
      setManualClaimName(''); setManualClaimAmount(''); setManualClaimTitle(''); setManualCheck(null);
      setMessage({ type: 'success', text: `Added ${money(amount)} to the Giveaway Portal for ${manualClaimName.trim()}.` });
      loadAll();
    } else setMessage({ type: 'error', text: data.error || 'Could not add to the portal.' });
    setSavingManualClaim(false);
  };

  const markGiveawayClaim = async (id, status) => {
    const data = await callAdmin('giveaway-claim-mark', { method: 'POST', body: { id, status } });
    if (data.success) loadAll();
    else setMessage({ type: 'error', text: data.error || 'Could not update that.' });
  };

  const deleteGiveawayClaim = async (row) => {
    if (!window.confirm(`Remove the ${money(row.amount)} portal entry for ${row.username}? ${row.status === 'paid' ? 'This also removes it from the payout total.' : ''}`)) return;
    const data = await callAdmin('giveaway-claim-delete', { method: 'POST', body: { id: row.id } });
    if (data.success) loadAll();
    else setMessage({ type: 'error', text: data.error || 'Could not remove that.' });
  };

  const winnerName = giveaway?.winner?.username;
  useEffect(() => { if (winnerName) setPayoutName(winnerName); }, [winnerName]);

  const addPayout = async () => {
    const amount = Number(payoutAmount);
    if (!payoutName.trim()) { setMessage({ type: 'error', text: 'Who got paid?' }); return; }
    if (!Number.isFinite(amount) || amount <= 0) { setMessage({ type: 'error', text: 'Enter an amount above 0.' }); return; }
    setSavingPayout(true);
    const data = await callAdmin('payout-add', { method: 'POST', body: { username: payoutName.trim(), amount, note: payoutNote } });
    if (data.success) {
      setPayouts(data); setPayoutName(''); setPayoutAmount(''); setPayoutNote('');
      setMessage({ type: 'success', text: `Recorded ${money(data.row.amount)} to ${data.row.username}. Total is now ${money(data.totalPaid)}.` });
    } else setMessage({ type: 'error', text: data.error || 'Could not record that payout.' });
    setSavingPayout(false);
  };
  const deletePayout = async (row) => {
    if (!window.confirm(`Remove the ${money(row.amount)} payout to ${row.username}? The total will go down by that amount.`)) return;
    const data = await callAdmin('payout-delete', { method: 'POST', body: { id: row.id } });
    if (data.success) {
      setPayouts(p => ({ ...p, ...data, ledger: (p?.ledger || []).filter(r => r.id !== row.id) }));
      setMessage({ type: 'success', text: `Removed. Total is now ${money(data.totalPaid)}.` });
    } else setMessage({ type: 'error', text: data.error || 'Could not remove that payout.' });
  };
  const importPayouts = async (replace) => {
    if (!importText.trim()) return;
    if (replace && !window.confirm('Replace the whole ledger with this list? Everything currently recorded is deleted.')) return;
    setImporting(true);
    const data = await callAdmin('payout-import', { method: 'POST', body: { text: importText, replace: !!replace } });
    if (data.success) {
      setImportText('');
      setMessage({ type: 'success', text: `Imported ${data.imported} payouts${data.skippedCount ? `, skipped ${data.skippedCount} unreadable lines` : ''}. Total is ${money(data.totalPaid)}.` });
      const fresh = await callAdmin('payout-list');
      if (typeof fresh?.totalPaid === 'number') setPayouts(fresh);
    } else setMessage({ type: 'error', text: data.error || 'Could not import that list.' });
    setImporting(false);
  };
  const fixPayoutNames = async () => {
    setFixingPayoutNames(true);
    const data = await callAdmin('payout-fix-names', { method: 'POST' });
    if (data.success) {
      setMessage({ type: 'success', text: data.fixed > 0 ? `Fixed ${data.fixed} of ${data.total} ledger rows to show their Twitch/Kick name.` : `Checked all ${data.total} rows — nothing needed fixing.` });
      const fresh = await callAdmin('payout-list');
      if (typeof fresh?.totalPaid === 'number') setPayouts(fresh);
    } else setMessage({ type: 'error', text: data.error || 'Could not fix names right now.' });
    setFixingPayoutNames(false);
  };
  const mergePayoutNames = async () => {
    if (!window.confirm(`Rename every ledger row currently showing "${mergeFromName.trim()}" to "${mergeToName.trim()}"? This can't be undone automatically — the old name is kept on each row, but you'd need to merge back manually if this was a mistake.`)) return;
    setMergingNames(true);
    const data = await callAdmin('payout-merge-names', { method: 'POST', body: { from: mergeFromName.trim(), to: mergeToName.trim() } });
    if (data.success) {
      setMessage({ type: 'success', text: `Merged ${data.merged} ledger row${data.merged === 1 ? '' : 's'} from "${data.from}" to "${data.to}".` });
      setMergeFromName(''); setMergeToName('');
      const fresh = await callAdmin('payout-list');
      if (typeof fresh?.totalPaid === 'number') setPayouts(fresh);
    } else setMessage({ type: 'error', text: data.error || 'Could not merge those names.' });
    setMergingNames(false);
  };

  const refreshWagers = useCallback(async () => {
    const data = await callAdmin('wager-list');
    if (data?.config) setWagerData(data);
  }, [callAdmin]);

  const importWagers = async () => {
    if (!wagerImportText.trim()) return;
    setImportingWagers(true);
    const data = await callAdmin('wager-import', { method: 'POST', body: { text: wagerImportText } });
    if (data.success) {
      setWagerImportText('');
      setMessage({ type: 'success', text: `Updated ${data.updated} players${data.skippedCount ? `, skipped ${data.skippedCount} unreadable lines` : ''}.` });
      refreshWagers();
    } else setMessage({ type: 'error', text: data.error || 'Could not import that list.' });
    setImportingWagers(false);
  };
  const deleteWagerPlayer = async (username) => {
    if (!window.confirm(`Stop tracking ${username}? Their wager history here is deleted.`)) return;
    const data = await callAdmin('wager-delete', { method: 'POST', body: { username } });
    if (data.success) refreshWagers();
  };
  const resolveClaim = async (id, status) => {
    const data = await callAdmin('claim-resolve', { method: 'POST', body: { id, status } });
    if (data.success) refreshWagers();
    else setMessage({ type: 'error', text: data.error || 'Could not update that claim.' });
  };
  const recordWagerPayment = async (player) => {
    const suggested = player.available > 0 ? player.available.toFixed(2) : '';
    const amountStr = window.prompt(`How much did you send ${player.rainbetUsername} for their wager reward?`, suggested);
    if (amountStr === null) return;
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) { setMessage({ type: 'error', text: 'Enter an amount above 0.' }); return; }
    const data = await callAdmin('wager-record-payment', { method: 'POST', body: { username: player.rainbetUsername, amount } });
    if (data.success) { setMessage({ type: 'success', text: `Recorded $${amount.toFixed(2)} to ${player.rainbetUsername}.` }); refreshWagers(); }
    else setMessage({ type: 'error', text: data.error || 'Could not record that payment.' });
  };
  const editWagerClaimAmount = async (claim) => {
    const amountStr = window.prompt(`New amount for ${claim.rainbetUsername}'s ${claim.status} claim:`, claim.amount.toFixed(2));
    if (amountStr === null) return;
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) { setMessage({ type: 'error', text: 'Enter an amount above 0.' }); return; }
    const data = await callAdmin('wager-claim-edit-amount', { method: 'POST', body: { id: claim.id, amount } });
    if (data.success) { setMessage({ type: 'success', text: `Updated to $${amount.toFixed(2)}.` }); refreshWagers(); }
    else setMessage({ type: 'error', text: data.error || 'Could not update that.' });
  };
  const deleteWagerClaim = async (claim) => {
    if (!window.confirm(`Remove this $${claim.amount.toFixed(2)} ${claim.status} claim for ${claim.rainbetUsername} entirely? ${claim.status === 'paid' ? 'This also removes it from the public payout total.' : ''}`)) return;
    const data = await callAdmin('wager-claim-delete', { method: 'POST', body: { id: claim.id } });
    if (data.success) { setMessage({ type: 'success', text: 'Removed.' }); refreshWagers(); }
    else setMessage({ type: 'error', text: data.error || 'Could not remove that.' });
  };

  const saveReferralList = async () => {
    setSavingReferralList(true);
    const data = await callAdmin('set-referral-list', { method: 'POST', body: { list: referralList } });
    if (data.success) { setReferralCount(data.count); setMessage({ type: 'success', text: `Referral list saved — ${data.count} names.` }); }
    else setMessage({ type: 'error', text: data.error || 'Could not save the list.' });
    setSavingReferralList(false);
  };

  const addClip = async () => {
    if (!clipUrl.trim()) return;
    setSavingClip(true);
    const data = await callAdmin('clips-add', { method: 'POST', body: { url: clipUrl.trim(), label: clipLabel, orientation: clipOrientation } });
    if (data.success) {
      setClipUrl(''); setClipLabel('');
      setMessage({ type: 'success', text: 'Clip added.' });
      loadAll();
    } else setMessage({ type: 'error', text: data.error || 'Could not add that clip.' });
    setSavingClip(false);
  };
  const deleteClip = async (clip) => {
    if (!window.confirm('Remove this clip?')) return;
    const data = await callAdmin('clips-delete', { method: 'POST', body: { id: clip.id } });
    if (data.success) loadAll();
    else setMessage({ type: 'error', text: data.error || 'Could not remove that.' });
  };

  const saveKeyword = async () => {
    if (!chatKeywordInput.trim()) { setMessage({ type: 'error', text: 'Enter a keyword first.' }); return; }
    setSavingKeyword(true);
    const data = await callAdmin('chat-entry-set-keyword', { method: 'POST', body: { keyword: chatKeywordInput } });
    if (data.success) { setMessage({ type: 'success', text: `Keyword set to "${data.keyword}".` }); setChatEntryStatus(s => ({ ...s, keyword: data.keyword })); }
    else setMessage({ type: 'error', text: data.error || 'Could not save keyword.' });
    setSavingKeyword(false);
  };
  const subscribeChatEntry = async () => {
    setSubscribingChat(true);
    const data = await callAdmin('chat-entry-subscribe', { method: 'POST' });
    if (data.success) setMessage({ type: 'success', text: 'Chat keyword entry is live.' });
    else setMessage({ type: 'error', text: data.error || 'Could not subscribe.' });
    const fresh = await callAdmin('chat-entry-status');
    if (!fresh.error) setChatEntryStatus(fresh);
    setSubscribingChat(false);
  };
  const unsubscribeChatEntry = async () => {
    if (!window.confirm('Turn off chat keyword entry? People will need another way to enter until you turn it back on.')) return;
    setSubscribingChat(true);
    await callAdmin('chat-entry-unsubscribe', { method: 'POST' });
    const fresh = await callAdmin('chat-entry-status');
    if (!fresh.error) setChatEntryStatus(fresh);
    setSubscribingChat(false);
  };

  if (checking) return <div className="text-center text-xl py-20">Checking access...</div>;

  if (!accounts.discord) {
    return (
      <div className="max-w-md mx-auto p-6 md:p-10 text-center">
        <p className="text-gray-400">Sign in with Discord to continue.</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto p-6 md:p-10 text-center">
        <p className="text-xl mb-1">🔒 Not authorized</p>
        <p className="text-gray-400 text-sm">This page is only for Dankquan.</p>
      </div>
    );
  }

  const renderEntryRow = (e, i) => (
    <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2 text-sm">
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <EntrantIcons inGuild={e.inGuild} isVerified={e.isVerified} isVIP={e.isVIP} provider={e.provider} linked={e.linked} referralVerified={e.referralVerified} size="md" />
        <span className="font-semibold truncate">{e.username}</span>
        {e.source === 'chat' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 shrink-0">via chat</span>}
        {e.rainbetUsername && <span className="text-xs text-gray-500 truncate">· Rainbet: {e.rainbetUsername}</span>}
        {e.slotCall && <span className="text-xs text-yellow-300/80 truncate">· 🎰 {e.slotCall}</span>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-gray-500 text-xs">{new Date(e.enteredAt).toLocaleString()}</span>
        <button onClick={() => removeEntrant(e)} className="text-gray-500 hover:text-red-400 p-1" aria-label={`Remove ${e.username}`} title="Remove from the shared pool">
          <X size={14} />
        </button>
      </div>
    </div>
  );

  const liveAdminProps = {
    stream: streamMode,
            meta: { giveaway, isOpen: gPoolOpen, entryMode: gMeta.entryMode, keyword: gMeta.keyword, activeWindow: gMeta.activeWindow, entryCount: gMeta.entryCount },
            busy,
            refreshKey: entriesRefresh,
            eligible: gEligible,
            defaultKeyword: chatEntryStatus?.keyword || '',
            onCreate: createGiveaway,
            onSetEntry: setEntrySettings,
            onOpen: openPool,
            onClose: closePool,
            onRoll: rollGiveaway,
            onReroll: rerollGiveaway,
            onClearWinner: clearGiveawayWinner,
            onReset: resetGiveaway,
            onEnd: archiveGiveaway,
            onRemove: removeEntrant,
            winnerTools: {
              editRainbet: editWinnerRainbet,
              verify: (w) => setIdentityCard(w),
              addToPortal: (w) => addToGiveawayPortal(w, 'giveaway', giveaway?.title),
              checkChat: checkChatStats,
              fetchFollow: fetchWinnerFollow,
              onAnnounce: announceWinner,
              checkRainbet,
              chatStats,
              rainbetCheck,
              checkingChat: checkingChatStats,
              checkingRainbet,
            },
  };

  if (streamMode) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
        <div className="flex justify-end">
          <button onClick={() => toggleStream(false)} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-500 hover:text-gray-300 transition">Exit stream mode</button>
        </div>
        <LiveGiveaway admin={liveAdminProps} />
        <PastWinnersPanel winners={giveaway?.pastWinners || []} history={gHistory} onReopen={reopenWinner} busy={busy} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-56 shrink-0">
          <h1 className="text-3xl font-black mb-3">🛠️ Admin</h1>
          <button onClick={() => toggleStream(true)} className="w-full mb-3 px-4 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-400/30 text-red-200 hover:from-red-500/30 hover:to-pink-500/30 transition">🎥 Stream mode</button>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible bg-zinc-900/60 backdrop-blur-sm p-1.5 rounded-2xl border border-white/5">
            <button onClick={() => toggleGroup('Giveaways')} className="hidden lg:flex items-center justify-between px-4 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-300 transition text-left">
              <span>Giveaways</span><span>{collapsed['Giveaways'] ? '▸' : '▾'}</span>
            </button>
            <div className={`contents ${collapsed['Giveaways'] ? 'lg:hidden' : ''}`}>
            <button onClick={() => setView('giveaway')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition text-left whitespace-nowrap ${view === 'giveaway' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}><Gift size={16} /> Giveaway</button>
            <button onClick={() => setView('slotpicker')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition text-left whitespace-nowrap ${view === 'slotpicker' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}><Dices size={16} /> Slot Picker</button>
            <button onClick={() => setView('entrants')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition text-left whitespace-nowrap ${view === 'entrants' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}><Users size={16} /> Entrant Pool</button>
            </div>
            <span className="lg:hidden w-px bg-white/10 mx-1 shrink-0 self-stretch" />
            <button onClick={() => toggleGroup('Money & rewards')} className="hidden lg:flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-300 transition text-left">
              <span>Money & rewards</span><span>{collapsed['Money & rewards'] ? '▸' : '▾'}</span>
            </button>
            <div className={`contents ${collapsed['Money & rewards'] ? 'lg:hidden' : ''}`}>
            <button onClick={() => setView('portal')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition relative text-left whitespace-nowrap ${view === 'portal' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Ticket size={16} /> Giveaway Portal
              {claimsNeedAttention > 0 && (
                <span className="absolute -top-1.5 -right-1.5 lg:right-2 lg:top-1/2 lg:-translate-y-1/2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {claimsNeedAttention}
                </span>
              )}
            </button>
            <button onClick={() => setView('payouts')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition text-left whitespace-nowrap ${view === 'payouts' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}><Banknote size={16} /> Payouts</button>
            <button onClick={() => setView('wagers')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition relative text-left whitespace-nowrap ${view === 'wagers' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Gem size={16} /> Wagers
              {wagerData?.pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 lg:right-2 lg:top-1/2 lg:-translate-y-1/2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {wagerData.pendingCount}
                </span>
              )}
            </button>
            <button onClick={() => setView('referral')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition text-left whitespace-nowrap ${view === 'referral' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}><FileText size={16} /> Referral List</button>
            </div>
            <span className="lg:hidden w-px bg-white/10 mx-1 shrink-0 self-stretch" />
            <button onClick={() => toggleGroup('People & security')} className="hidden lg:flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-300 transition text-left">
              <span>People & security</span><span>{collapsed['People & security'] ? '▸' : '▾'}</span>
            </button>
            <div className={`contents ${collapsed['People & security'] ? 'lg:hidden' : ''}`}>
            <button onClick={() => setView('people')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition relative text-left whitespace-nowrap ${view === 'people' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <UserCheck size={16} /> People
              {peopleMultiCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 lg:right-2 lg:top-1/2 lg:-translate-y-1/2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {peopleMultiCount}
                </span>
              )}
            </button>
            <button onClick={() => setView('security')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition relative text-left whitespace-nowrap ${view === 'security' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Shield size={16} /> Security
              {securityFlaggedCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 lg:right-2 lg:top-1/2 lg:-translate-y-1/2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {securityFlaggedCount}
                </span>
              )}
            </button>
            </div>
            <span className="lg:hidden w-px bg-white/10 mx-1 shrink-0 self-stretch" />
            <button onClick={() => toggleGroup('Site')} className="hidden lg:flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-300 transition text-left">
              <span>Site</span><span>{collapsed['Site'] ? '▸' : '▾'}</span>
            </button>
            <div className={`contents ${collapsed['Site'] ? 'lg:hidden' : ''}`}>
            <button onClick={() => setView('clips')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition text-left whitespace-nowrap ${view === 'clips' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/40' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}><Film size={16} /> Clips</button>
            </div>
          </nav>
        </div>

        <div className="flex-1 min-w-0 space-y-6">

      {/* At-a-glance status — everything you'd otherwise have to click into a tab to see.
          Pool control lives here once, not duplicated inside every tab that used it. */}
      <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-4 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${gPoolOpen ? 'bg-red-500/15 text-red-300 border-red-500/25' : 'bg-white/5 text-gray-400 border-white/10'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${gPoolOpen ? 'bg-red-400 animate-pulse' : 'bg-gray-500'}`} />
          Entries {gPoolOpen ? 'OPEN' : 'CLOSED'}
        </span>
        {gPoolOpen ? (
          <button onClick={closePool} disabled={busy} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 active:scale-[0.98] transition rounded-lg text-xs font-semibold transition disabled:opacity-50">Close entries</button>
        ) : (
          <button onClick={openPool} disabled={busy} className="px-3 py-1.5 bg-green-600 hover:bg-green-500 active:scale-[0.98] rounded-lg text-xs font-bold transition disabled:opacity-50">Open entries</button>
        )}

        <span className="w-px h-5 bg-white/10 mx-1" />

        <button onClick={() => setView('giveaway')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition">
          🎁 {giveaway ? <><span className="text-gray-200 font-semibold">{giveaway.title}</span> <span className="text-gray-500">· {gMeta.entryCount} entries{giveaway.winner ? ' · has winner' : ''}</span></> : <span className="text-gray-500">No giveaway running</span>}
        </button>
        <button onClick={() => setView('slotpicker')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition">
          🎰 {slotpicker ? <><span className="text-gray-200 font-semibold">{slotpicker.title}</span> <span className="text-gray-500">· {gMeta.entryCount} entries{slotpicker.winner ? ' · has winner' : ''}</span></> : <span className="text-gray-500">No slot picker running</span>}
        </button>

        <span className="w-px h-5 bg-white/10 mx-1" />

        {claimsNeedAttention > 0 && (
          <button onClick={() => setView('portal')} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 transition font-semibold">
            🎟️ {claimsNeedAttention} prize{claimsNeedAttention === 1 ? '' : 's'} awaiting payment
          </button>
        )}
        {wagerData?.pendingCount > 0 && (
          <button onClick={() => setView('wagers')} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 transition font-semibold">
            💎 {wagerData.pendingCount} wager claim{wagerData.pendingCount === 1 ? '' : 's'} pending
          </button>
        )}

        <button onClick={() => setView('payouts')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition ml-auto">
          💸 <span className="text-green-400 font-bold">{money(payouts?.totalPaid || 0)}</span> <span className="text-gray-500">paid all-time</span>
        </button>
      </div>

      {message && (
        <div className={`text-sm px-4 py-3 rounded-xl ${message.type === 'success' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
          {message.text}
        </div>
      )}

      {view === 'giveaway' && (
        <>
          <LiveGiveaway admin={liveAdminProps} />

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/10 space-y-3">
            <button onClick={() => setChatSettingsOpen(v => !v)} className="w-full flex items-center justify-between flex-wrap gap-2 text-left">
              <h2 className="text-lg font-bold flex items-center gap-2">💬 Twitch chat connection <span className="text-gray-500 text-xs font-normal">{(chatSettingsOpen || !chatEntryStatus?.connected || !chatEntryStatus?.subscriptionId || chatEntryStatus?.revoked) ? '▾' : '▸'}</span></h2>
              {chatEntryStatus?.connected ? (
                chatEntryStatus.subscriptionId && !chatEntryStatus.revoked ? (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 font-semibold">● Live — "{chatEntryStatus.keyword}"</span>
                ) : (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-zinc-700 text-gray-400 font-semibold">○ Off</span>
                )
              ) : (
                <span className="text-xs px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-400 font-semibold">Not connected</span>
              )}
            </button>
            {(chatSettingsOpen || !chatEntryStatus?.connected || !chatEntryStatus?.subscriptionId || chatEntryStatus?.revoked) && (!chatEntryStatus?.connected ? (
              <>
                <p className="text-sm text-gray-400">Lets people type a keyword in your Twitch chat to enter the shared pool. One-time setup: authorize this site to read your Twitch chat.</p>
                {!chatEntryStatus?.eventsubSecretConfigured && (
                  <p className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/25 rounded-lg px-3 py-2">⚠️ TWITCH_EVENTSUB_SECRET isn't set in your Netlify environment variables yet — add one and redeploy before connecting.</p>
                )}
                <a href={getTwitchChatAuthUrl()} className="inline-block px-5 py-2.5 bg-gradient-to-r from-[#4f39f6] to-[#372aac] hover:from-[#615fff] hover:to-[#432dd7] active:scale-[0.98] text-white font-bold rounded-xl transition text-sm">Connect Twitch Chat Reading</a>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-400">Connected as <span className="text-gray-200 font-semibold">{chatEntryStatus.broadcasterLogin}</span>. Set a keyword and turn it on below.</p>
                {chatEntryStatus.revoked && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">⚠️ Twitch revoked chat reading access — reconnect above.</p>}
                {(chatEntryStatus.missingScopes === null || chatEntryStatus.missingScopes?.length > 0) && (
                  <p className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/25 rounded-lg px-3 py-2">
                    ⚠️ {chatEntryStatus.missingScopes === null ? "This connection predates permission-checking — it may be missing scopes." : `Missing: ${chatEntryStatus.missingScopes.join(', ')}.`} <a href={getTwitchChatAuthUrl()} className="underline hover:text-yellow-300">Reconnect</a> and approve everything.
                  </p>
                )}
                {chatEntryStatus.followAccess === false && !(chatEntryStatus.missingScopes === null || chatEntryStatus.missingScopes?.length > 0) && (
                  <p className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/25 rounded-lg px-3 py-2">
                    ⚠️ Follower access isn't saved yet, so winner cards can't show follow time. <a href={getTwitchChatAuthUrl()} className="underline hover:text-yellow-300">Reconnect</a> and approve everything.
                  </p>
                )}
                {chatEntryStatus.followAccess && <p className="text-xs text-green-400">✓ Follower access on — winner cards show how long they've followed.</p>}
                <div className="flex flex-wrap gap-2">
                  <input value={chatKeywordInput} onChange={e => setChatKeywordInput(e.target.value)} placeholder="e.g. danky" maxLength={32} className="flex-1 min-w-[140px] bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500" />
                  <button onClick={saveKeyword} disabled={savingKeyword} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 active:scale-[0.98] transition rounded-xl text-sm font-semibold transition disabled:opacity-50">{savingKeyword ? 'Saving...' : 'Save keyword'}</button>
                  {chatEntryStatus.subscriptionId && !chatEntryStatus.revoked ? (
                    <button onClick={unsubscribeChatEntry} disabled={subscribingChat} className="px-4 py-2.5 bg-red-900/40 hover:bg-red-900/60 border border-red-500/30 rounded-xl text-sm font-semibold transition disabled:opacity-50">Turn off</button>
                  ) : (
                    <button onClick={subscribeChatEntry} disabled={subscribingChat || !chatKeywordInput.trim()} className="px-4 py-2.5 bg-green-600 hover:bg-green-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{subscribingChat ? 'Starting...' : 'Turn on'}</button>
                  )}
                </div>
                <p className="text-xs text-gray-500">Feeds the shared pool used by both Giveaway and Slot Picker — only counts once per person, even if they type it repeatedly.</p>
              </>
            ))}
          </div>

          <PastWinnersPanel winners={giveaway?.pastWinners || []} history={gHistory} onReopen={reopenWinner} busy={busy} />
        </>
      )}

      {view === 'slotpicker' && (
        <>
          {slotpicker?.winner && reveal.show && reveal.feature === 'slotpicker' && !reveal.done && (
            <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-yellow-500/30">
              <p className="text-center text-xs text-gray-400 uppercase tracking-wide mb-2">Picking...</p>
              <SlotWheel pool={reveal.pool} winner={reveal.winner || slotpicker.winner} onDone={() => setReveal(r => ({ ...r, done: true }))} />
            </div>
          )}

          {slotpicker?.winner && (!reveal.show || reveal.feature !== 'slotpicker' || reveal.done) && (
            <div className="bg-gradient-to-r from-yellow-500/15 to-transparent rounded-2xl p-6 border border-yellow-500/30 space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🏆</span>
                <div>
                  <p className="text-xs text-gray-400">Picked — {slotpicker.title}</p>
                  <p className="font-bold text-yellow-400 text-lg flex items-center gap-2 flex-wrap">
                    {slotpicker.winner.username} <span className="text-sm text-gray-400 font-normal">({slotpicker.winner.provider})</span>
                    <EntrantIcons inGuild={slotpicker.winner.inGuild} isVerified={slotpicker.winner.isVerified} isVIP={slotpicker.winner.isVIP} provider={slotpicker.winner.provider} linked={slotpicker.winner.linked} referralVerified={slotpicker.winner.referralVerified} size="md" />
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {slotpicker.winner.rainbetUsername ? <p className="text-xs text-gray-400">Rainbet: <span className="text-gray-200">{slotpicker.winner.rainbetUsername}</span></p> : <p className="text-xs text-gray-500 italic">No Rainbet username linked</p>}
                    <button onClick={() => editWinnerRainbet(slotpicker.winner)} className="text-xs text-indigo-400 hover:text-indigo-300 underline">✏️ Edit</button>
                    <button onClick={() => setIdentityCard(slotpicker.winner)} className="text-xs text-indigo-400 hover:text-indigo-300 underline">🔍 Verify</button>
                    <button onClick={() => addToGiveawayPortal(slotpicker.winner, 'slotpicker', slotpicker.title)} className="text-xs text-green-400 hover:text-green-300 underline">➕ Add to Giveaway Portal</button>
                  </div>
                  {slotpicker.winner.slotCall && <p className="text-sm text-yellow-200 mt-1">🎰 Requested slot: <span className="font-semibold">{slotpicker.winner.slotCall}</span></p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(slotpicker.entries || []).length > 0 && (
                  <button onClick={rerollSlotpicker} disabled={busy} className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 hover:opacity-90 rounded-xl text-sm font-bold transition disabled:opacity-50">🔁 Pick Again</button>
                )}
                <button onClick={clearSlotpickerWinner} disabled={busy} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 active:scale-[0.98] transition rounded-xl text-sm font-semibold transition disabled:opacity-50">🧹 Clear Pick</button>
              </div>
            </div>
          )}

          {!slotpicker ? (
            <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/10 space-y-3">
              <h2 className="text-lg font-bold">Start a Slot Picker</h2>
              <input value={spTitle} onChange={e => setSpTitle(e.target.value)} placeholder="Title" className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
              <textarea value={spDescription} onChange={e => setSpDescription(e.target.value)} placeholder="Description (optional)" rows={2} className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
              <button onClick={createSlotpicker} disabled={busy || !spTitle.trim()} className="px-6 py-3 bg-gradient-to-r from-[#4f39f6] to-[#372aac] hover:from-[#615fff] hover:to-[#432dd7] active:scale-[0.98] text-white font-bold rounded-xl transition disabled:opacity-50">Create &amp; Open Pool</button>
            </div>
          ) : (
            <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/10 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">{slotpicker.title}</h2>
                  {slotpicker.description && <p className="text-gray-400 text-sm">{slotpicker.description}</p>}
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${spPoolOpen ? 'bg-red-500/15 text-red-300 border-red-500/25' : 'bg-white/5 text-gray-400 border-white/10'}`}><span className={`w-1.5 h-1.5 rounded-full ${spPoolOpen ? 'bg-red-400 animate-pulse' : 'bg-gray-500'}`} />{spPoolOpen ? 'OPEN' : 'CLOSED'}</span>
              </div>

              {(slotpicker.entries || []).length > 0 && (
                <ParticipantsCarousel participants={slotpicker.entries.map(e => ({ username: e.username, provider: e.provider, avatar: e.avatar || null, inGuild: e.inGuild, isVerified: e.isVerified, isVIP: e.isVIP, linked: e.linked, referralVerified: e.referralVerified }))} />
              )}

              <div className="flex flex-wrap gap-2">
                <button onClick={rollSlotpicker} disabled={busy || (slotpicker.entries || []).length === 0} className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 hover:opacity-90 rounded-xl text-sm font-bold transition disabled:opacity-50">🎲 Pick</button>
                <button onClick={archiveSlotpicker} disabled={busy} className="px-4 py-2 bg-red-900/40 hover:bg-red-900/60 border border-red-500/30 rounded-xl text-sm font-semibold transition disabled:opacity-50">Archive</button>
              </div>

              <div>
                <p className="text-sm text-gray-400 font-semibold mb-2">{(slotpicker.entries || []).length} entries in the shared pool</p>
                <div className="max-h-96 overflow-y-auto space-y-1">
                  {(slotpicker.entries || []).map(renderEntryRow)}
                  {(slotpicker.entries || []).length === 0 && <p className="text-gray-500 text-sm py-4 text-center">No entries yet — manage the pool from the "Entrant Pool" tab.</p>}
                </div>
              </div>
            </div>
          )}

          {spHistory.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-gray-400 mb-2 uppercase tracking-wide">Past Slot Picker Events</h2>
              <div className="space-y-2">
                {spHistory.map((g, i) => (
                  <div key={i} className="flex items-center justify-between bg-zinc-800/30 backdrop-blur-sm rounded-xl px-4 py-3 text-sm border border-white/5">
                    <div><p className="font-semibold">{g.title}</p><p className="text-gray-500 text-xs">{g.entryCountAtArchive || 0} entries</p></div>
                    {g.winner ? <span className="text-yellow-400 font-semibold">🏆 {g.winner.username} ({g.winner.provider})</span> : <span className="text-gray-500 text-xs">No pick made</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {view === 'entrants' && (
        <div className="space-y-4">
          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/10 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-bold">👥 Shared Entrant Pool</h2>
                <p className="text-sm text-gray-400">Feeds both the Giveaway and the Slot Picker — one list, one open/closed state.</p>
              </div>
              <span className={`text-xs px-3 py-1.5 rounded-full font-semibold ${gPoolOpen ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-gray-400'}`}>{gPoolOpen ? '● Open' : '○ Closed'}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {gPoolOpen ? (
                <button onClick={closePool} disabled={busy} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 active:scale-[0.98] transition rounded-xl text-sm font-semibold transition disabled:opacity-50">Close entries</button>
              ) : (
                <button onClick={openPool} disabled={busy} className="px-4 py-2 bg-green-600 hover:bg-green-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">Open entries</button>
              )}
              <button onClick={clearPool} disabled={busy} className="px-4 py-2 bg-red-900/40 hover:bg-red-900/60 border border-red-500/30 rounded-xl text-sm font-semibold transition disabled:opacity-50">🧹 Clear entire pool</button>
            </div>
            <div className="max-h-[28rem] overflow-y-auto space-y-1">
              {(giveaway?.entries || []).length === 0 ? (
                <p className="text-gray-500 text-sm py-4 text-center">No entries in the pool right now.</p>
              ) : (giveaway.entries || []).map(renderEntryRow)}
            </div>
            <p className="text-xs text-gray-500 border-t border-zinc-700 pt-3">
              🦄 = in your Discord server · 👑 = VIP ($15,000+ lifetime wagered) · ✓ = Twitch/Kick has been linked to a Discord account on the site (so their Rainbet link is known even if they only ever typed the keyword in chat). Removing someone here removes them from both the Giveaway and the Slot Picker.
              {' '}Full Twitch/Discord/Rainbet directory is on the <button onClick={() => setView('people')} className="text-indigo-400 hover:text-indigo-300 underline inline-flex items-center gap-1"><UserCheck size={12} /> People</button> tab.
            </p>
          </div>
        </div>
      )}

      {view === 'people' && (() => {
        const q = peopleSearch.trim().toLowerCase();
        let filtered = q
          ? people.filter(p =>
              p.username?.toLowerCase().includes(q) ||
              p.discordUsername?.toLowerCase().includes(q) ||
              p.discordTag?.toLowerCase().includes(q) ||
              p.rainbetUsername?.toLowerCase().includes(q) ||
              p.discordId?.includes(q)
            )
          : people;
        if (peopleFlaggedOnly) filtered = filtered.filter(p => p.linkedAccountCount > 1 || p.sharedIpCount > 0);
        return (
          <div className="space-y-4">
          <button onClick={refreshAllPeople} disabled={refreshingAllPeople} className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/15 transition text-sm disabled:opacity-60">
            <span className="font-semibold text-cyan-300">🔄 {refreshingAllPeople ? 'Checking everyone and fetching missing names...' : "Refresh everyone's eligibility + fix missing names"}</span>
            <span className="text-cyan-400/70 text-xs">{lastPeopleRefresh || 'Live check against Discord for everyone linked'}</span>
          </button>
          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl p-6 border border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold">🪪 Connected accounts <span className="text-gray-500 font-normal text-sm">({filtered.length}{(q || peopleFlaggedOnly) ? ` of ${people.length}` : ''})</span></h2>
              <input
                value={peopleSearch}
                onChange={e => setPeopleSearch(e.target.value)}
                placeholder="Search Twitch, Discord, or Rainbet name..."
                className="min-w-[220px] bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <p className="text-xs text-gray-500">Everyone who's ever had a streaming account + Discord connected on the site at the same time — the record that lets a chat-only "danky" entry still resolve to a real, verified person.</p>

            {peopleMultiCount > 0 && (
              <button onClick={() => setPeopleFlaggedOnly(v => !v)} className={`w-full text-left text-sm px-4 py-3 rounded-xl border transition ${peopleFlaggedOnly ? 'bg-red-500/20 border-red-500/40 text-red-200' : 'bg-red-500/10 border-red-500/25 text-red-300 hover:bg-red-500/15'}`}>
                ⚠️ {peopleMultiCount} Discord account{peopleMultiCount === 1 ? ' is' : 's are'} linked to more than one Twitch/Kick handle — possible multi-accounting. {peopleFlaggedOnly ? 'Showing flagged only — click to show everyone.' : 'Click to show flagged only.'}
              </button>
            )}

            {peopleSharedIpCount > 0 && (
              <div className="text-sm px-4 py-3 rounded-xl border bg-orange-500/10 border-orange-500/25 text-orange-300">
                🌐 {peopleSharedIpCount} account{peopleSharedIpCount === 1 ? ' has' : 's have'} connected from the same place as another account (shown as 🌐 on their row).
                <span className="block text-xs text-orange-300/70 mt-1">Worth a look, but not proof on its own — shared wifi, families, roommates and phone hotspots all set this off legitimately.</span>
              </div>
            )}

            {filtered.length === 0 ? (
              <p className="text-gray-500 text-sm py-6 text-center">{q || peopleFlaggedOnly ? 'No matches.' : 'No connected accounts recorded yet.'}</p>
            ) : (
              <div className="max-h-[36rem] overflow-y-auto">
                <div className="grid grid-cols-[1fr_1fr_1fr_auto_auto_auto_auto] gap-2 px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 border-b border-white/10 sticky top-0 bg-zinc-800/90">
                  <span>Twitch / Kick</span>
                  <span>Discord</span>
                  <span>Rainbet</span>
                  <span>Eligible</span>
                  <span>Linked</span>
                  <span></span>
                  <span></span>
                </div>
                {filtered.map((p, i) => {
                  const flagged = p.linkedAccountCount > 1;
                  const ipFlagged = p.sharedIpCount > 0;
                  return (
                    <div key={i} className={`grid grid-cols-[1fr_1fr_1fr_auto_auto_auto_auto] gap-2 items-center px-3 py-2.5 text-sm border-b border-white/[0.04] ${flagged ? 'bg-red-500/[0.06] hover:bg-red-500/10' : ipFlagged ? 'bg-orange-500/[0.05] hover:bg-orange-500/10' : 'hover:bg-white/[0.03]'}`}>
                      <span className="flex items-center gap-1.5 min-w-0 truncate">
                        <ProviderIcon provider={p.platform} size={14} /> {p.username}
                      </span>
                      <div className="min-w-0">
                        <span className="flex items-center gap-1.5 truncate text-gray-300">
                          {p.discordUsername || <span className="text-gray-600 italic">unknown</span>}
                          {flagged && <span title={`Also linked as: ${p.otherAccounts.join(', ')}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 shrink-0">⚠️ {p.linkedAccountCount}x</span>}
                          {ipFlagged && <span title={`Same connection or browser as: ${p.sharedIpWith.join(', ')}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30 shrink-0">🌐 {p.sharedIpCount}</span>}
                        </span>
                        {p.discordId && <span className="text-gray-600 text-xs block truncate">{p.discordId}</span>}
                        {flagged && <span className="text-red-400/80 text-xs block truncate">also: {p.otherAccounts.join(', ')}</span>}
                        {ipFlagged && <span className="text-orange-400/80 text-xs block truncate">same connection/browser as: {p.sharedIpWith.join(', ')}</span>}
                      </div>
                      <span className="min-w-0 truncate">
                        {p.rainbetUsername ? <span className="text-green-400">{p.rainbetUsername}</span> : <span className="text-gray-600 italic">none</span>}
                      </span>
                      <span className="shrink-0" title={p.blacklisted ? 'Blacklisted' : p.eligibleToWin ? 'Verified, in your server, not blacklisted — can win' : p.isVerified === false ? "Doesn't have the verified role" : 'Unknown'}>
                        {p.blacklisted ? <span className="text-red-400 font-bold">🚫</span> : p.eligibleToWin ? <span className="text-green-400 font-bold">✅</span> : <span className="text-gray-500 font-bold">❌</span>}
                      </span>
                      <span className="text-gray-500 text-xs whitespace-nowrap">{new Date(p.linkedAt).toLocaleDateString()}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => editPersonRainbet(p)} className="text-xs text-indigo-400 hover:text-indigo-300 underline" title="Edit their linked Rainbet username">✏️ Edit</button>
                        {!p.discordUsername && p.discordId && (
                          <button onClick={() => refreshDiscordName(p)} disabled={refreshingName === p.discordId} className="text-xs text-yellow-400 hover:text-yellow-300 underline disabled:opacity-50" title="Fetch their current Discord username directly from Discord">{refreshingName === p.discordId ? '...' : '🔄 Get name'}</button>
                        )}
                      </div>
                      <button onClick={() => recheckDiscordRole(p)} className="text-xs text-cyan-400 hover:text-cyan-300 underline shrink-0" title="Force a live recheck against Discord right now, skipping any cache">🔄 Recheck</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        );
      })()}

      {view === 'portal' && (
        <div className="space-y-4">
          <button onClick={refreshAllEligibility} disabled={refreshingAll} className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/15 transition text-sm disabled:opacity-60">
            <span className="font-semibold text-cyan-300">🔄 {refreshingAll ? 'Checking everyone live against Discord...' : 'Refresh everyone\'s eligibility now'}</span>
            <span className="text-cyan-400/70 text-xs">{lastEligibilityRefresh ? `Last refreshed ${lastEligibilityRefresh}` : 'Do this right before paying out — clears any stale cache'}</span>
          </button>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-yellow-500/10 to-zinc-800/50 backdrop-blur-sm rounded-2xl border border-yellow-500/25 p-6 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-yellow-400/80 mb-1">Owed, unpaid</p>
              <p className="text-3xl font-black tabular-nums text-yellow-400">{money(claimsTotalUnpaid)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500/10 to-zinc-800/50 backdrop-blur-sm rounded-2xl border border-green-500/25 p-6 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-green-400/80 mb-1">Paid through the portal</p>
              <p className="text-3xl font-black tabular-nums text-green-400">{money(claimsTotalPaid)}</p>
            </div>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">➕ Add someone to the portal</h2>
            <p className="text-xs text-gray-500">For winners picked outside the site (Nightbot, manual picks, anything) — this adds them straight to the portal so they can come claim it and you can track paid/unpaid, exactly like a winner rolled here would.</p>
            <div className="flex flex-wrap gap-2">
              <input value={manualClaimName} onChange={e => { setManualClaimName(e.target.value); setManualCheck(null); }} placeholder="Twitch/Kick username" className="flex-1 min-w-[160px] bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
              <select value={manualClaimProvider} onChange={e => { setManualClaimProvider(e.target.value); setManualCheck(null); }} className="bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="twitch">Twitch</option>
                <option value="kick">Kick</option>
              </select>
              <input value={manualClaimAmount} onChange={e => setManualClaimAmount(e.target.value)} inputMode="decimal" placeholder="Amount" className="w-28 bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <input value={manualClaimTitle} onChange={e => setManualClaimTitle(e.target.value)} placeholder="What they won (optional) — e.g. Nightbot giveaway" className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />

            {manualCheck && manualCheck.for === manualKey() && (
              <div className={`rounded-xl border p-4 space-y-1.5 ${manualCheck.verdict === 'bad' ? 'border-red-500/40 bg-red-500/[0.07]' : manualCheck.verdict === 'flag' ? 'border-yellow-500/40 bg-yellow-500/[0.07]' : manualCheck.verdict === 'ok' ? 'border-green-500/30 bg-green-500/[0.06]' : 'border-white/10 bg-white/[0.03]'}`}>
                {manualCheck.checks.map(c => (
                  <div key={c.key} className="flex items-start gap-2 text-sm">
                    <span className="shrink-0 w-5 text-center">{c.status === 'ok' ? '✅' : c.status === 'bad' ? '❌' : c.status === 'flag' ? '🚩' : '➖'}</span>
                    <div className="min-w-0">
                      <span className={c.status === 'bad' ? 'text-red-300 font-semibold' : c.status === 'flag' ? 'text-yellow-300 font-semibold' : 'text-gray-300'}>{c.label}</span>
                      {c.detail && <span className="text-gray-500"> — {c.detail}</span>}
                    </div>
                  </div>
                ))}
                <p className={`text-sm font-bold pt-2 ${manualCheck.verdict === 'bad' ? 'text-red-300' : manualCheck.verdict === 'flag' ? 'text-yellow-300' : manualCheck.verdict === 'ok' ? 'text-green-400' : 'text-gray-400'}`}>
                  {manualCheck.verdict === 'bad' ? '⛔ ' : manualCheck.verdict === 'flag' ? '⚠️ ' : manualCheck.verdict === 'ok' ? '✅ ' : '➖ '}{manualCheck.summary}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button onClick={runWinnerCheck} disabled={checkingWinner || savingManualClaim || !manualClaimName.trim()} className="px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{checkingWinner ? 'Checking...' : '🔍 Check'}</button>
              <button onClick={addManualGiveawayClaim} disabled={savingManualClaim || checkingWinner || !manualClaimName.trim() || !manualClaimAmount.trim()} className={`px-5 py-2.5 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50 ${manualCheck && manualCheck.for === manualKey() && (manualCheck.verdict === 'bad' || manualCheck.verdict === 'flag') ? 'bg-yellow-600 hover:bg-yellow-500' : 'bg-indigo-600 hover:bg-indigo-500'}`}>{savingManualClaim ? 'Adding...' : manualCheck && manualCheck.for === manualKey() && (manualCheck.verdict === 'bad' || manualCheck.verdict === 'flag') ? 'Add anyway' : 'Add to portal'}</button>
            </div>
          </div>

          {claimsNeedAttention > 0 && (
            <div className="text-sm px-4 py-3 rounded-xl bg-red-500/15 text-red-300 border border-red-500/30">
              {claimsNeedAttention} {claimsNeedAttention === 1 ? 'prize has' : 'prizes have'} been claimed and are waiting on payment.
            </div>
          )}

          {(() => {
            const paidRows = giveawayClaims.filter(c => c.status === 'paid');
            const claimedUnpaidRows = giveawayClaims.filter(c => c.status !== 'paid' && c.claimed);
            const unclaimedRows = giveawayClaims.filter(c => c.status !== 'paid' && !c.claimed);

            const renderClaimRow = (c) => (
              <div key={c.id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 text-sm flex-wrap gap-2">
                <div className="min-w-0">
                  <p className="font-semibold flex items-center gap-1.5 flex-wrap">
                    {c.eventType === 'slotpicker' ? '🎰' : '🎁'} {c.username}
                    <span className="text-green-400 font-bold">{money(c.amount)}</span>
                    <EntrantIcons inGuild={c.inGuild} isVerified={c.isVerified} isVIP={c.isVIP} provider={c.provider} linked={c.linked} referralVerified={c.referralVerified} size="md" />
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {c.eventTitle || (c.eventType === 'slotpicker' ? 'Slot Picker' : 'Giveaway')} · won {new Date(c.wonAt).toLocaleDateString()}
                    {c.claimed && <span className="text-yellow-400"> · claimed as {c.rainbetUsername || 'unknown'}</span>}
                    {!c.claimed && <span> · not yet claimed by winner</span>}
                  </p>
                  {!c.linked && (
                    <p className="text-xs text-red-400 mt-0.5">⚠️ No Discord linked on file — can't confirm this is a real, unique account.</p>
                  )}
                  {c.sharedIpCount > 0 && (
                    <p className="text-xs text-orange-400 mt-0.5">🌐 Connected from the same place or browser as {c.sharedIpCount} other account{c.sharedIpCount === 1 ? '' : 's'} — worth checking before paying.</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setIdentityCard(c)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-white transition">🔍 Check</button>
                  {c.status === 'paid' ? (
                    <button onClick={() => markGiveawayClaim(c.id, 'unpaid')} className="text-xs px-3 py-1.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 transition">✓ Paid</button>
                  ) : (
                    <button onClick={() => markGiveawayClaim(c.id, 'paid')} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-white transition">Mark paid</button>
                  )}
                  <button onClick={() => deleteGiveawayClaim(c)} className="text-gray-500 hover:text-red-400 p-1" aria-label={`Remove ${c.username}'s portal entry`}><X size={14} /></button>
                </div>
              </div>
            );

            return (
              <>
                <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-yellow-500/25 p-6 space-y-3">
                  <h2 className="font-semibold text-yellow-300">⏳ Claimed, awaiting payment <span className="text-yellow-400/70 font-normal text-sm">({claimedUnpaidRows.length})</span></h2>
                  <p className="text-xs text-gray-500">The winner has proven who they are and is waiting on you — this is the list that actually needs your attention.</p>
                  {claimedUnpaidRows.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">Nothing waiting on you right now.</p>
                  ) : (
                    <div className="max-h-[24rem] overflow-y-auto space-y-1.5">{claimedUnpaidRows.map(renderClaimRow)}</div>
                  )}
                </div>

                <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
                  <h2 className="font-semibold">📬 Not yet claimed <span className="text-gray-500 font-normal text-sm">({unclaimedRows.length})</span></h2>
                  <p className="text-xs text-gray-500">Sitting in the portal, waiting on the winner to come claim it. Nothing for you to do here yet.</p>
                  {unclaimedRows.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">Nothing waiting on a winner right now.</p>
                  ) : (
                    <div className="max-h-[24rem] overflow-y-auto space-y-1.5">{unclaimedRows.map(renderClaimRow)}</div>
                  )}
                </div>

                <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
                  <h2 className="font-semibold">✅ Paid <span className="text-gray-500 font-normal text-sm">({paidRows.length})</span></h2>
                  {paidRows.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">Nothing paid out through the portal yet.</p>
                  ) : (
                    <div className="max-h-[24rem] overflow-y-auto space-y-1.5">{paidRows.map(renderClaimRow)}</div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {view === 'payouts' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-green-500/10 to-zinc-800/60 rounded-2xl border border-green-500/25 p-6 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-green-400/80 mb-1">Total paid out</p>
            <p className="text-4xl font-black tabular-nums text-green-400">{money(payouts?.totalPaid || 0)}</p>
            <p className="text-sm text-gray-400 mt-1">{payouts?.payoutCount || 0} payouts · {payouts?.winnerCount || 0} people</p>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">➕ Add a payout</h2>
            <p className="text-xs text-gray-500">For anything paid outside a rolled giveaway. After a roll, use the Record button on the winner card instead — it's the same ledger.</p>
            <div className="flex flex-wrap gap-2">
              <input value={payoutName} onChange={e => setPayoutName(e.target.value)} className="flex-1 min-w-[140px] bg-zinc-900 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-500" placeholder="Username" />
              <input value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)} onKeyDown={e => e.key === 'Enter' && addPayout()} inputMode="decimal" className="w-32 bg-zinc-900 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-500" placeholder="Amount" />
            </div>
            <input value={payoutNote} onChange={e => setPayoutNote(e.target.value)} className="w-full bg-zinc-900 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-500" placeholder="Note — only you see this (optional)" />
            <button onClick={addPayout} disabled={savingPayout} className="px-5 py-2.5 bg-green-600 hover:bg-green-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{savingPayout ? 'Saving...' : 'Add payout'}</button>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">📥 Import a list</h2>
            <p className="text-xs text-gray-500">One per line: <span className="text-gray-300">username amount</span>. Leading ranks, dollar signs and commas are fine. Unreadable lines are skipped and reported.</p>
            <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={7} spellCheck={false} className="w-full bg-zinc-900 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500" placeholder={'jortgod 410.96\ndankdime 148.95\nBoocarkey 128.86'} />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => importPayouts(false)} disabled={importing} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{importing ? 'Importing...' : 'Add to ledger'}</button>
              <button onClick={() => importPayouts(true)} disabled={importing} className="px-5 py-2.5 bg-zinc-700 hover:bg-red-600 rounded-xl text-sm font-semibold transition disabled:opacity-50">Replace everything</button>
            </div>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">🏆 Totals by person</h2>
            {!payouts?.leaderboard?.length ? (
              <p className="text-sm text-gray-500">Nothing recorded yet. Add a payout or import your list above.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                {payouts.leaderboard.map((r, i) => (
                  <div key={r.username} className="flex items-center gap-3 py-2 border-b border-zinc-700/50 text-sm">
                    <span className="text-xs text-gray-500 tabular-nums w-8 shrink-0">#{i + 1}</span>
                    <span className="flex-1 truncate">{r.username}</span>
                    {r.count > 1 && <span className="text-xs text-gray-500">×{r.count}</span>}
                    <span className="text-green-400 font-bold tabular-nums">{money(r.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">🔧 Fix names on the ledger</h2>
            <p className="text-xs text-gray-500">Wager reward payouts get recorded under the Rainbet username; giveaway wins already use the Twitch/Kick handle. This swaps every ledger row that has a known link to their Twitch/Kick name instead — using whatever's currently linked on the People tab. Safe to run more than once; anything already correct is left alone.</p>
            <button onClick={fixPayoutNames} disabled={fixingPayoutNames} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{fixingPayoutNames ? 'Fixing...' : 'Fix names now'}</button>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">🔗 Manually merge a name</h2>
            <p className="text-xs text-gray-500">For the cases the automatic fix above can't catch — like an old tip that landed under someone's main Rainbet account before they'd joined the Discord and linked properly. Every ledger row currently showing the first name gets renamed to the second, permanently, and the old name is kept on each row so nothing's lost.</p>
            <div className="flex flex-wrap items-center gap-2">
              <input value={mergeFromName} onChange={e => setMergeFromName(e.target.value)} placeholder="Name currently on the ledger (e.g. their Rainbet account)" className="flex-1 min-w-[200px] bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-gray-500 text-sm">→</span>
              <input value={mergeToName} onChange={e => setMergeToName(e.target.value)} placeholder="Name it should show instead (e.g. their Twitch)" className="flex-1 min-w-[200px] bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <button onClick={mergePayoutNames} disabled={mergingNames || !mergeFromName.trim() || !mergeToName.trim()} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{mergingNames ? 'Merging...' : 'Merge now'}</button>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">🧾 Individual payouts <span className="text-gray-500 font-normal text-sm">(newest first)</span></h2>
            {!payouts?.ledger?.length ? (
              <p className="text-sm text-gray-500">Nothing recorded yet.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {payouts.ledger.map(r => (
                  <div key={r.id} className="flex items-center gap-3 py-2 border-b border-zinc-700/50 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{r.username}</p>
                      <p className="text-xs text-gray-500 truncate">{new Date(r.at).toLocaleDateString()}{r.note ? ` · ${r.note}` : ''}</p>
                    </div>
                    <span className="text-green-400 font-bold tabular-nums shrink-0">{money(r.amount)}</span>
                    <button onClick={() => deletePayout(r)} className="text-gray-500 hover:text-red-400 shrink-0 p-1" aria-label={`Remove payout to ${r.username}`}><X size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'wagers' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-green-500/10 to-zinc-800/60 rounded-2xl border border-green-500/25 p-6 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-green-400/80 mb-1">Sent to people via wager rewards</p>
            <p className="text-4xl font-black tabular-nums text-green-400">{money(wagerData?.totalPaidOut || 0)}</p>
            <p className="text-sm text-gray-400 mt-1">{(wagerData?.allClaims || []).filter(c => c.status === 'paid').length} claims marked paid · counts toward the total shown on the Giveaway page</p>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">📥 Paste the whole list</h2>
            <p className="text-xs text-gray-500">One per line: <span className="text-gray-300">rainbetUsername wager commission</span> — the commission is whatever Rainbet itself reports for that player, and that's exactly what "available to claim" is calculated from. The third number can be left off if you don't have it yet; it'll just keep whatever was there before.</p>
            <textarea value={wagerImportText} onChange={e => setWagerImportText(e.target.value)} rows={6} spellCheck={false} className="w-full bg-zinc-900 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500" placeholder={'someguy 152340 45.20\nanotherguy 48200 14.75'} />
            <button onClick={importWagers} disabled={importingWagers} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{importingWagers ? 'Importing...' : 'Update all'}</button>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-green-500/25 p-6 space-y-3">
            <h2 className="font-semibold">💰 Claims{wagerData?.pendingCount > 0 && <span className="ml-2 text-sm text-red-400">{wagerData.pendingCount} waiting</span>}</h2>
            {!wagerData?.allClaims?.length ? (
              <p className="text-sm text-gray-500">No claims yet.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {wagerData.allClaims.map(c => (
                  <div key={c.id} className="flex items-center gap-3 py-2.5 border-b border-zinc-700/50 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-semibold">{c.rainbetUsername} <span className="font-normal text-gray-400">— ${c.amount.toFixed(2)}</span></p>
                      <p className="text-xs text-gray-500 truncate">${c.wagerAtClaim?.toLocaleString()} wagered at the time · {new Date(c.at).toLocaleDateString()}</p>
                    </div>
                    {c.status === 'pending' && (
                      <div className="flex gap-1.5 shrink-0">
                        <button onClick={() => resolveClaim(c.id, 'paid')} className="px-3 py-1.5 bg-green-600 hover:bg-green-500 active:scale-[0.98] rounded-lg text-xs font-bold transition">Mark paid</button>
                        <button onClick={() => resolveClaim(c.id, 'rejected')} className="px-3 py-1.5 bg-zinc-700 hover:bg-red-600 rounded-lg text-xs font-semibold transition">Reject</button>
                      </div>
                    )}
                    {c.status !== 'pending' && (
                      <span className={`text-xs px-3 py-1.5 rounded-full shrink-0 ${c.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-gray-400'}`}>{c.status === 'paid' ? '✓ Paid' : 'Rejected'}</span>
                    )}
                    <button onClick={() => editWagerClaimAmount(c)} className="text-gray-500 hover:text-indigo-400 shrink-0 p-1" aria-label={`Edit amount for ${c.rainbetUsername}`} title="Edit amount">✏️</button>
                    <button onClick={() => deleteWagerClaim(c)} className="text-gray-500 hover:text-red-400 shrink-0 p-1" aria-label={`Remove this claim for ${c.rainbetUsername}`} title="Remove entirely"><X size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">📊 Commission progress</h2>
            <p className="text-xs text-gray-500">Owed comes straight from the commission you paste in per player. Green is paid, yellow is pending a claim, gray is available but not yet claimed.</p>
            {!wagerData?.players?.some(p => p.commission > 0) ? (
              <p className="text-sm text-gray-500 py-4 text-center">Nobody has a commission figure on file yet — paste it in with the wager list above.</p>
            ) : (
              <div className="max-h-[28rem] overflow-y-auto space-y-3 pr-1">
                {[...wagerData.players].filter(p => p.commission > 0).sort((a, b) => b.commission - a.commission).map(p => {
                  const total = p.commission || 0.0001;
                  const paidPct = Math.min(100, (p.paid / total) * 100);
                  const pendingPct = Math.min(100 - paidPct, (p.pending / total) * 100);
                  return (
                    <div key={p.rainbetUsername}>
                      <div className="flex items-center justify-between text-sm mb-1 gap-2">
                        <span className="font-semibold truncate">{p.rainbetUsername}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-gray-400">
                            <span className="text-green-400 font-bold">${p.paid.toFixed(2)}</span> paid
                            {p.pending > 0 && <span className="text-yellow-400"> · ${p.pending.toFixed(2)} pending</span>}
                            {p.available > 0 && <span className="text-gray-400"> · ${p.available.toFixed(2)} available</span>}
                            {' '}of <span className="text-gray-300 font-semibold">${p.commission.toFixed(2)}</span>
                          </span>
                          <button onClick={() => recordWagerPayment(p)} className="text-xs px-2.5 py-1 rounded-full bg-green-600 hover:bg-green-500 active:scale-[0.98] font-bold transition shrink-0">💵 Record</button>
                          <button onClick={() => deleteWagerPlayer(p.rainbetUsername)} className="text-gray-500 hover:text-red-400 shrink-0 p-1" aria-label={`Stop tracking ${p.rainbetUsername}`}><X size={14} /></button>
                        </div>
                      </div>
                      <div className="h-2.5 bg-zinc-900 rounded-full overflow-hidden flex">
                        <div className="h-full bg-green-500" style={{ width: `${paidPct}%` }} />
                        <div className="h-full bg-yellow-500" style={{ width: `${pendingPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}


      {view === 'referral' && (
        <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow duration-300 rounded-2xl border border-white/[0.06] p-6 space-y-3">
          <h2 className="font-semibold">📋 Rainbet Referral List <span className="text-gray-500 font-normal text-sm">({referralCount} names on file)</span></h2>
          <p className="text-xs text-gray-500">There's no public Rainbet API to auto-verify referrals, so this is a manual stand-in: export your referred-users list from your Rainbet affiliate dashboard and paste the usernames below.</p>
          <textarea value={referralList} onChange={e => setReferralList(e.target.value)} placeholder={'username1\nusername2\nusername3'} rows={14} className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 text-sm font-mono" />
          <button onClick={saveReferralList} disabled={savingReferralList} className="px-6 py-2.5 bg-gradient-to-r from-[#4f39f6] to-[#372aac] hover:from-[#615fff] hover:to-[#432dd7] active:scale-[0.98] text-white font-bold rounded-xl transition disabled:opacity-50 text-sm">{savingReferralList ? 'Saving...' : 'Save List'}</button>
        </div>
      )}

      {view === 'clips' && (
        <div className="space-y-4">
          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold flex items-center gap-2"><Film size={16} /> Add a clip</h2>
            <p className="text-xs text-gray-500">Paste the link to a post on twitter.com/x.com that has your clip attached — it'll render as a real embedded video on the public Clips page.</p>
            <input value={clipUrl} onChange={e => setClipUrl(e.target.value)} placeholder="https://x.com/dankquangamba/status/..." className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            <div className="flex flex-wrap gap-2">
              <input value={clipLabel} onChange={e => setClipLabel(e.target.value)} placeholder="Label (optional) — e.g. Big win on Gates of Olympus" className="flex-1 min-w-[200px] bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
              <select value={clipOrientation} onChange={e => setClipOrientation(e.target.value)} className="bg-zinc-900/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="landscape">Landscape</option>
                <option value="vertical">Vertical</option>
              </select>
              <button onClick={addClip} disabled={savingClip || !clipUrl.trim()} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] rounded-xl text-sm font-bold transition disabled:opacity-50">{savingClip ? 'Adding...' : 'Add clip'}</button>
            </div>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] p-6 space-y-3">
            <h2 className="font-semibold">🎬 Saved clips <span className="text-gray-500 font-normal text-sm">({clips.length})</span></h2>
            {clips.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Nothing added yet.</p>
            ) : (
              <div className="max-h-[28rem] overflow-y-auto space-y-1.5">
                {clips.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-2.5 text-sm gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{c.label || c.url}</p>
                      <p className="text-xs text-gray-500 truncate">{c.url} · {c.orientation}</p>
                    </div>
                    <button onClick={() => deleteClip(c)} className="text-gray-500 hover:text-red-400 p-1 shrink-0" aria-label="Remove clip"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'security' && (() => {
        const groups = [
          ...(securityData?.discordGroups || []),
          ...(securityData?.ipGroups || []),
          ...(securityData?.velocityGroups || []),
        ];
        const claimGroups = securityData?.claimGroups || [];
        const newAccounts = securityData?.newAccounts || [];
        const bl = securityData?.blacklist || [];
        const blacklistedIdSet = new Set(bl.map(b => b.discordId));
        return (
          <div className="space-y-4">

            {claimGroups.length > 0 && (
              <div className="bg-red-500/[0.08] backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border-2 border-red-500/40 p-6 space-y-3">
                <h2 className="font-semibold flex items-center gap-2 text-red-300">🚨 Same person, different winners <span className="text-red-400/70 font-normal text-sm">({claimGroups.length})</span></h2>
                <p className="text-sm text-red-200/90">
                  This is your strongest signal, not a hint like the others below. "Claimed as" isn't something a winner can type in — it's automatically pulled from whichever Discord is signed in the moment they click Claim. If two <b>different</b> Twitch/Kick winners both got claimed under the exact same Rainbet name, the same real person was signed into that same Discord both times. That's a direct trail, not a coincidence.
                </p>
                <div className="space-y-3">
                  {claimGroups.map(g => (
                    <div key={g.key} className="rounded-xl border border-red-500/30 bg-black/30 p-4">
                      <p className="text-xs text-red-300 mb-2">{g.reason}</p>
                      <div className="space-y-1.5">
                        {g.accounts.map((a, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 bg-black/30 rounded-lg px-3 py-2 text-sm">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <ProviderIcon provider={a.platform} size={14} />
                              <span className="font-semibold truncate">{a.username}</span>
                              {a.discordUsername && <span className="text-gray-500 text-xs truncate">· {a.discordUsername}</span>}
                              <span className="text-green-400 text-xs truncate">· claimed as {a.rainbetUsername}</span>
                            </div>
                            {a.discordId && (
                              blacklistedIdSet.has(a.discordId) ? (
                                <button onClick={() => removeFromBlacklist(a.discordId, a.discordUsername || a.username)} disabled={blacklisting === a.discordId} className="text-xs px-3 py-1 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition shrink-0 disabled:opacity-50">Un-blacklist</button>
                              ) : (
                                <button onClick={() => blacklistAccount(a.discordId, a.discordUsername || a.username, g.reason)} disabled={blacklisting === a.discordId} className="text-xs px-3 py-1 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold transition shrink-0 disabled:opacity-50">🚫 Blacklist this Discord</button>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Blacklist whichever Discord actually did the claiming above — that's the real controlling account, not necessarily the Twitch/Kick name that entered.</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] p-6 space-y-3">
              <h2 className="font-semibold flex items-center gap-2">🆕 New accounts <span className="text-gray-500 font-normal text-sm">({newAccounts.length})</span></h2>
              <p className="text-xs text-gray-500">Account age from Discord (decoded straight from their ID) and Twitch (via Twitch's own API) — Kick has no reliable way to check this. A brand new account is a real signal on its own: real community members built theirs up over time. Shows anyone with a Discord or Twitch under 7 days old.</p>
              {newAccounts.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">Nobody flagged right now.</p>
              ) : (
                <div className="space-y-1.5">
                  {newAccounts.map(a => (
                    <div key={a.discordId} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${a.blacklisted ? 'bg-red-500/[0.07] border border-red-500/30' : 'bg-white/[0.03]'}`}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <ProviderIcon provider={a.platform} size={14} />
                        <span className="font-semibold truncate">{a.username}</span>
                        {a.discordUsername && <span className="text-gray-500 text-xs truncate">· {a.discordUsername}</span>}
                        <span className="text-yellow-400 text-xs shrink-0">· {a.youngestPlatformLabel} is {a.youngestAgeDays === 0 ? 'less than a day' : `${a.youngestAgeDays} day${a.youngestAgeDays === 1 ? '' : 's'}`} old</span>
                      </div>
                      {a.discordId && (
                        a.blacklisted ? (
                          <button onClick={() => removeFromBlacklist(a.discordId, a.username)} disabled={blacklisting === a.discordId} className="text-xs px-3 py-1 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition shrink-0 disabled:opacity-50">Un-blacklist</button>
                        ) : (
                          <button onClick={() => blacklistAccount(a.discordId, a.username, `${a.youngestPlatformLabel} account only ${a.youngestAgeDays}d old`)} disabled={blacklisting === a.discordId} className="text-xs px-3 py-1 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold transition shrink-0 disabled:opacity-50">🚫 Blacklist</button>
                        )
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] p-6 space-y-3">
              <h2 className="font-semibold flex items-center gap-2"><Shield size={17} /> Flagged pairs <span className="text-gray-500 font-normal text-sm">({groups.length})</span></h2>
              <p className="text-xs text-gray-500">Two independent signals, shown as actual pairs instead of scattered flags: the same Discord linked to more than one Twitch/Kick, or different Discords connecting from the same place. Neither is proof on its own — review before acting.</p>

              {groups.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">Nothing flagged right now.</p>
              ) : (
                <div className="space-y-3">
                  {groups.map(g => (
                    <div key={g.key} className={`rounded-xl border p-4 ${g.blacklisted ? 'bg-red-500/[0.07] border-red-500/30' : 'bg-white/[0.03] border-white/10'}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <span className="text-xs text-gray-400">{g.reason}</span>
                        {g.blacklisted && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white font-bold">Blacklisted</span>}
                      </div>
                      <div className="space-y-1.5">
                        {g.accounts.map((a, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2 text-sm">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <ProviderIcon provider={a.platform} size={14} />
                              <span className="font-semibold truncate">{a.username}</span>
                              {a.discordUsername && <span className="text-gray-500 text-xs truncate">· {a.discordUsername}</span>}
                              {a.rainbetUsername && <span className="text-green-400 text-xs truncate">· {a.rainbetUsername}</span>}
                            </div>
                            {a.discordId && (
                              blacklistedIdSet.has(a.discordId) ? (
                                <button onClick={() => removeFromBlacklist(a.discordId, a.username)} disabled={blacklisting === a.discordId} className="text-xs px-3 py-1 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition shrink-0 disabled:opacity-50">Un-blacklist</button>
                              ) : (
                                <button onClick={() => blacklistAccount(a.discordId, a.username, `${g.reason} (with ${g.accounts.filter(x => x !== a).map(x => x.username).join(', ') || 'unknown'})`)} disabled={blacklisting === a.discordId} className="text-xs px-3 py-1 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold transition shrink-0 disabled:opacity-50">🚫 Blacklist</button>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-zinc-800/50 backdrop-blur-sm shadow-lg shadow-black/20 rounded-2xl border border-white/[0.06] p-6 space-y-3">
              <h2 className="font-semibold">🚫 Blacklisted accounts <span className="text-gray-500 font-normal text-sm">({bl.length})</span></h2>
              <p className="text-xs text-gray-500">Blacklisting is by Discord ID, so it follows them even if they link a brand new Twitch/Kick account to the same Discord afterward. They can still enter from chat — this only blocks actually WINNING.</p>
              {bl.some(b => (b.reason || '').includes('VPN/hosting')) && (
                <p className="text-xs bg-orange-500/10 border border-orange-500/25 rounded-lg px-3 py-2 text-orange-300">
                  🌐 Entries marked "VPN/hosting" below were blacklisted on a shared IP that turned out to be a VPN or datacenter network, not a real household — the weakest signal here, since that's exactly what two unrelated strangers sharing a VPN exit node looks like. Worth a second look. Entries marked "residential connection" are a much stronger signal — that genuinely means the same household.
                </p>
              )}
              {bl.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">Nobody blacklisted.</p>
              ) : (
                <div className="space-y-1.5">
                  {bl.map(b => {
                    const ipOnly = (b.reason || '').includes('VPN/hosting');
                    return (
                    <div key={b.discordId} className={`flex items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-sm ${ipOnly ? 'bg-orange-500/[0.06] border border-orange-500/20' : 'bg-white/5'}`}>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{b.label}</p>
                        <p className="text-xs text-gray-500 truncate">{b.discordId} · blacklisted {new Date(b.blacklistedAt).toLocaleDateString()}</p>
                        {b.reason && <p className={`text-xs truncate mt-0.5 ${ipOnly ? 'text-orange-400' : 'text-gray-400'}`}>{ipOnly && '🌐 '}{b.reason}</p>}
                      </div>
                      <button onClick={() => removeFromBlacklist(b.discordId, b.label)} disabled={blacklisting === b.discordId} className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition shrink-0 disabled:opacity-50">Remove</button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

        </div>
      </div>

      {identityCard && (
        <AdminIdentityCard winner={identityCard} onClose={() => setIdentityCard(null)} callAdmin={callAdmin} />
      )}
    </div>
  );
}

function App() {
  const [accounts, setAccounts] = useState(() => loadSession());
  const [rainbetUsername, setRainbetUsername] = useState(null); // null = not loaded yet, '' = loaded, none set
  useEffect(() => { addPulsateStyles(); }, []);
  useEffect(() => { saveSession(accounts); }, [accounts]);
  useEffect(() => {
    const onFocus = () => setAccounts(loadSession());
    const onStorage = () => setAccounts(loadSession());
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Rainbet username lives server-side, tied to Discord ID, not localStorage —
  // this loads whatever's on file whenever the signed-in Discord account changes.
  useEffect(() => {
    const discordId = accounts.discord?.id;
    if (!discordId) { setRainbetUsername(null); return; }
    let cancelled = false;
    fetch(`/.netlify/functions/profile?discordId=${discordId}`)
      .then(res => res.json())
      .then(data => { if (!cancelled) setRainbetUsername(data.rainbetUsername || ''); })
      .catch(() => { if (!cancelled) setRainbetUsername(''); });
    return () => { cancelled = true; };
  }, [accounts.discord?.id]);

  // Records the (hashed) connection on every visit by anyone signed in with Discord, not just
  // at sign-in — so switching off a VPN or changing networks after the first connect still
  // gets picked up. Also sends a random per-browser device ID (kept in this browser's own
  // storage), which stays the same even when the IP changes, so two Discord accounts used in
  // one browser get linked regardless of VPN. Throttled per tab session, but per account — so
  // switching to a different Discord account in the same tab is always sent right away.
  useEffect(() => {
    const sessionToken = accounts.discord?.sessionToken;
    if (!sessionToken) return;
    const who = String(sessionToken).split('.')[0];
    try {
      const [lastWho, lastAt] = (sessionStorage.getItem('ipCheckinAt') || '').split(':');
      if (lastWho === who && Date.now() - Number(lastAt || 0) < 30 * 60 * 1000) return;
      sessionStorage.setItem('ipCheckinAt', `${who}:${Date.now()}`);
    } catch { /* storage blocked — just send it */ }
    let deviceId = '';
    try {
      deviceId = localStorage.getItem('dq_device') || '';
      if (!/^[a-f0-9]{32}$/.test(deviceId)) {
        const bytes = new Uint8Array(16);
        (window.crypto || window.msCrypto).getRandomValues(bytes);
        deviceId = Array.from(bytes, x => x.toString(16).padStart(2, '0')).join('');
        localStorage.setItem('dq_device', deviceId);
      }
    } catch { deviceId = ''; }
    fetch('/.netlify/functions/ip-checkin', { method: 'POST', headers: { 'X-Session-Token': sessionToken, ...(deviceId ? { 'X-Device-Id': deviceId } : {}) } })
      .catch(() => { /* a signal, never a gate */ });
  }, [accounts.discord?.sessionToken]);

  // Whenever someone has both Discord + a streaming platform connected, record that
  // Twitch/Kick username -> Discord ID link. This is what lets a chat-only "danky" entry
  // (which has no Discord login at all) still show its Rainbet link and guild/VIP status
  // in admin — the link only needs to have happened once, on any visit.
  useEffect(() => {
    const twitchLogin = accounts.twitch?.login || accounts.twitch?.username;
    const kickUsername = accounts.kick?.username;
    const platform = twitchLogin ? 'twitch' : kickUsername ? 'kick' : null;
    const username = platform === 'twitch' ? twitchLogin : kickUsername;
    const sessionToken = accounts.discord?.sessionToken;
    if (!platform || !username || !sessionToken) return;
    fetch('/.netlify/functions/link-identity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken },
      body: JSON.stringify({ platform, username }),
    })
      .then(async (res) => {
        let data = null;
        try { data = await res.json(); } catch { /* ignore parse errors */ }
        if (!res.ok || !data?.success) console.error('link-identity failed:', res.status, data);
      })
      .catch(err => console.error('link-identity request error:', err));
  }, [accounts.discord?.sessionToken, accounts.twitch?.login, accounts.twitch?.username, accounts.kick?.username]);

  const saveRainbetProfile = useCallback(async (value) => {
    const discordId = accounts.discord?.id;
    if (!discordId) return { success: false, message: 'Sign in with Discord first.' };
    const res = await fetch('/.netlify/functions/profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discordId, rainbetUsername: value }),
    });
    const data = await res.json();
    if (data.success) setRainbetUsername(data.rainbetUsername);
    return data;
  }, [accounts.discord?.id]);

  const disconnect = useCallback((provider) => {
    setAccounts(prev => { const next = {...prev}; delete next[provider]; return next; });
  }, []);
  const disconnectAll = useCallback(() => { setAccounts({}); localStorage.removeItem(STORAGE_KEY); }, []);
  const isValid = isValidCombo(accounts);

  const handleAccountAdded = useCallback((newAccounts) => {
    setAccounts(newAccounts);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/callback/:provider" element={<OAuthCallback onAccountAdded={handleAccountAdded} />} />
        <Route path="*" element={
          <div className={bg}>
            <AtmosphereBackground />
            <Header
              accounts={accounts}
              isValid={isValid}
              disconnect={disconnect}
              disconnectAll={disconnectAll}
              rainbetUsername={rainbetUsername}
              onSaveRainbet={saveRainbetProfile}
            />
            <div className="flex-1 flex flex-col">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/bonus-hunts" element={<BonusHuntsPage accounts={accounts} disconnect={disconnect} />} />
                <Route path="/giveaway" element={<GiveawayPage accounts={accounts} rainbetUsername={rainbetUsername} onSaveRainbet={saveRainbetProfile} />} />
                <Route path="/slot-picker" element={<Navigate to="/giveaway" replace />} />
                <Route path="/claim" element={<Navigate to="/giveaway" replace />} />
                <Route path="/rewards" element={<RewardsPage accounts={accounts} rainbetUsername={rainbetUsername} onSaveRainbet={saveRainbetProfile} />} />
                <Route path="/clips" element={SHOW_CLIPS_PAGE ? <ClipsPage /> : <Navigate to="/" replace />} />
                <Route path="/admin" element={<AdminPage accounts={accounts} />} />
              </Routes>
            </div>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
